import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/tokens/index.ts

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/display/truncation.ts
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var truncation = globalSingleton("display/truncation", () => ({
  active: DEFAULT_TRUNCATION
}));
function getTruncation(kind) {
  const { active } = truncation;
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text2 = value.trim();
  if (/\s/.test(text2)) return value;
  const prefix = hexPrefixOf(text2);
  const body = text2.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text2;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}
function isChainFamily(value) {
  return typeof value === "string" && Object.values(ChainFamily).includes(value);
}

// src/chains/catalogue.generated.json
var catalogue_generated_default = {
  chains: [
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:16661",
      cashaddr_prefix: null,
      chain_id_hex: "0x4115",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://chainscan.0g.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://0g.ai",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/0g.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "0G",
      native_asset: null,
      native_name: "0G",
      native_token: "0G",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "0g",
        coingecko_native: "zero-gravity"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evmrpc.0g.ai"
      ],
      short_name: "0g",
      slip44: null,
      slug: "0g",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "0g-galileo-1"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:16602",
      cashaddr_prefix: null,
      chain_id_hex: "0x40da",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://chainscan-galileo.0g.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://0g.ai",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/0g-galileo-1.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "0G Galileo Testnet",
      native_asset: null,
      native_name: "0G",
      native_token: "0G",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "0g"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evmrpc-testnet.0g.ai"
      ],
      short_name: "0g-galileo-testnet",
      slip44: null,
      slug: "0g_galileo_1",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "0g-galileo-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:16601",
      cashaddr_prefix: null,
      chain_id_hex: "0x40d9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://0g.ai",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/0g.svg",
      mainnet: "eip155:16661",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "0G-Galileo-Testnet",
      native_asset: null,
      native_name: "A0GI",
      native_token: "A0GI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "0gai-galileo-testnet",
      slip44: null,
      slug: "0g_galileo_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "0g-newton-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:16600",
      cashaddr_prefix: null,
      chain_id_hex: "0x40d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://0g.ai",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/0g.svg",
      mainnet: "eip155:16661",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "0G-Newton-Testnet",
      native_asset: null,
      native_name: "A0GI",
      native_token: "A0GI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "0gai-testnet",
      slip44: null,
      slug: "0g_newton_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:36888",
      cashaddr_prefix: null,
      chain_id_hex: "0x9018",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.core.ab.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ab.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/ab.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "AB Core",
      native_asset: null,
      native_name: "AB",
      native_token: "AB",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ab"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.core.ab.org",
        "https://rpc1.core.ab.org"
      ],
      short_name: "abcore",
      slip44: null,
      slug: "ab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ab-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:26888",
      cashaddr_prefix: null,
      chain_id_hex: "0x6908",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.core.testnet.ab.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ab.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/ab.svg",
      mainnet: "eip155:36888",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "AB Core Testnet",
      native_asset: null,
      native_name: "AB",
      native_token: "AB",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ab"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.core.testnet.ab.org"
      ],
      short_name: "tABCore",
      slip44: null,
      slug: "ab_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2741",
      cashaddr_prefix: null,
      chain_id_hex: "0xab5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://abscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://abs.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/abstract.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Abstract",
      native_asset: "eip155:2741/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "abstract",
        coingecko_native: "ethereum",
        thegraph: "abstract"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.mainnet.abs.xyz"
      ],
      short_name: "abstract",
      slip44: 60,
      slug: "abstract",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "abstract-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11124",
      cashaddr_prefix: null,
      chain_id_hex: "0x2b74",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.abscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://abs.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/abstract.svg",
      mainnet: "eip155:2741",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Abstract Sepolia Testnet",
      native_asset: "eip155:11124/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "abstract",
        thegraph: "abstract-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.testnet.abs.xyz"
      ],
      short_name: "abstract-sepolia",
      slip44: 60,
      slug: "abstract_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "acala-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:787",
      cashaddr_prefix: null,
      chain_id_hex: "0x313",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.acala.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://acala.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Acala Network",
      native_asset: "eip155:787/slip44:787",
      native_name: "Acala Token",
      native_token: "ACA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "acala"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://eth-rpc-acala.aca-api.network",
        "wss://eth-rpc-acala.aca-api.network"
      ],
      short_name: "aca",
      slip44: 787,
      slug: "acala_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:36900",
      cashaddr_prefix: null,
      chain_id_hex: "0x9024",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.adifoundation.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://adifoundation.ai",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/adi.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "ADI Chain",
      native_asset: null,
      native_name: "ADI",
      native_token: "ADI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "adi",
        coingecko_native: "adi-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.adifoundation.ai"
      ],
      short_name: "adi",
      slip44: null,
      slug: "adi",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "adi-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:99999",
      cashaddr_prefix: null,
      chain_id_hex: "0x1869f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.ab.testnet.adifoundation.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.adi.foundation",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/adi.svg",
      mainnet: "eip155:36900",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "ADI Network AB Testnet",
      native_asset: null,
      native_name: "ADI",
      native_token: "ADI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "adi"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ab.testnet.adifoundation.ai"
      ],
      short_name: "adi-testnet",
      slip44: null,
      slug: "adi_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "aleph-zero"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2039",
      cashaddr_prefix: null,
      chain_id_hex: "0x7f7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://alephzero.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Aleph Zero",
      native_asset: null,
      native_name: "TZERO",
      native_token: "TZERO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.alephzero-testnet.gelato.digital",
        "wss://rpc.alephzero-testnet.gelato.digital"
      ],
      short_name: "aleph",
      slip44: null,
      slug: "aleph_zero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "aleph-zero-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41455",
      cashaddr_prefix: null,
      chain_id_hex: "0xa1ef",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://alephzero.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Aleph Zero EVM",
      native_asset: null,
      native_name: "Aleph Zero",
      native_token: "AZERO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "aleph-zero"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.alephzero.raas.gelato.cloud",
        "wss://ws.alephzero.raas.gelato.cloud"
      ],
      short_name: "aleph-zero",
      slip44: null,
      slug: "aleph_zero_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "altair-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2088",
      cashaddr_prefix: null,
      chain_id_hex: "0x828",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://centrifuge.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Altair Testnet",
      native_asset: null,
      native_name: "Altair",
      native_token: "AIR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "wss://fullnode.altair.centrifuge.io",
        "wss://altair.api.onfinality.io/public-ws"
      ],
      short_name: "air",
      slip44: null,
      slug: "altair_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:888888888",
      cashaddr_prefix: null,
      chain_id_hex: "0x34fb5e38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.ancient8.gg",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ancient8.gg",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ancient8",
      native_asset: "eip155:888888888/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ancient8.gg"
      ],
      short_name: "ancient8",
      slip44: 60,
      slug: "ancient8",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:69000",
      cashaddr_prefix: null,
      chain_id_hex: "0x10d88",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.anime.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.anime.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Animechain",
      native_asset: null,
      native_name: "Animecoin",
      native_token: "ANIME",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "anime"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://public-rpc.anime.xyz"
      ],
      short_name: "anime",
      slip44: null,
      slug: "animechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6714",
      cashaddr_prefix: null,
      chain_id_hex: "0x1a3a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Anubis",
      native_asset: null,
      native_name: null,
      native_token: "DAI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "dai",
        thegraph: "anubis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "anubis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ape-chain",
        "apechain-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33139",
      cashaddr_prefix: null,
      chain_id_hex: "0x8173",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://apescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://apechain.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/apechain.svg",
      mainnet: null,
      metadata: {},
      name: "ApeChain",
      native_asset: null,
      native_name: "ApeCoin",
      native_token: "APE",
      native_token_address: null,
      parent: "eip155:42161",
      providers: {
        blockscout: "https://apechain.calderaexplorer.xyz/api",
        blockscout_v2: "https://apechain.calderaexplorer.xyz/api/v2",
        ccip: "apechain",
        coingecko: "apechain",
        coingecko_native: "apecoin",
        defillama: "apechain",
        dexscreener: "apechain",
        evm_rpc: "discovery",
        sourcify: "33139",
        thegraph: "apechain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apechain.com/http"
      ],
      short_name: "apechain",
      slip44: null,
      slug: "apechain",
      status: "active",
      wrapped_native_address: "0x48b62137edfa95a428d35c09e44256a739f6b557"
    },
    {
      aliases: [
        "curtis",
        "apechain-curtis",
        "apechain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33111",
      cashaddr_prefix: null,
      chain_id_hex: "0x8157",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.curtis.apechain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://curtis.hub.caldera.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/apechain.svg",
      mainnet: "eip155:33139",
      metadata: {},
      name: "ApeChain Curtis",
      native_asset: null,
      native_name: "ApeCoin",
      native_token: "APE",
      native_token_address: null,
      parent: "eip155:421614",
      providers: {
        ccip: "apechain",
        thegraph: "apechain-curtis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.curtis.apechain.com"
      ],
      short_name: "curtis",
      slip44: null,
      slug: "apechain_curtis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "apex-fusion-nexus"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9069",
      cashaddr_prefix: null,
      chain_id_hex: "0x236d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.nexus.mainnet.apexfusion.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://apexfusion.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Apex Fusion - Nexus",
      native_asset: null,
      native_name: "Apex Fusion Token",
      native_token: "AP3X",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.nexus.mainnet.apexfusion.org"
      ],
      short_name: "AP3X",
      slip44: null,
      slug: "apex_fusion_nexus",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:466",
      cashaddr_prefix: null,
      chain_id_hex: "0x1d2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.appchain.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://appchain.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "AppChain",
      native_asset: "eip155:466/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.appchain.xyz/http",
        "wss://rpc.appchain.xyz/ws"
      ],
      short_name: "appchain",
      slip44: 60,
      slug: "appchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "aptos:1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "other",
      finality_blocks: null,
      info_url: "https://aptosfoundation.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/aptos.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/aptos/caip2",
          "https://aptos.dev"
        ]
      },
      name: "Aptos",
      native_asset: "aptos:1/slip44:637",
      native_name: "Aptos Coin",
      native_token: "APT",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "aptos",
        coingecko_native: "aptos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 637,
      slug: "aptos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "aptos-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "aptos:2",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "other",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/aptos.svg",
      mainnet: "aptos:1",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://namespaces.chainagnostic.org/aptos/caip2"
        ]
      },
      name: "Aptos Testnet",
      native_asset: "aptos:2/slip44:637",
      native_name: "Aptos Coin",
      native_token: "APT",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "aptos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 637,
      slug: "aptos_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arabica-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "celestia",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:arabica-11",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:celestia",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "celestiatestnet2",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Arabica Testnet",
      native_asset: "cosmos:arabica-11/slip44:118",
      native_name: "Celestia",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "arabica_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arbitrum-one",
        "arb",
        "arbitrum-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:42161",
      cashaddr_prefix: null,
      chain_id_hex: "0xa4b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://arbiscan.io",
      family: "evm",
      finality_blocks: 32,
      info_url: "https://arbitrum.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/arbitrum.svg",
      mainnet: null,
      metadata: {},
      name: "Arbitrum",
      native_asset: "eip155:42161/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        alchemy: "arb-mainnet",
        ankr: "arbitrum",
        arkham: "arbitrum_one",
        blockchair: "arbitrum",
        blockscan: "42161",
        blockscout: "https://arbitrum.blockscout.com/api",
        blocksec: "42161",
        ccip: "arbitrum",
        codeslaw: "arbitrum",
        coingecko: "arbitrum-one",
        coingecko_native: "ethereum",
        coinmarketcap: "arbitrum",
        cowswap: "arbitrum_one",
        defillama: "arbitrum",
        dexscreener: "arbitrum",
        etherscan_scan: "arbiscan.io",
        infura: "arbitrum-mainnet",
        metasleuth: "arbitrum",
        moralis: "arbitrum",
        moralis_wallet: "arbitrum",
        sourcify: "42161",
        thegraph: "arbitrum-one"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arb1.arbitrum.io/rpc",
        "https://arbitrum-one-rpc.publicnode.com"
      ],
      short_name: "arb1",
      slip44: 60,
      slug: "arbitrum",
      status: "active",
      wrapped_native_address: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1"
    },
    {
      aliases: [
        "arbitrum-goerli"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421613",
      cashaddr_prefix: null,
      chain_id_hex: "0x66eed",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arbitrum.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/arbitrum.svg",
      mainnet: "eip155:42161",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arbitrum Goerli",
      native_asset: "eip155:421613/slip44:60",
      native_name: "Arbitrum Goerli Ether",
      native_token: "AGOR",
      native_token_address: null,
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://goerli-rollup.arbitrum.io/rpc",
        "https://arbitrum-goerli.publicnode.com",
        "wss://arbitrum-goerli.publicnode.com"
      ],
      short_name: "arb-goerli",
      slip44: 60,
      slug: "arbitrum_goerli",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arbitrum-nova"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:42170",
      cashaddr_prefix: null,
      chain_id_hex: "0xa4ba",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://nova-explorer.arbitrum.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arbitrum.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arbitrum Nova",
      native_asset: "eip155:42170/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum",
        thegraph: "arbitrum-nova"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nova.arbitrum.io/rpc",
        "https://arbitrum-nova-rpc.publicnode.com",
        "wss://arbitrum-nova-rpc.publicnode.com"
      ],
      short_name: "arb-nova",
      slip44: 60,
      slug: "arbitrum_nova",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421614",
      cashaddr_prefix: null,
      chain_id_hex: "0x66eee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arbitrum.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/arbitrum.svg",
      mainnet: "eip155:42161",
      metadata: {},
      name: "Arbitrum Sepolia",
      native_asset: "eip155:421614/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        alchemy: "arb-sepolia",
        ccip: "arbitrum",
        etherscan_v2: "421614",
        infura: "arbitrum-sepolia",
        sourcify: "421614",
        thegraph: "arbitrum-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia-rollup.arbitrum.io/rpc",
        "https://arbitrum-sepolia-rpc.publicnode.com"
      ],
      short_name: "arb-sep",
      slip44: 60,
      slug: "arbitrum_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5042",
      cashaddr_prefix: null,
      chain_id_hex: "0x13b2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.arc.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arc.io",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/arc.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arc",
      native_asset: null,
      native_name: "USDC",
      native_token: "USDC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "arc",
        coingecko_native: "usd-coin",
        thegraph: "arc"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.arc.io",
        "https://rpc.blockdaemon.mainnet.arc.io",
        "https://rpc.drpc.mainnet.arc.io",
        "https://rpc.quicknode.mainnet.arc.io"
      ],
      short_name: "arc-mainnet",
      slip44: null,
      slug: "arc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arc-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5042002",
      cashaddr_prefix: null,
      chain_id_hex: "0x4cef52",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.arcscan.app",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arc.network",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/arc.svg",
      mainnet: "eip155:5042",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arc Network Testnet",
      native_asset: null,
      native_name: "USDC",
      native_token: "USDC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "arc",
        thegraph: "arc-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.arc.network",
        "https://rpc.quicknode.testnet.arc.network",
        "https://rpc.blockdaemon.testnet.arc.network",
        "https://arc-testnet.drpc.org"
      ],
      short_name: "arc-testnet",
      slip44: null,
      slug: "arc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4278608",
      cashaddr_prefix: null,
      chain_id_hex: "0x414950",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.arcadia.khalani.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://khalani.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arcadia",
      native_asset: null,
      native_name: "AIP",
      native_token: "AIP",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arcadia.khalani.network"
      ],
      short_name: "aip",
      slip44: null,
      slug: "arcadia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arcadia-testnet-v2"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1098411886",
      cashaddr_prefix: null,
      chain_id_hex: "0x41786f6e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.khalani.network",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Arcadia Testnet v2",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.khalani.network"
      ],
      short_name: null,
      slip44: null,
      slug: "arcadia_testnet_v2",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "areon-network-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:462",
      cashaddr_prefix: null,
      chain_id_hex: "0x1ce",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://areon.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:463",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Areon Network Testnet",
      native_asset: null,
      native_name: "Areon",
      native_token: "TAREA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.areon.network",
        "https://testnet-rpc2.areon.network",
        "https://testnet-rpc3.areon.network",
        "https://testnet-rpc4.areon.network"
      ],
      short_name: "tarea",
      slip44: null,
      slug: "areon_network_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "areum-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:463",
      cashaddr_prefix: null,
      chain_id_hex: "0x1cf",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://areum.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Areum Network",
      native_asset: null,
      native_name: "Areum",
      native_token: "AREA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.areum.network",
        "https://mainnet-rpc2.areum.network",
        "https://mainnet-rpc3.areum.network",
        "https://mainnet-rpc4.areum.network"
      ],
      short_name: "area",
      slip44: null,
      slug: "areum_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "argochain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1298",
      cashaddr_prefix: null,
      chain_id_hex: "0x512",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://test-scanner.devolvedai.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://devolvedai.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Argochain Testnet",
      native_asset: null,
      native_name: "Argocoin",
      native_token: "AGC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-testnet.devolvedai.com",
        "https://test-rpc.devolvedai.com"
      ],
      short_name: "tAGC",
      slip44: null,
      slug: "argochain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11820",
      cashaddr_prefix: null,
      chain_id_hex: "0x2e2c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://artscan.artela.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://artela.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Artela",
      native_asset: null,
      native_name: "ART",
      native_token: "ART",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "artela-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://node-euro.artela.network/rpc"
      ],
      short_name: "artela-mainnet",
      slip44: null,
      slug: "artela",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "artela-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11822",
      cashaddr_prefix: null,
      chain_id_hex: "0x2e2e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://betanet-scan.artela.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://artela.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:11820",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Artela Testnet",
      native_asset: null,
      native_name: "ART",
      native_token: "ART",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://betanet-rpc1.artela.network"
      ],
      short_name: "Artela",
      slip44: null,
      slug: "artela_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10242",
      cashaddr_prefix: null,
      chain_id_hex: "0x2802",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.arthera.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.arthera.net/build/developing-sc/using-hardhat",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arthera",
      native_asset: "eip155:10242/slip44:10242",
      native_name: "Arthera",
      native_token: "AA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "arthera"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.arthera.net"
      ],
      short_name: "aa",
      slip44: 10242,
      slug: "arthera",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arthera-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10243",
      cashaddr_prefix: null,
      chain_id_hex: "0x2803",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-test.arthera.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.arthera.net",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:10242",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Arthera Testnet",
      native_asset: "eip155:10243/slip44:10242",
      native_name: "Arthera",
      native_token: "AA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-test.arthera.net"
      ],
      short_name: "aat",
      slip44: 10242,
      slug: "arthera_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:592",
      cashaddr_prefix: null,
      chain_id_hex: "0x250",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/astar",
      family: "evm",
      finality_blocks: null,
      info_url: "https://astar.network",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/astar.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Astar",
      native_asset: null,
      native_name: "Astar",
      native_token: "ASTR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "astar",
        coingecko_native: "astar"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.astar.network:8545"
      ],
      short_name: "astr",
      slip44: null,
      slug: "astar",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "astar-shibuya-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:81",
      cashaddr_prefix: null,
      chain_id_hex: "0x51",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/astar-shibuya-testnet.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Astar EVM Testnet",
      native_asset: null,
      native_name: null,
      native_token: "ASTR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "astar"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-1.japanopenchain.org:8545",
        "https://rpc-2.japanopenchain.org:8545",
        "https://rpc-3.japanopenchain.org"
      ],
      short_name: null,
      slip44: null,
      slug: "astar_shibuya_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "astar-zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3776",
      cashaddr_prefix: null,
      chain_id_hex: "0xec0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://astar-zkevm.explorer.startale.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://astar.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Astar zkEVM",
      native_asset: "eip155:3776/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.startale.com/astar-zkevm"
      ],
      short_name: "astrzk",
      slip44: 60,
      slug: "astar_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ault-blockchain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:904",
      cashaddr_prefix: null,
      chain_id_hex: "0x388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ault.explorer.orynt.tech",
      family: "evm",
      finality_blocks: null,
      info_url: "https://aultblockchain.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ault Blockchain",
      native_asset: null,
      native_name: "AULT Token",
      native_token: "AULT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://main-json-rpc.cloud.aultblockchain.xyz"
      ],
      short_name: "ault",
      slip44: null,
      slug: "ault_blockchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "aura-euphoria-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6321",
      cashaddr_prefix: null,
      chain_id_hex: "0x18b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://aura.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Aura Euphoria Testnet",
      native_asset: null,
      native_name: "test-EAura",
      native_token: "eAura",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://jsonrpc.euphoria.aura.network"
      ],
      short_name: "eaura",
      slip44: null,
      slug: "aura_euphoria_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161554",
      cashaddr_prefix: null,
      chain_id_hex: "0x4e454152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.aurora.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://aurora.dev",
      is_testnet: false,
      layer: "sidechain",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Aurora",
      native_asset: "eip155:1313161554/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum",
        defillama: "aurora",
        dexscreener: "aurora",
        thegraph: "aurora"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "aurora",
      slip44: 60,
      slug: "aurora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161555",
      cashaddr_prefix: null,
      chain_id_hex: "0x4e454153",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.aurora.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://aurora.dev",
      is_testnet: true,
      layer: "sidechain",
      logo: null,
      mainnet: "eip155:1313161554",
      metadata: {},
      name: "Aurora Testnet",
      native_asset: "eip155:1313161555/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "aurora-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "aurora-testnet",
      slip44: 60,
      slug: "aurora_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "autonity-piccadilly-yamuna-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:65100003",
      cashaddr_prefix: null,
      chain_id_hex: "0x3e158e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://piccadilly.autonity.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://autonity.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Autonity Piccadilly (Yamuna) Testnet",
      native_asset: null,
      native_name: "Piccadilly Auton",
      native_token: "ATN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "piccadilly-03",
      slip44: null,
      slug: "autonity_piccadilly_yamuna_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "avax",
        "avalanche-c-chain",
        "avax-c-chain",
        "avalanche-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43114",
      cashaddr_prefix: null,
      chain_id_hex: "0xa86a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://snowtrace.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.avax.network",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/avalanche.svg",
      mainnet: null,
      metadata: {},
      name: "Avalanche",
      native_asset: "eip155:43114/slip44:9005",
      native_name: "Avalanche",
      native_token: "AVAX",
      native_token_address: null,
      parent: null,
      providers: {
        alchemy: "avax-mainnet",
        ankr: "avalanche",
        arkham: "avalanche",
        blockchair: "avalanche",
        blockscan: "43114",
        blocksec: "43114",
        ccip: "avalanche",
        coingecko: "avalanche",
        coingecko_native: "avalanche-2",
        coinmarketcap: "avalanche",
        cowswap: "avalanche",
        defillama: "avax",
        dexscreener: "avalanche",
        etherscan_scan: "snowtrace.io",
        infura: "avalanche-mainnet",
        metasleuth: "avalanche",
        moralis: "avalanche",
        moralis_wallet: "avalanche",
        sourcify: "43114",
        thegraph: "avalanche"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax.network/ext/bc/C/rpc",
        "https://avalanche-c-chain-rpc.publicnode.com"
      ],
      short_name: "avax",
      slip44: 9005,
      slug: "avalanche",
      status: "active",
      wrapped_native_address: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7"
    },
    {
      aliases: [
        "fuji",
        "avalanche-fuji",
        "avax-fuji"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43113",
      cashaddr_prefix: null,
      chain_id_hex: "0xa869",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.snowtrace.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://cchain.explorer.avax-test.network",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/avalanche.svg",
      mainnet: "eip155:43114",
      metadata: {},
      name: "Avalanche Fuji",
      native_asset: "eip155:43113/slip44:9005",
      native_name: "Avalanche",
      native_token: "AVAX",
      native_token_address: null,
      parent: null,
      providers: {
        alchemy: "avax-fuji",
        ccip: "avalanche",
        etherscan_v2: "43113",
        infura: "avalanche-fuji",
        sourcify: "43113",
        thegraph: "fuji"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax-test.network/ext/bc/C/rpc",
        "https://avalanche-fuji-c-chain-rpc.publicnode.com"
      ],
      short_name: "Fuji",
      slip44: 9005,
      slug: "avalanche_fuji",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8333",
      cashaddr_prefix: null,
      chain_id_hex: "0x208d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.b3.fun",
      family: "evm",
      finality_blocks: null,
      info_url: "https://b3.fun",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "B3",
      native_asset: "eip155:8333/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.b3.fun"
      ],
      short_name: "b3",
      slip44: 60,
      slug: "b3",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "b-2"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:223",
      cashaddr_prefix: null,
      chain_id_hex: "0xdf",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.bsquared.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bsquared.network",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/b-2.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "B2",
      native_asset: null,
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "bsquared"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.b2-rpc.com",
        "https://rpc.bsquared.network",
        "https://b2-mainnet.alt.technology",
        "https://b2-mainnet-public.s.chainbase.com"
      ],
      short_name: "B2-mainnet",
      slip44: null,
      slug: "b_2",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "b-2-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1123",
      cashaddr_prefix: null,
      chain_id_hex: "0x463",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-explorer.bsquared.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bsquared.network",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/b-2.svg",
      mainnet: "eip155:223",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "B2 Testnet",
      native_asset: null,
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "bsquared"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://b2-testnet.alt.technology",
        "https://rpc.ankr.com/b2_testnet",
        "https://testnet-rpc.bsquared.network"
      ],
      short_name: "B2-testnet",
      slip44: null,
      slug: "b_2_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5165",
      cashaddr_prefix: null,
      chain_id_hex: "0x142d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://bahamut.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bahamut",
      native_asset: null,
      native_name: "FTN",
      native_token: "FTN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc1.bahamut.io",
        "https://rpc2.bahamut.io",
        "https://rpc1.ftnscan.io",
        "https://rpc2.ftnscan.io"
      ],
      short_name: "ftn",
      slip44: null,
      slug: "bahamut",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "base-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:8453",
      cashaddr_prefix: null,
      chain_id_hex: "0x2105",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basescan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base.svg",
      mainnet: null,
      metadata: {},
      name: "Base",
      native_asset: "eip155:8453/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        alchemy: "base-mainnet",
        ankr: "base",
        arkham: "base",
        blockchair: "base",
        blockscan: "8453",
        blockscout: "https://base.blockscout.com/api",
        ccip: "base",
        codeslaw: "base",
        coingecko: "base",
        coingecko_native: "ethereum",
        coinmarketcap: "base",
        cowswap: "base",
        defillama: "base",
        dexscreener: "base",
        etherscan_scan: "basescan.org",
        infura: "base-mainnet",
        metasleuth: "base",
        moralis: "base",
        moralis_wallet: "base",
        sourcify: "8453",
        thegraph: "base"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.base.org",
        "https://base-rpc.publicnode.com"
      ],
      short_name: "base",
      slip44: 60,
      slug: "base",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84531",
      cashaddr_prefix: null,
      chain_id_hex: "0x14a33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base.svg",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Goerli",
      native_asset: "eip155:84531/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "basegor",
      slip44: 60,
      slug: "base_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84532",
      cashaddr_prefix: null,
      chain_id_hex: "0x14a34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.basescan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base-sepolia.svg",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Sepolia",
      native_asset: "eip155:84532/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        alchemy: "base-sepolia",
        ccip: "base",
        infura: "base-sepolia",
        moralis: "base_sepolia",
        sourcify: "84532",
        thegraph: "base-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.base.org",
        "https://base-sepolia-rpc.publicnode.com"
      ],
      short_name: "basesep",
      slip44: 60,
      slug: "base_sepolia",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:123420001114",
      cashaddr_prefix: null,
      chain_id_hex: "0x1cbc67c35a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basecamp.cloud.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.campnetwork.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Basecamp",
      native_asset: null,
      native_name: "Camp",
      native_token: "CAMP",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.basecamp.t.raas.gelato.cloud"
      ],
      short_name: "Basecamp",
      slip44: null,
      slug: "basecamp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4337",
      cashaddr_prefix: null,
      chain_id_hex: "0x10f1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets.avax.network/beam",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.onbeam.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Beam",
      native_asset: null,
      native_name: "Beam",
      native_token: "BEAM",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "beam-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://build.onbeam.com/rpc",
        "https://subnets.avax.network/beam/mainnet/rpc",
        "wss://build.onbeam.com/ws",
        "wss://subnets.avax.network/beam/mainnet/ws"
      ],
      short_name: "beam",
      slip44: null,
      slug: "beam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:80094",
      cashaddr_prefix: null,
      chain_id_hex: "0x138de",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://berascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.berachain.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/berachain.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Berachain",
      native_asset: null,
      native_name: "BERA Token",
      native_token: "BERA",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "berachain",
        coingecko_native: "berachain-bera",
        etherscan_scan: "berascan.com",
        etherscan_v2: "80094",
        sourcify: "80094",
        thegraph: "berachain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.berachain.com",
        "https://berachain-rpc.publicnode.com",
        "https://rpc.berachain-apis.com",
        "wss://berachain-rpc.publicnode.com"
      ],
      short_name: "berachain",
      slip44: null,
      slug: "berachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "berachain-artio-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:80085",
      cashaddr_prefix: null,
      chain_id_hex: "0x138d5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.berachain.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/berachain.svg",
      mainnet: "eip155:80094",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Berachain Artio Testnet",
      native_asset: null,
      native_name: "BERA Token",
      native_token: "BERA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "berachainArtio",
      slip44: null,
      slug: "berachain_artio_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "berachain-bartio-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:80084",
      cashaddr_prefix: null,
      chain_id_hex: "0x138d4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.berachain.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/berachain.svg",
      mainnet: "eip155:80094",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Berachain bArtio Testnet",
      native_asset: null,
      native_name: "BERA Token",
      native_token: "BERA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "berachainbArtio",
      slip44: null,
      slug: "berachain_bartio_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "berachain-testnet-bepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:80069",
      cashaddr_prefix: null,
      chain_id_hex: "0x138c5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.berascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.berachain.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/berachain.svg",
      mainnet: "eip155:80094",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Berachain Bepolia Testnet",
      native_asset: null,
      native_name: "Testnet BERA Token",
      native_token: "BERA",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "berachain",
        thegraph: "berachain-bepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://bepolia.rpc.berachain.com"
      ],
      short_name: "berachain-bepolia",
      slip44: null,
      slug: "berachain_testnet_bepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11501",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ced",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Bevm",
      native_asset: null,
      native_name: null,
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "bitcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-mainnet-1.geb.network",
        "https://rpc-mainnet-2.geb.network"
      ],
      short_name: null,
      slip44: null,
      slug: "bevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1907",
      cashaddr_prefix: null,
      chain_id_hex: "0x773",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bitciexplorer.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bitcichain.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bitcichain",
      native_asset: null,
      native_name: "Bitci",
      native_token: "BITCI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.bitci.com"
      ],
      short_name: "bitci",
      slip44: null,
      slug: "bitcichain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcichain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1908",
      cashaddr_prefix: null,
      chain_id_hex: "0x774",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bitciexplorer.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bitcichain.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:1907",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bitcichain Testnet",
      native_asset: null,
      native_name: "Test Bitci",
      native_token: "TBITCI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.bitcichain.com"
      ],
      short_name: "tbitci",
      slip44: null,
      slug: "bitcichain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-mainnet",
        "0x0",
        "btc"
      ],
      base58_versions: [
        0,
        5
      ],
      bech32_hrp: "bc",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000019d6689c085ae165831e93",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: "https://blockstream.info",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://bitcoin.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Bitcoin",
      native_asset: "bip122:000000000019d6689c085ae165831e93/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "bitcoin",
        coingecko: "ordinals",
        coingecko_native: "bitcoin",
        dexscreener: "bitcoin",
        esplora: "mainnet",
        scanscope: "bitcoin",
        thegraph: "btc"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bch",
        "bitcoin-cash"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#0AC18E",
      caip2: "bip122:000000000000000000651ef99cb9fcbe",
      cashaddr_prefix: "bitcoincash",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/bitcoin-cash",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://bitcoincash.org",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/bip122/caip2",
          "https://github.com/bitcoin-cash-node/bitcoin-cash-node"
        ]
      },
      name: "Bitcoin Cash",
      native_asset: "bip122:000000000000000000651ef99cb9fcbe/slip44:145",
      native_name: "Bitcoin Cash",
      native_token: "BCH",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "bitcoin-cash",
        coingecko_native: "bitcoin-cash"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 145,
      slug: "bitcoin_cash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-regtest"
      ],
      base58_versions: [
        111,
        196
      ],
      bech32_hrp: "bcrt",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:0f9188f13cb7b2c71f2a335e3a4fc328",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Regtest",
      native_asset: "bip122:0f9188f13cb7b2c71f2a335e3a4fc328/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin_regtest",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-testnet"
      ],
      base58_versions: [
        111,
        196
      ],
      bech32_hrp: "tb",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000933ea01ad0ee984209779ba",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Testnet",
      native_asset: "bip122:000000000933ea01ad0ee984209779ba/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        esplora: "testnet"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:200901",
      cashaddr_prefix: null,
      chain_id_hex: "0x310c5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.btrscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.bitlayer.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/bitlayer.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bitlayer",
      native_asset: null,
      native_name: "BTC",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "bitlayer",
        coingecko_native: "bitcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.bitlayer.org",
        "https://rpc.bitlayer-rpc.com",
        "https://rpc.ankr.com/bitlayer",
        "https://rpc-bitlayer.rockx.com"
      ],
      short_name: "btr",
      slip44: null,
      slug: "bitlayer",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitlayer-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:200810",
      cashaddr_prefix: null,
      chain_id_hex: "0x3106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.btrscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.bitlayer.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/bitlayer.svg",
      mainnet: "eip155:200901",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bitlayer Testnet",
      native_asset: null,
      native_name: "BTC",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "bitlayer"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.bitlayer.org",
        "https://testnet-rpc.bitlayer-rpc.com",
        "https://rpc.ankr.com/bitlayer_testnet",
        "wss://testnet-ws.bitlayer.org"
      ],
      short_name: "btrt",
      slip44: null,
      slug: "bitlayer_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:964",
      cashaddr_prefix: null,
      chain_id_hex: "0x3c4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://bittensor.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/bittensor.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Subtensor EVM",
      native_asset: "eip155:964/slip44:1005",
      native_name: "TAO",
      native_token: "TAO",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "bittensor",
        coingecko_native: "bittensor"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://lite.chain.opentensor.ai"
      ],
      short_name: "bittensor-evm-mainnet",
      slip44: 1005,
      slug: "bittensor",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bittorrent-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:199",
      cashaddr_prefix: null,
      chain_id_hex: "0xc7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bttcscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://bt.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "BitTorrent Chain",
      native_asset: null,
      native_name: "BitTorrent",
      native_token: "BTT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.bt.io",
        "https://bittorrent.drpc.org",
        "wss://bittorrent.drpc.org"
      ],
      short_name: "BTT",
      slip44: null,
      slug: "bittorrent_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bittorrent-chain-donau-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1029",
      cashaddr_prefix: null,
      chain_id_hex: "0x405",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bttcscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://bt.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:199",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "BitTorrent Chain Donau Testnet",
      native_asset: null,
      native_name: "BitTorrent",
      native_token: "BTT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://pre-rpc.bt.io"
      ],
      short_name: "tBTT",
      slip44: null,
      slug: "bittorrent_chain_donau_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "blast-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:81457",
      cashaddr_prefix: null,
      chain_id_hex: "0x13e31",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blastscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://blast.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/blast.svg",
      mainnet: null,
      metadata: {},
      name: "Blast",
      native_asset: "eip155:81457/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        arkham: "blast",
        ccip: "blast",
        codeslaw: "blast",
        coingecko_native: "ethereum",
        defillama: "blast",
        dexscreener: "blast",
        sourcify: "81457",
        thegraph: "blast-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://blast-rpc.publicnode.com"
      ],
      short_name: "blastmainnet",
      slip44: 60,
      slug: "blast",
      status: "active",
      wrapped_native_address: "0x4300000000000000000000000000000000000004"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:168587773",
      cashaddr_prefix: null,
      chain_id_hex: "0xa0c71fd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.blastscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://blast.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/blast.svg",
      mainnet: "eip155:81457",
      metadata: {},
      name: "Blast Sepolia",
      native_asset: "eip155:168587773/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "blast",
        thegraph: "blast-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "blastsepolia",
      slip44: 60,
      slug: "blast_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:60808",
      cashaddr_prefix: null,
      chain_id_hex: "0xed88",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.gobob.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gobob.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/bob.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "BOB",
      native_asset: "eip155:60808/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "bob",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gobob.xyz",
        "https://bob-mainnet.public.blastapi.io",
        "wss://rpc.gobob.xyz",
        "wss://bob-mainnet.public.blastapi.io"
      ],
      short_name: "bob",
      slip44: 60,
      slug: "bob",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bob-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:808813",
      cashaddr_prefix: null,
      chain_id_hex: "0xc576d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bob-sepolia.explorer.gobob.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gobob.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/bob.svg",
      mainnet: "eip155:60808",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "BOB Sepolia",
      native_asset: "eip155:808813/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "bob"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://bob-sepolia.rpc.gobob.xyz",
        "wss://bob-sepolia.rpc.gobob.xyz"
      ],
      short_name: "bob-sepolia",
      slip44: 60,
      slug: "bob_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "boba-bnb"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:56288",
      cashaddr_prefix: null,
      chain_id_hex: "0xdbe0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://boba.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Boba BNB",
      native_asset: null,
      native_name: "Boba Token",
      native_token: "BOBA",
      native_token_address: null,
      parent: "eip155:56",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://bnb.boba.network",
        "https://boba-bnb.gateway.tenderly.co",
        "https://gateway.tenderly.co/public/boba-bnb",
        "https://replica.bnb.boba.network"
      ],
      short_name: "BobaBnb",
      slip44: null,
      slug: "boba_bnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "boba-bnb-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9728",
      cashaddr_prefix: null,
      chain_id_hex: "0x2600",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://boba.network",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:56288",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Boba BNB Testnet",
      native_asset: null,
      native_name: "Boba Token",
      native_token: "BOBA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.bnb.boba.network",
        "https://replica.testnet.bnb.boba.network",
        "https://boba-bnb-testnet.gateway.tenderly.co",
        "wss://wss.testnet.bnb.boba.network"
      ],
      short_name: "BobaBnbTestnet",
      slip44: null,
      slug: "boba_bnb_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "boba-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:288",
      cashaddr_prefix: null,
      chain_id_hex: "0x120",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://boba.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Boba Network",
      native_asset: "eip155:288/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum",
        thegraph: "boba"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.boba.network",
        "https://replica.boba.network",
        "https://boba-ethereum.gateway.tenderly.co",
        "https://gateway.tenderly.co/public/boba-ethereum"
      ],
      short_name: "Boba",
      slip44: 60,
      slug: "boba_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bokuto-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:737373",
      cashaddr_prefix: null,
      chain_id_hex: "0xb405d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bokuto.katanascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://katana.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Bokuto Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "katana-bokuto"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-bokuto.katanarpc.com"
      ],
      short_name: "bokuto",
      slip44: null,
      slug: "bokuto_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3637",
      cashaddr_prefix: null,
      chain_id_hex: "0xe35",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://botanixscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://botanixlabs.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/botanix.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Botanix",
      native_asset: null,
      native_name: "Botanix",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "botanix",
        coingecko_native: "bitcoin",
        thegraph: "botanix"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.botanixlabs.com"
      ],
      short_name: "BTNX",
      slip44: null,
      slug: "botanix",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "botanix-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3636",
      cashaddr_prefix: null,
      chain_id_hex: "0xe34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.botanixscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://botanixlabs.xyz",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/botanix.svg",
      mainnet: "eip155:3637",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Botanix Testnet",
      native_asset: null,
      native_name: "Botanix",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "botanix",
        thegraph: "botanix-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://node.botanixlabs.dev"
      ],
      short_name: "BTNXt",
      slip44: null,
      slug: "botanix_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6001",
      cashaddr_prefix: null,
      chain_id_hex: "0x1771",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://bouncebit.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "BounceBit",
      native_asset: null,
      native_name: "BounceBit",
      native_token: "BB",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "bouncebit"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://fullnode-mainnet.bouncebitapi.com"
      ],
      short_name: "bouncebit-mainnet",
      slip44: null,
      slug: "bouncebit",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "binance",
        "binance-smart-chain",
        "bnb",
        "bnb-chain",
        "bnb-smart-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:56",
      cashaddr_prefix: null,
      chain_id_hex: "0x38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bscscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bnbchain.org/en",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/bsc.svg",
      mainnet: null,
      metadata: {},
      name: "BNB Chain",
      native_asset: "eip155:56/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "BNB",
      native_token_address: null,
      parent: null,
      providers: {
        alchemy: "bnb-mainnet",
        ankr: "bsc",
        arkham: "bsc",
        blockchair: "binance-smart-chain",
        blockscan: "56",
        blocksec: "56",
        ccip: "bsc",
        codeslaw: "bnbchain",
        coingecko: "binance-smart-chain",
        coingecko_native: "binancecoin",
        coinmarketcap: "bsc",
        defillama: "bsc",
        dexscreener: "bsc",
        etherscan_scan: "bscscan.com",
        infura: "bsc-mainnet",
        metasleuth: "bsc",
        moralis: "bsc",
        moralis_wallet: "bsc",
        sourcify: "56",
        thegraph: "bsc"
      },
      reorg_depth: 15,
      rpc_urls: [
        "https://bsc-dataseed1.binance.org",
        "https://bsc-rpc.publicnode.com"
      ],
      short_name: "bnb",
      slip44: 714,
      slug: "bsc",
      status: "active",
      wrapped_native_address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
    },
    {
      aliases: [
        "bnb-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:97",
      cashaddr_prefix: null,
      chain_id_hex: "0x61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bnbchain.org/en",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/bsc-testnet.svg",
      mainnet: "eip155:56",
      metadata: {},
      name: "BSC Testnet",
      native_asset: "eip155:97/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "tBNB",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "bsc-testnet",
        ccip: "bsc",
        infura: "bsc-testnet",
        moralis: "bsc_testnet",
        thegraph: "chapel"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "bnbt",
      slip44: 714,
      slug: "bsc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "camp-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:484",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://camp.cloud.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.campnetwork.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Camp Network",
      native_asset: null,
      native_name: "Camp",
      native_token: "CAMP",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "camp-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.camp.raas.gelato.cloud"
      ],
      short_name: "CampMainnet",
      slip44: null,
      slug: "camp_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7700",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e14",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.oklink.com/canto",
      family: "evm",
      finality_blocks: null,
      info_url: "https://canto.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Canto",
      native_asset: null,
      native_name: "Canto",
      native_token: "CANTO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "canto"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://canto.slingshot.finance",
        "https://canto-rpc.ansybl.io",
        "https://mainnode.plexnode.org:8545",
        "https://canto.gravitychain.io"
      ],
      short_name: "canto",
      slip44: null,
      slug: "canto",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "canto-tesnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7701",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e15",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://canto-test.dex.guru",
      family: "evm",
      finality_blocks: null,
      info_url: "https://canto.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Canto Tesnet",
      native_asset: null,
      native_name: "Testnet Canto",
      native_token: "CANTO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "canto"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-archive.plexnode.wtf"
      ],
      short_name: "TestnetCanto",
      slip44: null,
      slug: "canto_tesnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "carbon-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9790",
      cashaddr_prefix: null,
      chain_id_hex: "0x263e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://carbon.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Carbon EVM",
      native_asset: null,
      native_name: "swth",
      native_token: "SWTH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm-api.carbon.network"
      ],
      short_name: "carbon",
      slip44: null,
      slug: "carbon_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7667",
      cashaddr_prefix: null,
      chain_id_hex: "0x1df3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://carrchain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "CarrChain",
      native_asset: null,
      native_name: "CarrChain Coin",
      native_token: "CARR",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "carnomaly"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.carrchain.io"
      ],
      short_name: "CarrChain-Mainnet",
      slip44: null,
      slug: "carrchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "carrchain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:76672",
      cashaddr_prefix: null,
      chain_id_hex: "0x12b80",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://carrchain.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:7667",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "CarrChain Testnet",
      native_asset: null,
      native_name: "CarrChain Coin",
      native_token: "CARR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-testnet.carrchain.io"
      ],
      short_name: "CarrChain-Testnet",
      slip44: null,
      slug: "carrchain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "celestia",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:celestia",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://celestia.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "celestia",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Celestia",
      native_asset: "cosmos:celestia/slip44:118",
      native_name: "Celestia",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "celestia"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "celestia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:42220",
      cashaddr_prefix: null,
      chain_id_hex: "0xa4ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celoscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.celo.org",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/celo.svg",
      mainnet: null,
      metadata: {},
      name: "Celo",
      native_asset: "eip155:42220/slip44:52752",
      native_name: "CELO",
      native_token: "CELO",
      native_token_address: "0x471ece3750da237f93b8e339c536989b8978a438",
      parent: "eip155:1",
      providers: {
        ccip: "celo",
        coingecko_native: "celo",
        defillama: "celo",
        dexscreener: "celo",
        thegraph: "celo"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://celo-rpc.publicnode.com"
      ],
      short_name: "celo",
      slip44: 52752,
      slug: "celo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:44787",
      cashaddr_prefix: null,
      chain_id_hex: "0xaef3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.celo.org",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/celo.svg",
      mainnet: "eip155:42220",
      metadata: {},
      name: "Celo Alfajores",
      native_asset: "eip155:44787/slip44:52752",
      native_name: "CELO",
      native_token: "CELO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ALFA",
      slip44: 52752,
      slug: "celo_alfajores",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "celo-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:11142220",
      cashaddr_prefix: null,
      chain_id_hex: "0xaa044c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celo-sepolia.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.celo.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/celo.svg",
      mainnet: "eip155:42220",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Celo Sepolia Testnet",
      native_asset: "eip155:11142220/slip44:52752",
      native_name: "CELO",
      native_token: "CELO",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "celo",
        thegraph: "celo-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://forno.celo-sepolia.celo-testnet.org"
      ],
      short_name: "celo-sep",
      slip44: 52752,
      slug: "celo_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2031",
      cashaddr_prefix: null,
      chain_id_hex: "0x7ef",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://centrifuge.subscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://centrifuge.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Centrifuge",
      native_asset: null,
      native_name: "Centrifuge",
      native_token: "CFG",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://fullnode.centrifuge.io",
        "https://centrifuge-parachain.api.onfinality.io/public",
        "https://centrifuge-rpc.dwellir.com",
        "https://rpc-centrifuge.luckyfriday.io"
      ],
      short_name: "cfg",
      slip44: null,
      slug: "centrifuge",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:383353",
      cashaddr_prefix: null,
      chain_id_hex: "0x5d979",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://cheesechain.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "CheeseChain",
      native_asset: null,
      native_name: "CHEESE",
      native_token: "CHEESE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "cheese-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.cheesechain.xyz"
      ],
      short_name: "CheeseChain",
      slip44: null,
      slug: "cheesechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88888",
      cashaddr_prefix: null,
      chain_id_hex: "0x15b38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://chiliz.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Chiliz",
      native_asset: null,
      native_name: "Chiliz",
      native_token: "CHZ",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "chiliz",
        moralis: "chiliz",
        moralis_wallet: "chiliz",
        thegraph: "chiliz"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chzmainnet",
      slip44: null,
      slug: "chiliz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88882",
      cashaddr_prefix: null,
      chain_id_hex: "0x15b32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.chiliz.com/en/chain",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:88888",
      metadata: {},
      name: "Chiliz Testnet",
      native_asset: null,
      native_name: "Chiliz",
      native_token: "CHZ",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "chiliz-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chzspicy",
      slip44: null,
      slug: "chiliz_spicy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "chronicle-yellowstone-lit-protocol-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:175188",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ac54",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://yellowstone-explorer.litprotocol.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://litprotocol.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Chronicle Yellowstone - Lit Protocol Testnet",
      native_asset: null,
      native_name: "Test LPX",
      native_token: "tstLPX",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://yellowstone-rpc.litprotocol.com"
      ],
      short_name: "lpy",
      slip44: null,
      slug: "chronicle_yellowstone_lit_protocol_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4114",
      cashaddr_prefix: null,
      chain_id_hex: "0x1012",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mainnet.citrea.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://citrea.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Citrea",
      native_asset: null,
      native_name: "Citrea BTC",
      native_token: "cBTC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "bitcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.citrea.xyz"
      ],
      short_name: "citrea",
      slip44: null,
      slug: "citrea",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "citrea-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5115",
      cashaddr_prefix: null,
      chain_id_hex: "0x13fb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.citrea.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://citrea.xyz",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:4114",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Citrea Testnet",
      native_asset: null,
      native_name: "Citrea BTC",
      native_token: "cBTC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.citrea.xyz"
      ],
      short_name: "citrea-testnet",
      slip44: null,
      slug: "citrea_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8853",
      cashaddr_prefix: null,
      chain_id_hex: "0x2295",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.myclique.io",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Clique",
      native_asset: "eip155:8853/slip44:60",
      native_name: "Ethereum",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.myclique.io"
      ],
      short_name: null,
      slip44: 60,
      slug: "clique",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:81224",
      cashaddr_prefix: null,
      chain_id_hex: "0x13d48",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.codex.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.codex.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Codex",
      native_asset: "eip155:81224/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.codex.xyz",
        "wss://rpc.codex.xyz"
      ],
      short_name: "codex",
      slip44: 60,
      slug: "codex",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "codex-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:812242",
      cashaddr_prefix: null,
      chain_id_hex: "0xc64d2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.codex-stg.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.codex.xyz",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:81224",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Codex Testnet",
      native_asset: "eip155:812242/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.codex-stg.xyz",
        "wss://rpc.codex-stg.xyz"
      ],
      short_name: "codex-testnet",
      slip44: 60,
      slug: "codex_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "coinex-smart-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:52",
      cashaddr_prefix: null,
      chain_id_hex: "0x34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.coinex.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "CoinEx Smart Chain",
      native_asset: null,
      native_name: "CoinEx Chain Native Token",
      native_token: "cet",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.coinex.net"
      ],
      short_name: "cet",
      slip44: null,
      slug: "coinex_smart_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "coinex-smart-chain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:53",
      cashaddr_prefix: null,
      chain_id_hex: "0x35",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.coinex.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:52",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "CoinEx Smart Chain Testnet",
      native_asset: null,
      native_name: "CoinEx Chain Test Native Token",
      native_token: "cett",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.coinex.net"
      ],
      short_name: "tcet",
      slip44: null,
      slug: "coinex_smart_chain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:12739",
      cashaddr_prefix: null,
      chain_id_hex: "0x31c3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Concrete",
      native_asset: "eip155:12739/slip44:60",
      native_name: null,
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "concrete",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "conflux-espace"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1030",
      cashaddr_prefix: null,
      chain_id_hex: "0x406",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://confluxnetwork.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Conflux eSpace",
      native_asset: null,
      native_name: "CFX",
      native_token: "CFX",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "conflux-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm.confluxrpc.com"
      ],
      short_name: "cfx",
      slip44: null,
      slug: "conflux_espace",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "connext-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6398",
      cashaddr_prefix: null,
      chain_id_hex: "0x18fe",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/everclear.svg",
      mainnet: "eip155:25327",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Connext Sepolia",
      native_asset: "eip155:6398/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.connext-sepolia.gelato.digital"
      ],
      short_name: "connext-sepolia",
      slip44: 60,
      slug: "connext_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:432",
      cashaddr_prefix: null,
      chain_id_hex: "0x1b0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Converge",
      native_asset: "eip155:432/slip44:60",
      native_name: null,
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "converge",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:668668",
      cashaddr_prefix: null,
      chain_id_hex: "0xa33fc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://conwai.calderaexplorer.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://conwai.net",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Conwai",
      native_asset: null,
      native_name: "Conwai",
      native_token: "CNW",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "conwai"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://conwai.calderachain.xyz/http"
      ],
      short_name: "cnw",
      slip44: null,
      slug: "conwai",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1116",
      cashaddr_prefix: null,
      chain_id_hex: "0x45c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.coredao.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.coredao.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/core.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Core Blockchain",
      native_asset: null,
      native_name: "Core Blockchain Native Token",
      native_token: "CORE",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "core",
        coingecko_native: "coredaoorg"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.coredao.org",
        "https://rpc-core.icecreamswap.com",
        "https://core.drpc.org",
        "wss://core.drpc.org"
      ],
      short_name: "core",
      slip44: null,
      slug: "core",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "core-blockchain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1115",
      cashaddr_prefix: null,
      chain_id_hex: "0x45b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.test.btcs.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.coredao.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/core.svg",
      mainnet: "eip155:1116",
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Core Blockchain Testnet",
      native_asset: null,
      native_name: "Core Blockchain Testnet Native Token",
      native_token: "tCORE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.test.btcs.network"
      ],
      short_name: "tcore",
      slip44: null,
      slug: "core_blockchain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "core-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1114",
      cashaddr_prefix: null,
      chain_id_hex: "0x45a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.test2.btcs.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.coredao.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/core.svg",
      mainnet: "eip155:1116",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Core Blockchain Testnet2",
      native_asset: null,
      native_name: "Core Blockchain Testnet2 Native Token",
      native_token: "tCORE2",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "core"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.test2.btcs.network"
      ],
      short_name: "tcore2",
      slip44: null,
      slug: "core_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:21000000",
      cashaddr_prefix: null,
      chain_id_hex: "0x1406f40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cornscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://usecorn.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/corn.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Corn",
      native_asset: null,
      native_name: "Bitcorn",
      native_token: "BTCN",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "corn",
        coingecko_native: "bitcoin",
        thegraph: "corn"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.corn-rpc.com",
        "https://rpc.ankr.com/corn_maizenet",
        "https://maizenet-rpc.usecorn.com"
      ],
      short_name: "corn",
      slip44: null,
      slug: "corn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "corn-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:21000001",
      cashaddr_prefix: null,
      chain_id_hex: "0x1406f41",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.cornscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://usecorn.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/corn.svg",
      mainnet: "eip155:21000000",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Corn Testnet",
      native_asset: null,
      native_name: "Bitcorn",
      native_token: "BTCN",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "corn",
        thegraph: "corn-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.corn-rpc.com",
        "https://rpc.ankr.com/corn_testnet",
        "https://testnet-rpc.usecorn.com"
      ],
      short_name: "corn-testnet",
      slip44: null,
      slug: "corn_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cosmoshub-mainnet",
        "cosmoshub-4",
        "gaia"
      ],
      base58_versions: [],
      bech32_hrp: "cosmos",
      block_time_seconds: 5.5,
      brand_color: "#2E3148",
      caip2: "cosmos:cosmoshub-4",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/cosmos",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://cosmos.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Cosmos Hub",
      native_asset: "cosmos:cosmoshub-4/slip44:118",
      native_name: "Atom",
      native_token: "ATOM",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko: "cosmos",
        coingecko_native: "cosmos",
        cosmos_directory: "https://rest.cosmos.directory/cosmoshub",
        cosmos_lcd: "https://cosmos-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "cosmoshub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2632500",
      cashaddr_prefix: null,
      chain_id_hex: "0x282b34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mainnet.cotiscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://coti.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "COTI",
      native_asset: null,
      native_name: "COTI",
      native_token: "COTI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "coti"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.coti.io/rpc"
      ],
      short_name: "coti",
      slip44: null,
      slug: "coti",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "coti-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7082400",
      cashaddr_prefix: null,
      chain_id_hex: "0x6c11a0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.cotiscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://coti.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:2632500",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "COTI Testnet",
      native_asset: null,
      native_name: "COTI",
      native_token: "COTI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.coti.io/rpc",
        "https://testnet-magnus-validator.coti.io/rpc",
        "https://testnet-minor-validator.coti.io/rpc"
      ],
      short_name: "coti-testnet",
      slip44: null,
      slug: "coti_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:102030",
      cashaddr_prefix: null,
      chain_id_hex: "0x18e8e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://creditcoin.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://creditcoin.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/creditcoin.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Creditcoin",
      native_asset: null,
      native_name: "CTC",
      native_token: "CTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "creditcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet3.creditcoin.network"
      ],
      short_name: "ctc",
      slip44: null,
      slug: "creditcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "creditcoin-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:102031",
      cashaddr_prefix: null,
      chain_id_hex: "0x18e8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://creditcoin-testnet.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://creditcoin.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/creditcoin.svg",
      mainnet: "eip155:102030",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Creditcoin Testnet",
      native_asset: null,
      native_name: "Testnet CTC",
      native_token: "tCTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "creditcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.cc3-testnet.creditcoin.network"
      ],
      short_name: "ctctest",
      slip44: null,
      slug: "creditcoin_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cro"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:25",
      cashaddr_prefix: null,
      chain_id_hex: "0x19",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cronoscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://cronos.org",
      is_testnet: false,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/cronos.svg",
      mainnet: null,
      metadata: {},
      name: "Cronos",
      native_asset: "eip155:25/slip44:394",
      native_name: "Cronos",
      native_token: "CRO",
      native_token_address: null,
      parent: null,
      providers: {
        arkham: "cronos",
        ccip: "cronos",
        coingecko_native: "crypto-com-chain",
        defillama: "cronos",
        dexscreener: "cronos",
        moralis: "cronos",
        moralis_wallet: "cronos",
        thegraph: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "cro",
      slip44: 394,
      slug: "cronos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cronos-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:338",
      cashaddr_prefix: null,
      chain_id_hex: "0x152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://cronos.org",
      is_testnet: true,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/cronos.svg",
      mainnet: "eip155:25",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Cronos Testnet",
      native_asset: "eip155:338/slip44:394",
      native_name: "Cronos Test Coin",
      native_token: "TCRO",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm-t3.cronos.org",
        "https://cronos-testnet.drpc.org",
        "wss://cronos-testnet.drpc.org"
      ],
      short_name: "tcro",
      slip44: 394,
      slug: "cronos_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cronos-testnet-zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:240",
      cashaddr_prefix: null,
      chain_id_hex: "0xf0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs-zkevm.cronos.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/cronos-zkevm.svg",
      mainnet: "eip155:388",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Cronos zkEVM Testnet",
      native_asset: null,
      native_name: "Cronos zkEVM Test Coin",
      native_token: "zkTCRO",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "cronos-zkevm"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.zkevm.cronos.org"
      ],
      short_name: "zkTCRO",
      slip44: null,
      slug: "cronos_testnet_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cronos-zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:388",
      cashaddr_prefix: null,
      chain_id_hex: "0x184",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://cronos.org/zkevm",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/cronos-zkevm.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Cronos zkEVM",
      native_asset: null,
      native_name: "Cronos zkEVM CRO",
      native_token: "zkCRO",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "cronos-zkevm",
        coingecko_native: "crypto-com-chain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.zkevm.cronos.org"
      ],
      short_name: "zkCRO",
      slip44: null,
      slug: "cronos_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7560",
      cashaddr_prefix: null,
      chain_id_hex: "0x1d88",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cyberscan.co",
      family: "evm",
      finality_blocks: null,
      info_url: "https://cyber.co",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Cyber",
      native_asset: "eip155:7560/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://cyber.alt.technology",
        "https://rpc.cyber.co",
        "wss://cyber-ws.alt.technology/",
        "wss://rpc.cyber.co/"
      ],
      short_name: "cyeth",
      slip44: 60,
      slug: "cyber",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "darwinia-koi-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:701",
      cashaddr_prefix: null,
      chain_id_hex: "0x2bd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://koi-scan.darwinia.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://darwinia.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Darwinia Koi Testnet",
      native_asset: null,
      native_name: "Koi Network Native Token",
      native_token: "KRING",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://koi-rpc.darwinia.network"
      ],
      short_name: "darwinia-koi",
      slip44: null,
      slug: "darwinia_koi_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "darwinia-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:46",
      cashaddr_prefix: null,
      chain_id_hex: "0x2e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.darwinia.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://darwinia.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Darwinia Network",
      native_asset: null,
      native_name: "Darwinia Network Native Token",
      native_token: "RING",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.darwinia.network",
        "https://darwinia-rpc.dcdao.box",
        "https://darwinia-rpc.dwellir.com",
        "https://darwinia.rpc.subquery.network/public"
      ],
      short_name: "darwinia",
      slip44: null,
      slug: "darwinia_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "darwinia-pangoro-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:45",
      cashaddr_prefix: null,
      chain_id_hex: "0x2d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://darwinia.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Darwinia Pangoro Testnet",
      native_asset: null,
      native_name: "Pangoro Network Native Token",
      native_token: "ORING",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://pangoro-rpc.darwinia.network"
      ],
      short_name: "pangoro",
      slip44: null,
      slug: "darwinia_pangoro_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "data-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1514",
      cashaddr_prefix: null,
      chain_id_hex: "0x5ea",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://datanetscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://datafdn.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Data Network",
      native_asset: null,
      native_name: "DATA",
      native_token: "DATA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "story-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.datarpc.io"
      ],
      short_name: "data",
      slip44: null,
      slug: "data_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:19880818",
      cashaddr_prefix: null,
      chain_id_hex: "0x12f5b72",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.dbcscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.deepbrainchain.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "DeepBrainChain",
      native_asset: null,
      native_name: "DeepBrainChain",
      native_token: "DBC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "deepbrain-chain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.dbcwallet.io"
      ],
      short_name: "DBC",
      slip44: null,
      slug: "deepbrainchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "deepbrainchain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:19850818",
      cashaddr_prefix: null,
      chain_id_hex: "0x12ee642",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.dbcscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.deepbrainchain.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:19880818",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "DeepBrainChain Testnet",
      native_asset: null,
      native_name: "DeepBrainChain",
      native_token: "tDBC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-testnet.dbcwallet.io"
      ],
      short_name: "tDBC",
      slip44: null,
      slug: "deepbrainchain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "degen-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:666666666",
      cashaddr_prefix: null,
      chain_id_hex: "0x27bc86aa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://degen.tips",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Degen Chain",
      native_asset: null,
      native_name: "DEGEN",
      native_token: "DEGEN",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "degen-base"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.degen.tips"
      ],
      short_name: "degen-chain",
      slip44: null,
      slug: "degen_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "deprecated-cronos-zkevm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:282",
      cashaddr_prefix: null,
      chain_id_hex: "0x11a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs-zkevm.cronos.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Deprecated Cronos zkEVM Testnet",
      native_asset: null,
      native_name: "Cronos zkEVM Test Coin",
      native_token: "zkTCRO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://deprecated.testnet.zkevm.cronos.org"
      ],
      short_name: "deprecated-zkTCRO",
      slip44: null,
      slug: "deprecated_cronos_zkevm_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dexalot-subnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:432204",
      cashaddr_prefix: null,
      chain_id_hex: "0x6984c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets.avax.network/dexalot",
      family: "evm",
      finality_blocks: null,
      info_url: "https://dexalot.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Dexalot Subnet",
      native_asset: null,
      native_name: "Dexalot",
      native_token: "ALOT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "dexalot"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/dexalot/mainnet/rpc"
      ],
      short_name: "dexalot",
      slip44: null,
      slug: "dexalot_subnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dexalot-subnet-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:432201",
      cashaddr_prefix: null,
      chain_id_hex: "0x69849",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets-test.avax.network/dexalot",
      family: "evm",
      finality_blocks: null,
      info_url: "https://dexalot.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:432204",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Dexalot Subnet Testnet",
      native_asset: null,
      native_name: "Dexalot",
      native_token: "ALOT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/dexalot/testnet/rpc"
      ],
      short_name: "dexalot-testnet",
      slip44: null,
      slug: "dexalot_subnet_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dfk-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:53935",
      cashaddr_prefix: null,
      chain_id_hex: "0xd2af",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://defikingdoms.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "DFK Chain",
      native_asset: null,
      native_name: "Jewel",
      native_token: "JEWEL",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "defi-kingdoms"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/defi-kingdoms/dfk-chain/rpc"
      ],
      short_name: "DFK",
      slip44: null,
      slug: "dfk_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:202110",
      cashaddr_prefix: null,
      chain_id_hex: "0x3157e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Dinari",
      native_asset: null,
      native_name: null,
      native_token: "DGAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "usd-coin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/dinari/mainnet/rpc"
      ],
      short_name: null,
      slip44: null,
      slug: "dinari",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dm2-verse"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:68770",
      cashaddr_prefix: null,
      chain_id_hex: "0x10ca2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.dm2verse.dmm.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://seamoon.dmm.com",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "DM2 Verse",
      native_asset: null,
      native_name: "OAS",
      native_token: "OAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "oasys"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.dm2verse.dmm.com"
      ],
      short_name: "dm2",
      slip44: null,
      slug: "dm2_verse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dodochain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:53457",
      cashaddr_prefix: null,
      chain_id_hex: "0xd0d1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-scan.dodochain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.dodochain.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "DODOchain testnet",
      native_asset: null,
      native_name: "DODO",
      native_token: "DODO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://dodochain-testnet.alt.technology",
        "wss://dodochain-testnet.alt.technology/ws"
      ],
      short_name: "dodochain",
      slip44: null,
      slug: "dodochain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2000",
      cashaddr_prefix: null,
      chain_id_hex: "0x7d0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.dogechain.dog",
      family: "evm",
      finality_blocks: null,
      info_url: "https://dogechain.dog",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Dogechain",
      native_asset: null,
      native_name: "Dogecoin",
      native_token: "DOGE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "dogecoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.dogechain.dog",
        "https://rpc01-sg.dogechain.dog",
        "https://rpc.ankr.com/dogechain"
      ],
      short_name: "dc",
      slip44: null,
      slug: "dogechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "doge"
      ],
      base58_versions: [
        30,
        22
      ],
      bech32_hrp: null,
      block_time_seconds: 60,
      brand_color: "#C2A633",
      caip2: "bip122:1a91e3dace36e2be3bf030a65679fe82",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/dogecoin",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://dogecoin.com",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/bip122/caip2",
          "https://github.com/dogecoin/dogecoin"
        ]
      },
      name: "Dogecoin",
      native_asset: "bip122:1a91e3dace36e2be3bf030a65679fe82/slip44:3",
      native_name: "Dogecoin",
      native_token: "DOGE",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "dogecoin",
        coingecko_native: "dogecoin"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 3,
      slug: "dogecoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:97477",
      cashaddr_prefix: null,
      chain_id_hex: "0x17cc5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.doma.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://doma.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Doma",
      native_asset: "eip155:97477/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.doma.xyz"
      ],
      short_name: "doma",
      slip44: 60,
      slug: "doma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dos-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7979",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f2b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://doscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://doschain.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "DOS Chain",
      native_asset: null,
      native_name: "DOS",
      native_token: "DOS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://main.doschain.com"
      ],
      short_name: "dos",
      slip44: null,
      slug: "dos_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5545",
      cashaddr_prefix: null,
      chain_id_hex: "0x15a9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://duckchain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "DuckChain",
      native_asset: null,
      native_name: "TON",
      native_token: "TON",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "the-open-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.duckchain.io",
        "https://rpc-hk.duckchain.io"
      ],
      short_name: "Duck-Chain-Mainnet",
      slip44: null,
      slug: "duckchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "dymension-hub"
      ],
      base58_versions: [],
      bech32_hrp: "dym",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:dymension_1100-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {
        address: "account",
        token: null
      },
      explorer_url: "https://www.mintscan.io/dymension",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://portal.dymension.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "dymension",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Dymension Hub",
      native_asset: "cosmos:dymension_1100-1/slip44:60",
      native_name: "Dymension",
      native_token: "DYM",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "dymension"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "dymension_hub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ebi-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98881",
      cashaddr_prefix: null,
      chain_id_hex: "0x18241",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://ebi.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ebi Chain",
      native_asset: "eip155:98881/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ebi.xyz"
      ],
      short_name: "ebi",
      slip44: 60,
      slug: "ebi_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "echos-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4321",
      cashaddr_prefix: null,
      chain_id_hex: "0x10e1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.echos.fun",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Echos Chain",
      native_asset: null,
      native_name: "USD Coin",
      native_token: "USDC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "usd-coin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-echos-mainnet-0.t.conduit.xyz"
      ],
      short_name: "echos",
      slip44: null,
      slug: "echos_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "eco-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:471923",
      cashaddr_prefix: null,
      chain_id_hex: "0x73373",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://eco-testnet.explorer.caldera.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Eco Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://eco-testnet.rpc.caldera.xyz/http"
      ],
      short_name: null,
      slip44: null,
      slug: "eco_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:714",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ca",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://eden.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://celestia.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Eden",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "celestia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.eden.gateway.fm"
      ],
      short_name: "eden",
      slip44: null,
      slug: "eden",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "eden-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3735928814",
      cashaddr_prefix: null,
      chain_id_hex: "0xdeadbfee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:714",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Eden Testnet",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "eden_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3343",
      cashaddr_prefix: null,
      chain_id_hex: "0xd0f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://edge-mainnet.explorer.alchemy.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.edgex.exchange",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/edge.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Edge",
      native_asset: "eip155:3343/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "edge",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://edge-mainnet.g.alchemy.com/public"
      ],
      short_name: "edge",
      slip44: 60,
      slug: "edge",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "edge-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:33431",
      cashaddr_prefix: null,
      chain_id_hex: "0x8297",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://edge-testnet.explorer.alchemy.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.edgex.exchange",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/edge.svg",
      mainnet: "eip155:3343",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Edge Testnet",
      native_asset: "eip155:33431/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "edge"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://edge-testnet.g.alchemy.com/public"
      ],
      short_name: "edge-testnet",
      slip44: 60,
      slug: "edge_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "edu-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41923",
      cashaddr_prefix: null,
      chain_id_hex: "0xa3c3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://raas.gelato.network/rollups/details/public/edu-chain",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "EDU Chain",
      native_asset: null,
      native_name: "EDU",
      native_token: "EDU",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "edu-coin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.edu-chain.raas.gelato.cloud"
      ],
      short_name: "edu-chain",
      slip44: null,
      slug: "edu_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:52014",
      cashaddr_prefix: null,
      chain_id_hex: "0xcb2e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockexplorer.electroneum.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://electroneum.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Electroneum",
      native_asset: null,
      native_name: "Electroneum",
      native_token: "ETN",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "electroneum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.electroneum.com",
        "https://rpc.ankr.com/electroneum"
      ],
      short_name: "etn-mainnet",
      slip44: null,
      slug: "electroneum",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "endurance-smart-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:648",
      cashaddr_prefix: null,
      chain_id_hex: "0x288",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.endurance.fusionist.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ace.fusionist.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Endurance Smart Chain",
      native_asset: null,
      native_name: "Endurance Chain Native Token",
      native_token: "ACE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "endurance"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-endurance.fusionist.io"
      ],
      short_name: "ace",
      slip44: null,
      slug: "endurance_smart_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:173",
      cashaddr_prefix: null,
      chain_id_hex: "0xad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.eniac.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://eniac.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "ENI",
      native_asset: null,
      native_name: "EGAS",
      native_token: "EGAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "eniac-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.eniac.network",
        "https://rpc1.eniac.network",
        "https://rpc2.eniac.network",
        "https://enirpc.com"
      ],
      short_name: "eni",
      slip44: null,
      slug: "eni",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 13,
      brand_color: "#328332",
      caip2: "eip155:61",
      cashaddr_prefix: null,
      chain_id_hex: "0x3d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/etc/mainnet",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ethereumclassic.org",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {
        note: "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk."
      },
      name: "Ethereum Classic",
      native_asset: "eip155:61/slip44:61",
      native_name: "Ether",
      native_token: "ETC",
      native_token_address: null,
      parent: null,
      providers: {
        blockscout: "https://etc.blockscout.com/api",
        coingecko_native: "ethereum-classic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://etc.etcdesktop.com"
      ],
      short_name: "etc",
      slip44: 61,
      slug: "etc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum",
        "ethereum-mainnet",
        "eth-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:1",
      cashaddr_prefix: null,
      chain_id_hex: "0x1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://etherscan.io",
      family: "evm",
      finality_blocks: 96,
      info_url: "https://ethereum.org",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: null,
      metadata: {},
      name: "Ethereum",
      native_asset: "eip155:1/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        alchemy: "eth-mainnet",
        ankr: "eth",
        arkham: "ethereum",
        blockchair: "ethereum",
        blockscan: "1",
        blockscout: "https://eth.blockscout.com/api",
        blocksec: "1",
        ccip: "ethereum",
        codeslaw: "ethereum",
        coingecko: "ethereum",
        coingecko_native: "ethereum",
        coinmarketcap: "ethereum",
        cowswap: "mainnet",
        defillama: "ethereum",
        dexscreener: "ethereum",
        etherscan_scan: "etherscan.io",
        infura: "mainnet",
        metasleuth: "eth",
        moralis: "eth",
        moralis_wallet: "eth",
        scanscope: "eth",
        sourcify: "1",
        thegraph: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-rpc.publicnode.com",
        "https://eth.drpc.org",
        "https://mainnet.gateway.tenderly.co"
      ],
      short_name: "eth",
      slip44: 60,
      slug: "eth",
      status: "active",
      wrapped_native_address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5064014",
      cashaddr_prefix: null,
      chain_id_hex: "0x4d454e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Ethereal",
      native_asset: null,
      native_name: null,
      native_token: "USDe",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethena-usde"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ethereal.trade"
      ],
      short_name: null,
      slip44: null,
      slug: "ethereal",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum-hoodi-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:560048",
      cashaddr_prefix: null,
      chain_id_hex: "0x88bb0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://hoodi.ethpandaops.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: "eip155:1",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ethereum Hoodi",
      native_asset: "eip155:560048/slip44:60",
      native_name: "Hoodi Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ethereum",
        thegraph: "hoodi"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.hoodi.ethpandaops.io"
      ],
      short_name: "hoe",
      slip44: 60,
      slug: "ethereum_hoodi_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:42793",
      cashaddr_prefix: null,
      chain_id_hex: "0xa729",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.etherlink.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://etherlink.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/etherlink.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Etherlink",
      native_asset: null,
      native_name: "tez",
      native_token: "XTZ",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "etherlink",
        coingecko_native: "tezos",
        thegraph: "etherlink-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://node.mainnet.etherlink.com"
      ],
      short_name: "etlk",
      slip44: null,
      slug: "etherlink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "etherlink-ghostnet-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:128123",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f47b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.explorer.etherlink.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://etherlink.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/etherlink.svg",
      mainnet: "eip155:42793",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Etherlink Ghostnet Testnet",
      native_asset: null,
      native_name: "tez",
      native_token: "XTZ",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://node.ghostnet.etherlink.com"
      ],
      short_name: "etlt",
      slip44: null,
      slug: "etherlink_ghostnet_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:25327",
      cashaddr_prefix: null,
      chain_id_hex: "0x62ef",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/everclear.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Everclear",
      native_asset: "eip155:25327/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "everclear",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.everclear.raas.gelato.cloud"
      ],
      short_name: "Everclear",
      slip44: 60,
      slug: "everclear",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9001",
      cashaddr_prefix: null,
      chain_id_hex: "0x2329",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://evmos.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Evmos",
      native_asset: null,
      native_name: "Evmos",
      native_token: "EVMOS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "evmos"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evmos.lava.build",
        "https://evmos-evm-rpc.publicnode.com",
        "wss://evmos.lava.build/websocket",
        "wss://evmos-evm-rpc.publicnode.com"
      ],
      short_name: "evmos",
      slip44: null,
      slug: "evmos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "evmos-cosmos"
      ],
      base58_versions: [],
      bech32_hrp: "evmos",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:evmos_9001-2",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://evmos.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "evmos",
        sources: [
          "cosmos/chain-registry"
        ]
      },
      name: "Evmos",
      native_asset: "cosmos:evmos_9001-2/slip44:60",
      native_name: "Evmos",
      native_token: "EVMOS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "evmos_cosmos",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ftm",
        "fantom-opera"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:250",
      cashaddr_prefix: null,
      chain_id_hex: "0xfa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ftmscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://fantom.foundation",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/fantom.svg",
      mainnet: null,
      metadata: {},
      name: "Fantom",
      native_asset: "eip155:250/slip44:1007",
      native_name: "Fantom",
      native_token: "FTM",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "fantom",
        blockscan: "250",
        blocksec: "250",
        ccip: "fantom",
        coingecko: "fantom",
        coingecko_native: "fantom",
        coinmarketcap: "fantom",
        defillama: "fantom",
        dexscreener: "fantom",
        etherscan_scan: "ftmscan.com",
        metasleuth: "fantom",
        sourcify: "250",
        thegraph: "fantom"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpcapi.fantom.network"
      ],
      short_name: "ftm",
      slip44: 1007,
      slug: "fantom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:4002",
      cashaddr_prefix: null,
      chain_id_hex: "0xfa2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.fantom.foundation/quick-start/short-guide#fantom-testnet",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/fantom-testnet.svg",
      mainnet: "eip155:250",
      metadata: {},
      name: "Fantom Testnet",
      native_asset: "eip155:4002/slip44:1007",
      native_name: "Fantom",
      native_token: "FTM",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "fantom",
        thegraph: "fantom-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tftm",
      slip44: 1007,
      slug: "fantom_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "fhenix-helium"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8008135",
      cashaddr_prefix: null,
      chain_id_hex: "0x7a31c7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.helium.fhenix.zone",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.fhenix.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Fhenix Helium",
      native_asset: null,
      native_name: "tFHE",
      native_token: "tFHE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://api.helium.fhenix.zone"
      ],
      short_name: "fhe-helium",
      slip44: null,
      slug: "fhenix_helium",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:314",
      cashaddr_prefix: null,
      chain_id_hex: "0x13a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.glif.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://filecoin.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Filecoin",
      native_asset: "eip155:314/slip44:461",
      native_name: "filecoin",
      native_token: "FIL",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "filecoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.node.glif.io",
        "https://rpc.ankr.com/filecoin",
        "https://filecoin-mainnet.chainstacklabs.com/rpc/v1",
        "https://filfox.info/rpc/v1"
      ],
      short_name: "filecoin",
      slip44: 461,
      slug: "filecoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "filecoin-local-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:31415926",
      cashaddr_prefix: null,
      chain_id_hex: "0x1df5e76",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://filecoin.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:314",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Filecoin - Local testnet",
      native_asset: "eip155:31415926/slip44:461",
      native_name: "testnet filecoin",
      native_token: "tFIL",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "filecoin-local",
      slip44: 461,
      slug: "filecoin_local_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:253368190",
      cashaddr_prefix: null,
      chain_id_hex: "0xf1a177e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.flame.astria.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://astria.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Flame",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "celestia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.flame.astria.org"
      ],
      short_name: "flame",
      slip44: null,
      slug: "flame",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "flame-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:16604737732183",
      cashaddr_prefix: null,
      chain_id_hex: "0xf1a177e7e57",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.flame.dawn-1.astria.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://astria.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:253368190",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Flame Testnet",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.flame.dawn-1.astria.org"
      ],
      short_name: "flame-testnet",
      slip44: null,
      slug: "flame_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:14",
      cashaddr_prefix: null,
      chain_id_hex: "0xe",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://flare-explorer.flare.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://flare.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Flare",
      native_asset: null,
      native_name: "Flare",
      native_token: "FLR",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "flare-networks"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://flare-api.flare.network/ext/C/rpc",
        "https://flare.rpc.thirdweb.com",
        "https://flare-bundler.etherspot.io",
        "https://rpc.ankr.com/flare"
      ],
      short_name: "flr",
      slip44: null,
      slug: "flare",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747",
      cashaddr_prefix: null,
      chain_id_hex: "0x2eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm.flowscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://developers.flow.com/evm/about",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/flow.svg",
      mainnet: null,
      metadata: {},
      name: "Flow",
      native_asset: "eip155:747/slip44:539",
      native_name: "FLOW",
      native_token: "FLOW",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "flow",
        moralis: "flow",
        moralis_wallet: "flow"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "flow-mainnet",
      slip44: 539,
      slug: "flow",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:545",
      cashaddr_prefix: null,
      chain_id_hex: "0x221",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.evm.flow.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://developers.flow.com/evm/about",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/flow.svg",
      mainnet: "eip155:747",
      metadata: {},
      name: "Flow Testnet",
      native_asset: "eip155:545/slip44:539",
      native_name: "FLOW",
      native_token: "FLOW",
      native_token_address: null,
      parent: null,
      providers: {
        moralis: "flow_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "flow-testnet",
      slip44: 539,
      slug: "flow_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9999999",
      cashaddr_prefix: null,
      chain_id_hex: "0x98967f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.mainnet.fluence.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://fluence.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Fluence",
      native_asset: null,
      native_name: "FLT",
      native_token: "FLT",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "fluence-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fluence.dev",
        "wss://ws.mainnet.fluence.dev/"
      ],
      short_name: "fluence",
      slip44: null,
      slug: "fluence",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:25363",
      cashaddr_prefix: null,
      chain_id_hex: "0x6313",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://fluentscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.fluent.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Fluent",
      native_asset: "eip155:25363/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.fluent.xyz"
      ],
      short_name: "fluent",
      slip44: 60,
      slug: "fluent",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "form-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:478",
      cashaddr_prefix: null,
      chain_id_hex: "0x1de",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.form.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://info.form.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Form Network",
      native_asset: "eip155:478/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.form.network/http",
        "wss://rpc.form.network/ws"
      ],
      short_name: "formnetwork",
      slip44: 60,
      slug: "form_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "form-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:132902",
      cashaddr_prefix: null,
      chain_id_hex: "0x20726",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-explorer.form.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://sepolia-info.form.network",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Form Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia-rpc.form.network/http",
        "wss://sepolia-rpc.form.network/ws"
      ],
      short_name: "formtestnet",
      slip44: null,
      slug: "form_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:984122",
      cashaddr_prefix: null,
      chain_id_hex: "0xf043a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.forma.art",
      family: "evm",
      finality_blocks: null,
      info_url: "https://forma.art",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Forma",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "celestia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.forma.art"
      ],
      short_name: "forma",
      slip44: null,
      slug: "forma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "forma-sketchpad"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:984123",
      cashaddr_prefix: null,
      chain_id_hex: "0xf043b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.sketchpad-1.forma.art",
      family: "evm",
      finality_blocks: null,
      info_url: "https://forma.art",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Forma Sketchpad",
      native_asset: null,
      native_name: "TIA",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "celestia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.sketchpad-1.forma.art"
      ],
      short_name: "sketchpad",
      slip44: null,
      slug: "forma_sketchpad",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "fractal-confluence"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2152",
      cashaddr_prefix: null,
      chain_id_hex: "0x868",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evmscan.fra.tech",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Fractal Confluence",
      native_asset: null,
      native_name: "Fractal",
      native_token: "FRA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "findora"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-mainnet.fra.tech"
      ],
      short_name: null,
      slip44: null,
      slug: "fractal_confluence",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:252",
      cashaddr_prefix: null,
      chain_id_hex: "0xfc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://fraxscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mainnet.frax.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/fraxtal.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Fraxtal",
      native_asset: null,
      native_name: "Frax",
      native_token: "FRAX",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "fraxtal",
        coingecko_native: "frax-share",
        thegraph: "fraxtal"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.frax.com",
        "https://fraxtal-rpc.publicnode.com",
        "wss://fraxtal-rpc.publicnode.com"
      ],
      short_name: "frax",
      slip44: null,
      slug: "fraxtal",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "fraxtal-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2522",
      cashaddr_prefix: null,
      chain_id_hex: "0x9da",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://testnet.frax.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/fraxtal.svg",
      mainnet: "eip155:252",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Fraxtal Testnet",
      native_asset: null,
      native_name: "Frax",
      native_token: "FRAX",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "fraxtal"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "fraxtal-testnet",
      slip44: null,
      slug: "fraxtal_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:33979",
      cashaddr_prefix: null,
      chain_id_hex: "0x84bb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://funkichain.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Funki",
      native_asset: "eip155:33979/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-mainnet.funkichain.com",
        "wss://rpc-mainnet.funkichain.com"
      ],
      short_name: "funki",
      slip44: 60,
      slug: "funki",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:122",
      cashaddr_prefix: null,
      chain_id_hex: "0x7a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://fuse.io",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Fuse",
      native_asset: null,
      native_name: "Fuse",
      native_token: "FUSE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "fuse-network-token",
        thegraph: "fuse"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "fuse",
      slip44: null,
      slug: "fuse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:123",
      cashaddr_prefix: null,
      chain_id_hex: "0x7b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.fusespark.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.fuse.io/general/fuse-network-blockchain/fuse-testnet",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:122",
      metadata: {},
      name: "Fuse Sparknet",
      native_asset: null,
      native_name: "Spark",
      native_token: "SPARK",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "spark",
      slip44: null,
      slug: "fuse_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:613419",
      cashaddr_prefix: null,
      chain_id_hex: "0x95c2b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.galactica.com",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Galactica",
      native_asset: null,
      native_name: "GNET",
      native_token: "GNET",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "galactica-com"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://galactica-mainnet.g.alchemy.com/public"
      ],
      short_name: null,
      slip44: null,
      slug: "galactica",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "galadriel-devnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:696969",
      cashaddr_prefix: null,
      chain_id_hex: "0xaa289",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://galadriel.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Galadriel Devnet",
      native_asset: null,
      native_name: "Galadriel Devnet token",
      native_token: "GAL",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://devnet.galadriel.com"
      ],
      short_name: "galadriel-devnet",
      slip44: null,
      slug: "galadriel_devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2187",
      cashaddr_prefix: null,
      chain_id_hex: "0x88b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mainnet.game7.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://game7.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Game7",
      native_asset: null,
      native_name: "G7",
      native_token: "G7",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "game7"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.game7.io",
        "https://mainnet-rpc.game7.build",
        "wss://mainnet-rpc.game7.io",
        "wss://mainnet-rpc.game7.build"
      ],
      short_name: "g7",
      slip44: null,
      slug: "game7",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "gate-layer"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10088",
      cashaddr_prefix: null,
      chain_id_hex: "0x2768",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.gatescan.org/gatelayer",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gatechain.io/gatelayer",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Gate Layer",
      native_asset: null,
      native_name: "GateToken",
      native_token: "GT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://gatelayer-mainnet.gatenode.cc"
      ],
      short_name: "GateLayer",
      slip44: null,
      slug: "gate_layer",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "gate-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10087",
      cashaddr_prefix: null,
      chain_id_hex: "0x2767",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:10088",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Gate-Testnet",
      native_asset: null,
      native_name: "GateToken",
      native_token: "GT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "gate_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:86",
      cashaddr_prefix: null,
      chain_id_hex: "0x56",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.gatescan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.gatechain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "GateChain",
      native_asset: null,
      native_name: "GateToken",
      native_token: "GT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://evm.gatenode.cc"
      ],
      short_name: "gt",
      slip44: null,
      slug: "gatechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "gatechain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:85",
      cashaddr_prefix: null,
      chain_id_hex: "0x55",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.gatescan.org/testnet",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.gatechain.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:86",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "GateChain Testnet",
      native_asset: null,
      native_name: "GateToken",
      native_token: "GT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.gatenode.cc"
      ],
      short_name: "gttest",
      slip44: null,
      slug: "gatechain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:685689",
      cashaddr_prefix: null,
      chain_id_hex: "0xa7679",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gensyn-mainnet.explorer.alchemy.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gensyn.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Gensyn",
      native_asset: "eip155:685689/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://gensyn-mainnet.g.alchemy.com/public"
      ],
      short_name: "gensyn-mainnet",
      slip44: 60,
      slug: "gensyn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "giwa-sepolia-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:91342",
      cashaddr_prefix: null,
      chain_id_hex: "0x164ce",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-explorer.giwa.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://giwa.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "GIWA Sepolia Testnet",
      native_asset: null,
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia-rpc.giwa.io"
      ],
      short_name: "giwasepolia",
      slip44: null,
      slug: "giwa_sepolia_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1300",
      cashaddr_prefix: null,
      chain_id_hex: "0x514",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.glue.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://glue.net",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Glue",
      native_asset: null,
      native_name: "Glue",
      native_token: "GLUE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "glue"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.glue.net"
      ],
      short_name: "glue",
      slip44: null,
      slug: "glue",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdai",
        "gnosis-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:100",
      cashaddr_prefix: null,
      chain_id_hex: "0x64",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosisscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.gnosischain.com",
      is_testnet: false,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/gnosis.svg",
      mainnet: null,
      metadata: {},
      name: "Gnosis",
      native_asset: "eip155:100/slip44:700",
      native_name: "xDAI",
      native_token: "xDAI",
      native_token_address: null,
      parent: null,
      providers: {
        ankr: "gnosis",
        arkham: "gnosis",
        blockchair: "gnosis",
        blockscan: "100",
        ccip: "gnosis",
        coingecko_native: "xdai",
        cowswap: "xdai",
        defillama: "xdai",
        dexscreener: "gnosis",
        moralis: "gnosis",
        moralis_wallet: "gnosis",
        thegraph: "gnosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gnosischain.com"
      ],
      short_name: "gno",
      slip44: 700,
      slug: "gnosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:10200",
      cashaddr_prefix: null,
      chain_id_hex: "0x27d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosis-chiado.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.gnosischain.com",
      is_testnet: true,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/gnosis-chiado.svg",
      mainnet: "eip155:100",
      metadata: {},
      name: "Gnosis Chiado",
      native_asset: "eip155:10200/slip44:700",
      native_name: "Chiado xDAI",
      native_token: "xDAI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "gnosis",
        thegraph: "gnosis-chiado"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chi",
      slip44: 700,
      slug: "gnosis_chiado",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "goat-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2345",
      cashaddr_prefix: null,
      chain_id_hex: "0x929",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.goat.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.goat.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "GOAT Network",
      native_asset: null,
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "bitcoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.goat.network"
      ],
      short_name: "goat",
      slip44: null,
      slug: "goat_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:5",
      cashaddr_prefix: null,
      chain_id_hex: "0x5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://goerli.net/#about",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Goerli",
      native_asset: "eip155:5/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "gor",
      slip44: 60,
      slug: "goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:127001",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mainnet-explorer.gravity.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gravity.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/gravity.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Gravity",
      native_asset: null,
      native_name: "Gravity",
      native_token: "G",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "gravity"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.gravity.xyz"
      ],
      short_name: "grav",
      slip44: null,
      slug: "gravity",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "gravity-alpha"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1625",
      cashaddr_prefix: null,
      chain_id_hex: "0x659",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.gravity.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gravity.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Gravity Alpha",
      native_asset: null,
      native_name: "Gravity",
      native_token: "G",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "g-token",
        thegraph: "gravity-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gravity.xyz",
        "https://rpc.ankr.com/gravity"
      ],
      short_name: "gravity",
      slip44: null,
      slug: "gravity_alpha",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:325",
      cashaddr_prefix: null,
      chain_id_hex: "0x145",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://grvt.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "GRVT",
      native_asset: null,
      native_name: "GRVT Base Token",
      native_token: "GBT",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "grvt",
      slip44: null,
      slug: "grvt",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:43419",
      cashaddr_prefix: null,
      chain_id_hex: "0xa99b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gunzscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gunbygunz.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "GUNZ",
      native_asset: null,
      native_name: "GUN",
      native_token: "GUN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gunzchain.io/ext/bc/2M47TxWHGnhNtq6pM5zPXdATBtuqubxn5EPFgFmEawCQr9WFML/rpc"
      ],
      short_name: "gunz-mainnet",
      slip44: null,
      slug: "gunz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "guru-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:260",
      cashaddr_prefix: null,
      chain_id_hex: "0x104",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.gurunetwork.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gurunetwork.ai",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Guru Network",
      native_asset: null,
      native_name: "Guru",
      native_token: "GURU",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "guru-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-main.gurunetwork.ai"
      ],
      short_name: "guru",
      slip44: null,
      slug: "guru_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5112",
      cashaddr_prefix: null,
      chain_id_hex: "0x13f8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.ham.fun",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ham.fun",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ham",
      native_asset: "eip155:5112/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ham.fun"
      ],
      short_name: "ham",
      slip44: 60,
      slug: "ham",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "haqq-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11235",
      cashaddr_prefix: null,
      chain_id_hex: "0x2be3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.haqq.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://islamiccoin.net",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Haqq Network",
      native_asset: null,
      native_name: "Islamic Coin",
      native_token: "ISLM",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.eth.haqq.network",
        "https://haqq-evm-rpc.publicnode.com",
        "https://haqq.drpc.org",
        "wss://haqq-evm-rpc.publicnode.com"
      ],
      short_name: "ISLM",
      slip44: null,
      slug: "haqq_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666600000",
      cashaddr_prefix: null,
      chain_id_hex: "0x63564c40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.harmony.one",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Harmony",
      native_asset: "eip155:1666600000/slip44:1023",
      native_name: "ONE",
      native_token: "ONE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "harmony",
        defillama: "harmony",
        dexscreener: "harmony",
        thegraph: "harmony"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "hmy-s0",
      slip44: 1023,
      slug: "harmony",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666700000",
      cashaddr_prefix: null,
      chain_id_hex: "0x6357d2e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.harmony.one",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:1666600000",
      metadata: {},
      name: "Harmony Testnet",
      native_asset: "eip155:1666700000/slip44:1023",
      native_name: "ONE",
      native_token: "ONE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "hmy-b-s0",
      slip44: 1023,
      slug: "harmony_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:177",
      cashaddr_prefix: null,
      chain_id_hex: "0xb1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hsk.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hskchain.net",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/hashkey.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "HSKChain",
      native_asset: null,
      native_name: "HashKey EcoPoints",
      native_token: "HSK",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "hashkey",
        coingecko_native: "hashkey-ecopoints",
        thegraph: "hashkeychain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.hsk.xyz"
      ],
      short_name: "hsk",
      slip44: null,
      slug: "hashkey",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hashkey-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:133",
      cashaddr_prefix: null,
      chain_id_hex: "0x85",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-explorer.hskchain.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hskchain.net",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/hashkey.svg",
      mainnet: "eip155:177",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "HSKChain Testnet",
      native_asset: null,
      native_name: "HashKey EcoPoints",
      native_token: "HSK",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "hashkey",
        thegraph: "hashkeychain-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.hsk.xyz"
      ],
      short_name: "HSKT",
      slip44: null,
      slug: "hashkey_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:295",
      cashaddr_prefix: null,
      chain_id_hex: "0x127",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://hedera.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/hedera.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hedera",
      native_asset: "eip155:295/slip44:3030",
      native_name: "hbar",
      native_token: "HBAR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "hedera",
        coingecko_native: "hedera-hashgraph"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.hashio.io/api",
        "https://hedera.linkpool.pro"
      ],
      short_name: "hedera-mainnet",
      slip44: 3030,
      slug: "hedera",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hedera-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:296",
      cashaddr_prefix: null,
      chain_id_hex: "0x128",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hashscan.io/testnet",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hedera.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/hedera.svg",
      mainnet: "eip155:295",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hedera Testnet",
      native_asset: "eip155:296/slip44:3030",
      native_name: "hbar",
      native_token: "HBAR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "hedera"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.hashio.io/api"
      ],
      short_name: "hedera-testnet",
      slip44: 3030,
      slug: "hedera_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:43111",
      cashaddr_prefix: null,
      chain_id_hex: "0xa867",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.hemi.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hemi.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/hemi.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hemi",
      native_asset: "eip155:43111/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "hemi",
        coingecko_native: "ethereum",
        thegraph: "hemi"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.hemi.network/rpc"
      ],
      short_name: "hemi",
      slip44: 60,
      slug: "hemi",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hemi-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:743111",
      cashaddr_prefix: null,
      chain_id_hex: "0xb56c7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.explorer.hemi.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hemi.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/hemi.svg",
      mainnet: "eip155:43111",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hemi Sepolia",
      native_asset: "eip155:743111/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "hemi",
        thegraph: "hemi-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.rpc.hemi.network/rpc"
      ],
      short_name: "hemi-sep",
      slip44: 60,
      slug: "hemi_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:68414",
      cashaddr_prefix: null,
      chain_id_hex: "0x10b3e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://msu-explorer.xangle.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://nexpace.io",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/henesys.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Henesys",
      native_asset: null,
      native_name: "NEXPACE",
      native_token: "NXPC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "henesys"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://henesys-rpc.msu.io"
      ],
      short_name: "nxpc",
      slip44: null,
      slug: "henesys",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "henez-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:91111",
      cashaddr_prefix: null,
      chain_id_hex: "0x163e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://henez.calderaexplorer.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.henez.fi",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Henez Chain",
      native_asset: "eip155:91111/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://henez.calderachain.xyz/http"
      ],
      short_name: "henez",
      slip44: 60,
      slug: "henez_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:17000",
      cashaddr_prefix: null,
      chain_id_hex: "0x4268",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://holesky.ethpandaops.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Holesky",
      native_asset: "eip155:17000/slip44:60",
      native_name: "Testnet ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "holesky",
      slip44: 60,
      slug: "holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "home-verse"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:19011",
      cashaddr_prefix: null,
      chain_id_hex: "0x4a43",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.oasys.homeverse.games",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.homeverse.games",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "HOME Verse",
      native_asset: null,
      native_name: "OAS",
      native_token: "OAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "oasys"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.oasys.homeverse.games"
      ],
      short_name: "HMV",
      slip44: null,
      slug: "home_verse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:26514",
      cashaddr_prefix: null,
      chain_id_hex: "0x6792",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://horizen.calderaexplorer.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://horizen.hub.caldera.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Horizen",
      native_asset: "eip155:26514/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://horizen.calderachain.xyz/http"
      ],
      short_name: "horizen",
      slip44: 60,
      slug: "horizen",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "horizen-eon"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7332",
      cashaddr_prefix: null,
      chain_id_hex: "0x1ca4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://horizen.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Horizen EON",
      native_asset: "eip155:7332/slip44:121",
      native_name: "Zencash",
      native_token: "ZEN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "EON",
      slip44: 121,
      slug: "horizen_eon",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hubble-exchange"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1992",
      cashaddr_prefix: null,
      chain_id_hex: "0x7c8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.hubble.exchange",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hubble Exchange",
      native_asset: null,
      native_name: "USD Coin",
      native_token: "USDC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "usd-coin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.hubble.exchange",
        "wss://ws-rpc.hubble.exchange"
      ],
      short_name: "hubblenet",
      slip44: null,
      slug: "hubble_exchange",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "humanity-protocol"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6985385",
      cashaddr_prefix: null,
      chain_id_hex: "0x6a96a9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://humanity.org",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Humanity Protocol",
      native_asset: null,
      native_name: "H",
      native_token: "H",
      native_token_address: null,
      parent: "eip155:42161",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://humanity-mainnet.g.alchemy.com/public"
      ],
      short_name: "hpold",
      slip44: null,
      slug: "humanity_protocol",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "humanity-protocol-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1942999413",
      cashaddr_prefix: null,
      chain_id_hex: "0x73cfd175",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.humanity.org",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:6985385",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Humanity Protocol Testnet",
      native_asset: null,
      native_name: "Humanity Protocol",
      native_token: "tHP",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.humanity.org"
      ],
      short_name: null,
      slip44: null,
      slug: "humanity_protocol_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:222222",
      cashaddr_prefix: null,
      chain_id_hex: "0x3640e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.evm.hydration.cloud",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hydration.net",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Hydration",
      native_asset: null,
      native_name: "Wrapped ETH",
      native_token: "WETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.hydradx.cloud",
        "https://hydration-rpc.n.dwellir.com",
        "https://rpc.helikon.io/hydradx",
        "https://hydration.dotters.network"
      ],
      short_name: "hdx",
      slip44: null,
      slug: "hydration",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-mainnet",
        "hyperliquid",
        "hyperliquid-core",
        "hyperliquid-l1"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:mainnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      info_url: "https://hyperliquid.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "HyperCore",
      native_asset: "hyperliq:mainnet/slip44:2457",
      native_name: "Hyperliquid",
      native_token: "HYPE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "hyperliquid",
        hyperliquid: "mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 2457,
      slug: "hypercore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-testnet",
        "hyperliquid-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:testnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid-testnet.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "hyperliq:mainnet",
      metadata: {},
      name: "HyperCore Testnet",
      native_asset: "hyperliq:testnet/slip44:2457",
      native_name: "Hyperliquid",
      native_token: "HYPE",
      native_token_address: null,
      parent: null,
      providers: {
        hyperliquid: "testnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 2457,
      slug: "hypercore_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyper-evm",
        "hyperliquid-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:999",
      cashaddr_prefix: null,
      chain_id_hex: "0x3e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hyperevmscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hyperliquid.xyz",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/hyperevm.svg",
      mainnet: null,
      metadata: {},
      name: "HyperEVM",
      native_asset: "eip155:999/slip44:2457",
      native_name: "HYPE",
      native_token: "HYPE",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "hyperevm",
        coingecko_native: "hyperliquid",
        evm_rpc: "ingest",
        sourcify: "999",
        thegraph: "hyper-evm"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid.xyz/evm"
      ],
      short_name: null,
      slip44: 2457,
      slug: "hyperevm",
      status: "active",
      wrapped_native_address: "0x5555555555555555555555555555555555555555"
    },
    {
      aliases: [
        "hyperevm-testnet",
        "hyperliquid-evm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:998",
      cashaddr_prefix: null,
      chain_id_hex: "0x3e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://hyperfoundation.org",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/hyperevm-testnet.svg",
      mainnet: "eip155:999",
      metadata: {},
      name: "HyperEVM Testnet",
      native_asset: "eip155:998/slip44:2457",
      native_name: "HYPE",
      native_token: "HYPE",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "hyperliquid",
        evm_rpc: "ingest",
        sourcify: "998"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid-testnet.xyz/evm"
      ],
      short_name: "hype-evm-testnet",
      slip44: 2457,
      slug: "hyperevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "igra-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:38833",
      cashaddr_prefix: null,
      chain_id_hex: "0x97b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.igralabs.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://igralabs.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Igra Network",
      native_asset: null,
      native_name: "iKAS",
      native_token: "iKAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "wrapped-ikas-zealous-swap"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.igralabs.com:8545"
      ],
      short_name: "igra",
      slip44: null,
      slug: "igra_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "immutable-zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:13371",
      cashaddr_prefix: null,
      chain_id_hex: "0x343b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.immutable.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.immutable.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Immutable zkEVM",
      native_asset: null,
      native_name: "IMX",
      native_token: "IMX",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "immutable-x"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.immutable.com",
        "https://immutable-zkevm.drpc.org",
        "wss://immutable-zkevm.drpc.org"
      ],
      short_name: "imx",
      slip44: null,
      slug: "immutable_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "immutable-zkevm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:13473",
      cashaddr_prefix: null,
      chain_id_hex: "0x34a1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.immutable.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.immutable.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:13371",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Immutable zkEVM Testnet",
      native_asset: null,
      native_name: "Test IMX",
      native_token: "tIMX",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.immutable.com",
        "https://immutable-zkevm-testnet.drpc.org",
        "wss://immutable-zkevm-testnet.drpc.org"
      ],
      short_name: "imx-testnet",
      slip44: null,
      slug: "immutable_zkevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:24101",
      cashaddr_prefix: null,
      chain_id_hex: "0x5e25",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Incentiv",
      native_asset: null,
      native_name: "CENT",
      native_token: "CENT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "incentiv"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-archive.incentiv.io"
      ],
      short_name: null,
      slip44: null,
      slug: "incentiv",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "incentiv-testnet-v2"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:28802",
      cashaddr_prefix: null,
      chain_id_hex: "0x7082",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-testnet.incentiv.io",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Incentiv Testnet v2",
      native_asset: null,
      native_name: "TCENT",
      native_token: "TCENT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc2.testnet.incentiv.io"
      ],
      short_name: null,
      slip44: null,
      slug: "incentiv_testnet_v2",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "inclusive-layer-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:71461164656",
      cashaddr_prefix: null,
      chain_id_hex: "0x10a36ad670",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Inclusive Layer Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "inclusive_layer_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "infinity-vm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1032009",
      cashaddr_prefix: null,
      chain_id_hex: "0xfbf49",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Infinity VM",
      native_asset: null,
      native_name: "INF",
      native_token: "INF",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "infinityvm"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-endpoint.infinityvm.xyz"
      ],
      short_name: null,
      slip44: null,
      slug: "infinity_vm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "infinity-vm-monza-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:96025",
      cashaddr_prefix: null,
      chain_id_hex: "0x17719",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Infinity VM Monza Testnet",
      native_asset: null,
      native_name: "INF",
      native_token: "INF",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.monza.infinityvm.xyz"
      ],
      short_name: null,
      slip44: null,
      slug: "infinity_vm_monza_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1776",
      cashaddr_prefix: null,
      chain_id_hex: "0x6f0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.injective.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://injective.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Injective",
      native_asset: null,
      native_name: "Injective",
      native_token: "INJ",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "injective-protocol",
        thegraph: "injective-evm"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sentry.evm-rpc.injective.network",
        "https://injectiveevm-rpc.polkachu.com",
        "wss://sentry.evm-ws.injective.network",
        "wss://injectiveevm-ws.polkachu.com"
      ],
      short_name: "injective",
      slip44: null,
      slug: "injective",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "injective-cosmos"
      ],
      base58_versions: [],
      bech32_hrp: "inj",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:injective-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/injective",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://injective.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "injective",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Injective",
      native_asset: "cosmos:injective-1/slip44:60",
      native_name: "Injective",
      native_token: "INJ",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "injective-protocol",
        thegraph: "injective-mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "injective_cosmos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "injective-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2525",
      cashaddr_prefix: null,
      chain_id_hex: "0x9dd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://inevm.calderaexplorer.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Injective EVM",
      native_asset: null,
      native_name: "Injective",
      native_token: "INJ",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "injective-protocol"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://inevm.calderachain.xyz/http",
        "https://mainnet.rpc.inevm.com/http"
      ],
      short_name: null,
      slip44: null,
      slug: "injective_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "injective-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1439",
      cashaddr_prefix: null,
      chain_id_hex: "0x59f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.blockscout.injective.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://injective.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Injective Testnet",
      native_asset: null,
      native_name: "Injective",
      native_token: "INJ",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "injective-evm-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://k8s.testnet.json-rpc.injective.network",
        "https://injectiveevm-testnet-rpc.polkachu.com",
        "wss://k8s.testnet.ws.injective.network",
        "wss://injectiveevm-testnet-rpc.polkachu.com"
      ],
      short_name: "injective-testnet",
      slip44: null,
      slug: "injective_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57073",
      cashaddr_prefix: null,
      chain_id_hex: "0xdef1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://inkonchain.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/ink.svg",
      mainnet: null,
      metadata: {},
      name: "Ink",
      native_asset: "eip155:57073/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        blockscout_v2: "https://explorer.inkonchain.com/api/v2",
        ccip: "ink",
        coingecko_native: "ethereum",
        sourcify: "57073",
        thegraph: "ink"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ink",
      slip44: 60,
      slug: "ink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ink-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:763373",
      cashaddr_prefix: null,
      chain_id_hex: "0xba5ed",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-sepolia.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://inkonchain.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/ink.svg",
      mainnet: "eip155:57073",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ink Sepolia",
      native_asset: "eip155:763373/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ink",
        thegraph: "ink-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-gel-sepolia.inkonchain.com",
        "wss://ws-gel-sepolia.inkonchain.com"
      ],
      short_name: "inksepolia",
      slip44: 60,
      slug: "ink_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "iota-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8822",
      cashaddr_prefix: null,
      chain_id_hex: "0x2276",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.evm.iota.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.iota.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "IOTA EVM",
      native_asset: null,
      native_name: "IOTA",
      native_token: "IOTA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://json-rpc.evm.iotaledger.net",
        "wss://ws.json-rpc.evm.iotaledger.net"
      ],
      short_name: "iotaevm",
      slip44: null,
      slug: "iota_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "irys-mainnet-beta"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3282",
      cashaddr_prefix: null,
      chain_id_hex: "0xcd2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm-explorer.irys.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://irys.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Irys Mainnet Beta",
      native_asset: null,
      native_name: "Irys",
      native_token: "IRYS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-beta-rpc.irys.xyz/v1/execution-rpc",
        "https://mainnet-beta-rpc-2.irys.xyz/v1/execution-rpc"
      ],
      short_name: "irys-mainnet-beta",
      slip44: null,
      slug: "irys_mainnet_beta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:678",
      cashaddr_prefix: null,
      chain_id_hex: "0x2a6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://janction.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Janction",
      native_asset: null,
      native_name: "Janction",
      native_token: "JCT",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.janction.io",
        "wss://rpc.janction.io"
      ],
      short_name: "janction",
      slip44: null,
      slug: "janction",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "janction-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:679",
      cashaddr_prefix: null,
      chain_id_hex: "0x2a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://janction.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/janction-testnet.svg",
      mainnet: "eip155:678",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Janction Testnet",
      native_asset: null,
      native_name: "Janction",
      native_token: "JCT",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "janction"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc_testnet.janction.io",
        "wss://rpc_testnet.janction.io"
      ],
      short_name: "janction_testnet",
      slip44: null,
      slug: "janction_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5734951",
      cashaddr_prefix: null,
      chain_id_hex: "0x578227",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://jovay.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/jovay.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Jovay",
      native_asset: "eip155:5734951/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "jovay",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.jovay.io"
      ],
      short_name: "jovay",
      slip44: 60,
      slug: "jovay",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "jovay-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2019775",
      cashaddr_prefix: null,
      chain_id_hex: "0x1ed1bf",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://jovay.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/jovay.svg",
      mainnet: "eip155:5734951",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Jovay Sepolia Testnet",
      native_asset: "eip155:2019775/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "jovay"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.zan.top/public/jovay-testnet"
      ],
      short_name: "jovay-sepolia",
      slip44: 60,
      slug: "jovay_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3888",
      cashaddr_prefix: null,
      chain_id_hex: "0xf30",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://kalyscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://kalychain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "KalyChain",
      native_asset: null,
      native_name: "KalyCoin",
      native_token: "KLC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.kalychain.io/rpc"
      ],
      short_name: "kalymainnet",
      slip44: null,
      slug: "kalychain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "karura-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:686",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.karura.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://acala.network/karura",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Karura Network",
      native_asset: "eip155:686/slip44:686",
      native_name: "Karura Token",
      native_token: "KAR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://eth-rpc-karura.aca-api.network",
        "wss://eth-rpc-karura.aca-api.network"
      ],
      short_name: "kar",
      slip44: 686,
      slug: "karura_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747474",
      cashaddr_prefix: null,
      chain_id_hex: "0xb67d2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://katanascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://katana.network",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/katana.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Katana",
      native_asset: "eip155:747474/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "katana",
        coingecko_native: "ethereum",
        thegraph: "katana"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.katana.network",
        "https://katana.gateway.tenderly.co",
        "https://rpc.katanarpc.com"
      ],
      short_name: "katana",
      slip44: 60,
      slug: "katana",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2222",
      cashaddr_prefix: null,
      chain_id_hex: "0x8ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.kava.io",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Kava",
      native_asset: "eip155:2222/slip44:459",
      native_name: "Kava",
      native_token: "KAVA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "kava",
        defillama: "kava",
        thegraph: "kava"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kava",
      slip44: 459,
      slug: "kava",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2221",
      cashaddr_prefix: null,
      chain_id_hex: "0x8ad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.kava.io",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:2222",
      metadata: {},
      name: "Kava Testnet",
      native_asset: "eip155:2221/slip44:459",
      native_name: "TKava",
      native_token: "TKAVA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tkava",
      slip44: 459,
      slug: "kava_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1783",
      cashaddr_prefix: null,
      chain_id_hex: "0x6f7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://kiichain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "KiiChain",
      native_asset: null,
      native_name: "Kii",
      native_token: "KII",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "kiichain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://json-rpc.kiivalidator.com"
      ],
      short_name: "kiichain",
      slip44: null,
      slug: "kiichain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7887",
      cashaddr_prefix: null,
      chain_id_hex: "0x1ecf",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.kinto.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://kinto.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Kinto",
      native_asset: "eip155:7887/slip44:60",
      native_name: "Ethereum",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.kinto.xyz/http",
        "https://kinto-mainnet.calderachain.xyz/http"
      ],
      short_name: "kintoMainnet",
      slip44: 60,
      slug: "kinto",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2366",
      cashaddr_prefix: null,
      chain_id_hex: "0x93e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://kitescan.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://gokite.ai",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "KiteAI",
      native_asset: null,
      native_name: "Kite",
      native_token: "KITE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "kite-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gokite.ai"
      ],
      short_name: "KiteAI",
      slip44: null,
      slug: "kiteai",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:8217",
      cashaddr_prefix: null,
      chain_id_hex: "0x2019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://kaia.io",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/klaytn.svg",
      mainnet: null,
      metadata: {},
      name: "Klaytn",
      native_asset: "eip155:8217/slip44:8217",
      native_name: "KAIA",
      native_token: "KAIA",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "kaia",
        coingecko_native: "kaia",
        defillama: "klaytn",
        thegraph: "kaia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kaia-mainnet",
      slip44: 8217,
      slug: "klaytn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:1001",
      cashaddr_prefix: null,
      chain_id_hex: "0x3e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://kaia.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/klaytn-baobab.svg",
      mainnet: "eip155:8217",
      metadata: {},
      name: "Klaytn Baobab",
      native_asset: "eip155:1001/slip44:8217",
      native_name: "KAIA",
      native_token: "KAIA",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "kaia",
        thegraph: "kaia-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kaia-kairos",
      slip44: 8217,
      slug: "klaytn_baobab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:255",
      cashaddr_prefix: null,
      chain_id_hex: "0xff",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.kroma.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://kroma.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Kroma",
      native_asset: "eip155:255/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.kroma.network",
        "https://rpc-kroma.rockx.com"
      ],
      short_name: "kroma",
      slip44: 60,
      slug: "kroma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "kroma-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2358",
      cashaddr_prefix: null,
      chain_id_hex: "0x936",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.sepolia.kroma.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://kroma.network",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:255",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Kroma Sepolia",
      native_asset: "eip155:2358/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://api.sepolia.kroma.network"
      ],
      short_name: "kroma-sepolia",
      slip44: 60,
      slug: "kroma_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1983",
      cashaddr_prefix: null,
      chain_id_hex: "0x7bf",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.krown.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://krown.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Krown",
      native_asset: null,
      native_name: "KROWN",
      native_token: "KROWN",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "krown-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.krown.network",
        "https://mainnet1.krown.network"
      ],
      short_name: "krown",
      slip44: null,
      slug: "krown",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "kujira",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:kaiyo-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://kujira.app",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "kujira",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Kujira",
      native_asset: "cosmos:kaiyo-1/slip44:118",
      native_name: "Kujira",
      native_token: "KUJI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "kujira",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "kyve",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:kyve-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        token: null,
        tx: "txs"
      },
      explorer_url: "https://www.mintscan.io/kyve",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.kyve.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "kyve",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "KYVE",
      native_asset: "cosmos:kyve-1/slip44:118",
      native_name: "KYVE",
      native_token: "KYVE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "kyve-network"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "kyve",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "kyve-kaon-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "kyve",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:kaon-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        token: null,
        tx: "txs"
      },
      explorer_url: "https://mintscan.io/kyve-testnet",
      family: "cosmos",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:kyve-1",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "kyvetestnet",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "KYVE Kaon Testnet",
      native_asset: "cosmos:kaon-1/slip44:118",
      native_name: "KYVE",
      native_token: "KYVE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "kyve_kaon_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "l3x-protocol"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:12324",
      cashaddr_prefix: null,
      chain_id_hex: "0x3024",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.l3x.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://l3x.com",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "L3X Protocol",
      native_asset: "eip155:12324/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:42161",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-mainnet.l3x.com"
      ],
      short_name: "l3x",
      slip44: 60,
      slug: "l3x_protocol",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "l3x-protocol-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:12325",
      cashaddr_prefix: null,
      chain_id_hex: "0x3025",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-testnet.l3x.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://l3x.com",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:12324",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "L3X Protocol Testnet",
      native_asset: "eip155:12325/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:421614",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-testnet.l3x.com"
      ],
      short_name: "l3x-testnet",
      slip44: 60,
      slug: "l3x_protocol_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "layer-edge"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4207",
      cashaddr_prefix: null,
      chain_id_hex: "0x106f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://edgenscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://layeredge.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Layer Edge",
      native_asset: null,
      native_name: "Layer Edge",
      native_token: "EDGEN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://layeredge-mainnet-evm.itrocket.net",
        "https://layeredge.rpc.subquery.network/public",
        "https://rpc.layeredge.io",
        "https://rpc.layeredge.foundation"
      ],
      short_name: "LayerEdge",
      slip44: null,
      slug: "layer_edge",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:52924",
      cashaddr_prefix: null,
      chain_id_hex: "0xcebc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mainnet.lazai.network",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "LazAI",
      native_asset: null,
      native_name: "METIS",
      native_token: "METIS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "metis-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.lazai.network"
      ],
      short_name: null,
      slip44: null,
      slug: "lazai",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:232",
      cashaddr_prefix: null,
      chain_id_hex: "0xe8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.lens.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lens.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lens.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lens",
      native_asset: null,
      native_name: "GHO",
      native_token: "GHO",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "lens",
        coingecko_native: "gho",
        thegraph: "lens"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.lens.xyz"
      ],
      short_name: "lens",
      slip44: null,
      slug: "lens",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "lens-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:37111",
      cashaddr_prefix: null,
      chain_id_hex: "0x90f7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.lens.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lens.svg",
      mainnet: "eip155:232",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lens Testnet",
      native_asset: null,
      native_name: "GRASS",
      native_token: "GRASS",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "lens",
        thegraph: "lens-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.lens.dev"
      ],
      short_name: "lens-sepolia",
      slip44: null,
      slug: "lens_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "lightlink-phoenix"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1890",
      cashaddr_prefix: null,
      chain_id_hex: "0x762",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://phoenix.lightlink.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lightlink.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lightlink Phoenix",
      native_asset: "eip155:1890/slip44:60",
      native_name: "Ethereum",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://replicator.phoenix.lightlink.io/rpc/v1"
      ],
      short_name: "lightlink_phoenix",
      slip44: 60,
      slug: "lightlink_phoenix",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "linea-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59144",
      cashaddr_prefix: null,
      chain_id_hex: "0xe708",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lineascan.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea.svg",
      mainnet: null,
      metadata: {},
      name: "Linea",
      native_asset: "eip155:59144/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        arkham: "linea",
        ccip: "linea",
        coingecko_native: "ethereum",
        coinmarketcap: "linea",
        defillama: "linea",
        dexscreener: "linea",
        etherscan_scan: "lineascan.build",
        moralis: "linea",
        moralis_wallet: "linea",
        sourcify: "59144",
        thegraph: "linea"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.linea.build"
      ],
      short_name: "linea",
      slip44: 60,
      slug: "linea",
      status: "active",
      wrapped_native_address: "0xe5d7c2a44ffddf6b295a15c148167daaaf5cf34f"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59141",
      cashaddr_prefix: null,
      chain_id_hex: "0xe705",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.lineascan.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea-sepolia.svg",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Sepolia",
      native_asset: "eip155:59141/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "linea",
        thegraph: "linea-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "linea-sepolia",
      slip44: 60,
      slug: "linea_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59140",
      cashaddr_prefix: null,
      chain_id_hex: "0xe704",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea.svg",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Goerli",
      native_asset: "eip155:59140/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "linea-goerli",
      slip44: 60,
      slug: "linea_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1135",
      cashaddr_prefix: null,
      chain_id_hex: "0x46f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.lisk.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lisk.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lisk.svg",
      mainnet: null,
      metadata: {},
      name: "Lisk",
      native_asset: "eip155:1135/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "lisk",
        coingecko_native: "lisk",
        moralis: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "lisk",
      slip44: 60,
      slug: "lisk",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4202",
      cashaddr_prefix: null,
      chain_id_hex: "0x106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://lisk.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lisk.svg",
      mainnet: "eip155:1135",
      metadata: {},
      name: "Lisk Sepolia",
      native_asset: "eip155:4202/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "lisksep",
      slip44: 60,
      slug: "lisk_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "lit-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:175200",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ac60",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lit-chain-explorer.litprotocol.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://litprotocol.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lit Chain",
      native_asset: null,
      native_name: "Lit Protocol",
      native_token: "LITKEY",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "lit-protocol"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://lit-chain-rpc.litprotocol.com"
      ],
      short_name: "lit",
      slip44: null,
      slug: "lit_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ltc"
      ],
      base58_versions: [
        48,
        50,
        5
      ],
      bech32_hrp: "ltc",
      block_time_seconds: 150,
      brand_color: "#345D9D",
      caip2: "bip122:12a765e31ffd4059bada1e25190f6e98",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/litecoin",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://litecoin.org",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/bip122/caip2",
          "https://github.com/litecoin-project/litecoin"
        ]
      },
      name: "Litecoin",
      native_asset: "bip122:12a765e31ffd4059bada1e25190f6e98/slip44:2",
      native_name: "Litecoin",
      native_token: "LTC",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "litecoin",
        coingecko_native: "litecoin",
        thegraph: "litecoin"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 2,
      slug: "litecoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "load-alphanet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9496",
      cashaddr_prefix: null,
      chain_id_hex: "0x2518",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.load.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://load.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Load Alphanet",
      native_asset: null,
      native_name: "Testnet Load Token",
      native_token: "tLOAD",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://alphanet.load.network"
      ],
      short_name: "tload",
      slip44: null,
      slug: "load_alphanet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5151706",
      cashaddr_prefix: null,
      chain_id_hex: "0x4e9bda",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Loot",
      native_asset: null,
      native_name: null,
      native_token: "AGLD",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "adventure-gold"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.lootchain.com/http"
      ],
      short_name: null,
      slip44: null,
      slug: "loot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:42",
      cashaddr_prefix: null,
      chain_id_hex: "0x2a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.lukso.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lukso.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "LUKSO",
      native_asset: null,
      native_name: "LUKSO",
      native_token: "LYX",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "lukso-token-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://42.rpc.thirdweb.com",
        "https://rpc.lukso.sigmacore.io",
        "https://rpc.mainnet.lukso.network",
        "wss://ws-rpc.mainnet.lukso.network"
      ],
      short_name: "lukso",
      slip44: null,
      slug: "lukso",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "lukso-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4201",
      cashaddr_prefix: null,
      chain_id_hex: "0x1069",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.execution.testnet.lukso.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lukso.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:42",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "LUKSO Testnet",
      native_asset: null,
      native_name: "TestLYX",
      native_token: "LYXt",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.lukso.network",
        "wss://ws-rpc.testnet.lukso.network"
      ],
      short_name: "lukso-testnet",
      slip44: null,
      slug: "lukso_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:994873017",
      cashaddr_prefix: null,
      chain_id_hex: "0x3b4c8eb9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.lumia.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lumia.org",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lumia",
      native_asset: null,
      native_name: "Lumia",
      native_token: "LUMIA",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "orion-protocol",
        thegraph: "lumia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.lumia.org"
      ],
      short_name: "lumia-mainnet",
      slip44: null,
      slug: "lumia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "lyra-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:957",
      cashaddr_prefix: null,
      chain_id_hex: "0x3bd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.lyra.finance",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lyra.finance",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Lyra Chain",
      native_asset: "eip155:957/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.lyra.finance"
      ],
      short_name: "lyra",
      slip44: 60,
      slug: "lyra_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "manta-pacific"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:169",
      cashaddr_prefix: null,
      chain_id_hex: "0xa9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://pacific-explorer.manta.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://pacific-info.manta.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Manta Pacific",
      native_asset: "eip155:169/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum",
        thegraph: "manta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://pacific-rpc.manta.network/http",
        "https://manta-pacific.drpc.org",
        "wss://manta-pacific.drpc.org"
      ],
      short_name: "manta",
      slip44: 60,
      slug: "manta_pacific",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "manta-pacific-sepolia-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3441006",
      cashaddr_prefix: null,
      chain_id_hex: "0x34816e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://pacific-explorer.sepolia-testnet.manta.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://manta-testnet.caldera.dev",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:169",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Manta Pacific Sepolia Testnet",
      native_asset: "eip155:3441006/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://pacific-rpc.sepolia-testnet.manta.network/http"
      ],
      short_name: "mantaSepoliaTestnet",
      slip44: 60,
      slug: "manta_pacific_sepolia_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5000",
      cashaddr_prefix: null,
      chain_id_hex: "0x1388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantle.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mantle.svg",
      mainnet: null,
      metadata: {},
      name: "Mantle",
      native_asset: null,
      native_name: "Mantle",
      native_token: "MNT",
      native_token_address: "0xdeaddeaddeaddeaddeaddeaddeaddeaddead0000",
      parent: "eip155:1",
      providers: {
        arkham: "mantle",
        ccip: "mantle",
        coingecko_native: "mantle",
        defillama: "mantle",
        dexscreener: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mantle.xyz"
      ],
      short_name: "mantle",
      slip44: null,
      slug: "mantle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-goerli"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5001",
      cashaddr_prefix: null,
      chain_id_hex: "0x1389",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantle.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mantle.svg",
      mainnet: "eip155:5000",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mantle Testnet",
      native_asset: null,
      native_name: "Testnet Mantle",
      native_token: "MNT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.mantle.xyz"
      ],
      short_name: "mantle-testnet",
      slip44: null,
      slug: "mantle_goerli",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-testnet",
        "mantle_testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5003",
      cashaddr_prefix: null,
      chain_id_hex: "0x138b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.mantlescan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantle.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mantle-sepolia.svg",
      mainnet: "eip155:5000",
      metadata: {},
      name: "Mantle Sepolia",
      native_asset: null,
      native_name: "Sepolia Mantle",
      native_token: "MNT",
      native_token_address: "0xdeaddeaddeaddeaddeaddeaddeaddeaddead0000",
      parent: "eip155:11155111",
      providers: {
        ccip: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mnt-sep",
      slip44: null,
      slug: "mantle_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantra-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5888",
      cashaddr_prefix: null,
      chain_id_hex: "0x1700",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.mantrascan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantrachain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "MANTRA Chain",
      native_asset: null,
      native_name: "MANTRA",
      native_token: "MANTRA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "mantra"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm.mantrachain.io",
        "wss://evm.mantrachain.io/ws"
      ],
      short_name: "mantra",
      slip44: null,
      slug: "mantra_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:13396",
      cashaddr_prefix: null,
      chain_id_hex: "0x3454",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets.avax.network/masa",
      family: "evm",
      finality_blocks: null,
      info_url: "https://masa.finance",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Masa",
      native_asset: null,
      native_name: "Masa Token",
      native_token: "MASA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "avalanche-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/masanetwork/mainnet/rpc"
      ],
      short_name: "masa",
      slip44: null,
      slug: "masa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:698",
      cashaddr_prefix: null,
      chain_id_hex: "0x2ba",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://matchscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.matchain.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Matchain",
      native_asset: "eip155:698/slip44:714",
      native_name: "BNB",
      native_token: "BNB",
      native_token_address: null,
      parent: "eip155:56",
      providers: {
        coingecko_native: "binancecoin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.matchain.io"
      ],
      short_name: "Matchain",
      slip44: 714,
      slug: "matchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4326",
      cashaddr_prefix: null,
      chain_id_hex: "0x10e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mega.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://megaeth.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/megaeth.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "MegaETH",
      native_asset: "eip155:4326/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "megaeth",
        coingecko_native: "ethereum",
        thegraph: "megaeth"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.megaeth.com/rpc",
        "wss://mainnet.megaeth.com/ws"
      ],
      short_name: "megaeth",
      slip44: 60,
      slug: "megaeth",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "megaeth-testnet-2"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6343",
      cashaddr_prefix: null,
      chain_id_hex: "0x18c7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-mega.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://megaeth.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/megaeth.svg",
      mainnet: "eip155:4326",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "MegaETH Testnet",
      native_asset: "eip155:6343/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "megaeth",
        thegraph: "megaeth-timothy"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://carrot.megaeth.com/rpc"
      ],
      short_name: "megaeth-testnet",
      slip44: 60,
      slug: "megaeth_testnet_2",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "megaeth-testnet-deprecated"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6342",
      cashaddr_prefix: null,
      chain_id_hex: "0x18c6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://testnet.megaeth.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/megaeth.svg",
      mainnet: "eip155:4326",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "MegaETH Testnet (Deprecated)",
      native_asset: "eip155:6342/slip44:60",
      native_name: "MegaETH Testnet Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "megatest-deprecated",
      slip44: 60,
      slug: "megaeth_testnet_deprecated",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4352",
      cashaddr_prefix: null,
      chain_id_hex: "0x1100",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://memecorescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://memecore.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "MemeCore",
      native_asset: null,
      native_name: "M",
      native_token: "M",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.memecore.net",
        "wss://ws.memecore.net"
      ],
      short_name: "m",
      slip44: null,
      slug: "memecore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:51888",
      cashaddr_prefix: null,
      chain_id_hex: "0xcab0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/memento.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Memento",
      native_asset: "eip155:51888/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "memento",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mementoblockchain.com"
      ],
      short_name: "memento-mainnet",
      slip44: 60,
      slug: "memento",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "memento-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2129",
      cashaddr_prefix: null,
      chain_id_hex: "0x851",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/memento.svg",
      mainnet: "eip155:51888",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Memento Testnet",
      native_asset: "eip155:2129/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "memento"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.memento.zeeve.online"
      ],
      short_name: "memento-testnet",
      slip44: 60,
      slug: "memento_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4200",
      cashaddr_prefix: null,
      chain_id_hex: "0x1068",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.merlinchain.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://merlinchain.io",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/merlin.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Merlin",
      native_asset: null,
      native_name: "BTC",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "merlin",
        coingecko_native: "merlin-chain-bridged-wrapped-btc-merlin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.merlinchain.io",
        "https://merlin-mainnet-enterprise.unifra.io",
        "https://rpc-merlin.rockx.com"
      ],
      short_name: "Merlin-Mainnet",
      slip44: null,
      slug: "merlin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "merlin-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:686868",
      cashaddr_prefix: null,
      chain_id_hex: "0xa7b14",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/merlin-testnet.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Merlin-Testnet",
      native_asset: null,
      native_name: null,
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "merlin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.merlinchain.io"
      ],
      short_name: null,
      slip44: null,
      slug: "merlin_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1750",
      cashaddr_prefix: null,
      chain_id_hex: "0x6d6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.metall2.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://metall2.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/metal.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Metal L2",
      native_asset: "eip155:1750/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "metal",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.metall2.com"
      ],
      short_name: "metall2",
      slip44: 60,
      slug: "metal",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "metal-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1740",
      cashaddr_prefix: null,
      chain_id_hex: "0x6cc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.explorer.metall2.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://metall2.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/metal.svg",
      mainnet: "eip155:1750",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Metal L2 Testnet",
      native_asset: "eip155:1740/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "metal"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.rpc.metall2.com"
      ],
      short_name: "metall2-testnet",
      slip44: 60,
      slug: "metal_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:82",
      cashaddr_prefix: null,
      chain_id_hex: "0x52",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.meter.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.meter.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Meter",
      native_asset: null,
      native_name: "Meter",
      native_token: "MTR",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "meter-stable"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.meter.io"
      ],
      short_name: "Meter",
      slip44: null,
      slug: "meter",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "meter-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:83",
      cashaddr_prefix: null,
      chain_id_hex: "0x53",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan-warringstakes.meter.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.meter.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:82",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Meter Testnet",
      native_asset: null,
      native_name: "Meter",
      native_token: "MTR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpctest.meter.io"
      ],
      short_name: "MeterTest",
      slip44: null,
      slug: "meter_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:1088",
      cashaddr_prefix: null,
      chain_id_hex: "0x440",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.metis.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/metis.svg",
      mainnet: null,
      metadata: {},
      name: "Metis",
      native_asset: null,
      native_name: "Metis",
      native_token: "METIS",
      native_token_address: "0xdeaddeaddeaddeaddeaddeaddeaddeaddead0000",
      parent: "eip155:1",
      providers: {
        ccip: "metis",
        coingecko_native: "metis-token",
        defillama: "metis",
        thegraph: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "metis-andromeda",
      slip44: null,
      slug: "metis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:588",
      cashaddr_prefix: null,
      chain_id_hex: "0x24c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.metis.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/metis.svg",
      mainnet: "eip155:1088",
      metadata: {},
      name: "Metis Goerli",
      native_asset: null,
      native_name: "tMetis",
      native_token: "METIS",
      native_token_address: null,
      parent: "eip155:4",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "metis-stardust",
      slip44: null,
      slug: "metis_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "metis-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:59902",
      cashaddr_prefix: null,
      chain_id_hex: "0xe9fe",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-explorer.metisdevops.link",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.metis.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/metis.svg",
      mainnet: "eip155:1088",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Metis Sepolia Testnet",
      native_asset: null,
      native_name: "tMetis",
      native_token: "tMETIS",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.metisdevops.link",
        "https://metis-sepolia-rpc.publicnode.com",
        "wss://metis-sepolia-rpc.publicnode.com"
      ],
      short_name: "metis-sepolia",
      slip44: null,
      slug: "metis_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:31612",
      cashaddr_prefix: null,
      chain_id_hex: "0x7b7c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mezo.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mezo.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mezo",
      native_asset: null,
      native_name: "Bitcoin",
      native_token: "BTC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc_evm-mezo.imperator.co",
        "https://mezo-mainnet.boar.network",
        "https://mainnet.mezo.public.validationcloud.io",
        "https://rpc-internal.mezo.org"
      ],
      short_name: "mezo",
      slip44: null,
      slug: "mezo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "milk",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:milkyway",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://milkyway.zone",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "milkyway",
        sources: [
          "cosmos/chain-registry"
        ]
      },
      name: "MilkyWay",
      native_asset: "cosmos:milkyway/slip44:118",
      native_name: "Milk",
      native_token: "MILK",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "milkyway",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "milkyway-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "milk",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:ceers-2112",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:milkyway",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "milkywaytestnet",
        sources: [
          "hyperlane"
        ]
      },
      name: "Milkyway Testnet",
      native_asset: "cosmos:ceers-2112/slip44:118",
      native_name: "MILK",
      native_token: "MILK",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "milkyway_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:228",
      cashaddr_prefix: null,
      chain_id_hex: "0xe4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://mindnetwork.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/mind.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mind Network",
      native_asset: "eip155:228/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "mind",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-mainnet.mindnetwork.xyz",
        "wss://rpc-mainnet.mindnetwork.xyz"
      ],
      short_name: "fhe",
      slip44: 60,
      slug: "mind",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mind-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:192940",
      cashaddr_prefix: null,
      chain_id_hex: "0x2f1ac",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://mindnetwork.xyz",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/mind.svg",
      mainnet: "eip155:228",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mind Network Testnet",
      native_asset: "eip155:192940/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "mind"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-testnet.mindnetwork.xyz",
        "wss://rpc-testnet.mindnetwork.xyz"
      ],
      short_name: "fhet",
      slip44: 60,
      slug: "mind_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:185",
      cashaddr_prefix: null,
      chain_id_hex: "0xb9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mintchain.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.mintchain.io",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/mint.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mint",
      native_asset: "eip155:185/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "mint",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mintchain.io",
        "https://global.rpc.mintchain.io",
        "https://asia.rpc.mintchain.io"
      ],
      short_name: "mint",
      slip44: 60,
      slug: "mint",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mint-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1687",
      cashaddr_prefix: null,
      chain_id_hex: "0x697",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-testnet-explorer.mintchain.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.mintchain.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mint.svg",
      mainnet: "eip155:185",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mint Sepolia Testnet",
      native_asset: "eip155:1687/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "mint"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia-testnet-rpc.mintchain.io"
      ],
      short_name: "mintsepoliatest",
      slip44: 60,
      slug: "mint_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "miracle-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:92278",
      cashaddr_prefix: null,
      chain_id_hex: "0x16876",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://miracleplay.gg",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Miracle Chain",
      native_asset: null,
      native_name: "Miracle Play Token",
      native_token: "MPT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "miracle-play"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.miracleplay.io"
      ],
      short_name: "MIRACLE",
      slip44: null,
      slug: "miracle_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:124816",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e790",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mitoscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Mitosis",
      native_asset: null,
      native_name: "MITO",
      native_token: "MITO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "mitosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mitosis.org"
      ],
      short_name: null,
      slip44: null,
      slug: "mitosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mitosis-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:124832",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e7a0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.mitosiscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mitosis.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:124816",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mitosis Testnet",
      native_asset: null,
      native_name: "MITO",
      native_token: "MITO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.mitosis.org"
      ],
      short_name: "mitosis-testnet",
      slip44: null,
      slug: "mitosis_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "moca-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2288",
      cashaddr_prefix: null,
      chain_id_hex: "0x8f0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.mocachain.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mocachain.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Moca Chain",
      native_asset: null,
      native_name: "MOCA",
      native_token: "MOCA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "mocaverse"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mocachain.org"
      ],
      short_name: "moca",
      slip44: null,
      slug: "moca_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mocha-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "celestia",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:mocha-4",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:celestia",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "celestiatestnet3",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Mocha Testnet",
      native_asset: "cosmos:mocha-4/slip44:118",
      native_name: "Celestia",
      native_token: "TIA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "mocha_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:34443",
      cashaddr_prefix: null,
      chain_id_hex: "0x868b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.mode.network",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/mode.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mode",
      native_asset: "eip155:34443/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "mode",
        coingecko_native: "ethereum",
        thegraph: "mode-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.mode.network",
        "https://mode.drpc.org",
        "wss://mode.drpc.org"
      ],
      short_name: "mode",
      slip44: 60,
      slug: "mode",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mode-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:919",
      cashaddr_prefix: null,
      chain_id_hex: "0x397",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.mode.network",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mode.svg",
      mainnet: "eip155:34443",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Mode Testnet",
      native_asset: "eip155:919/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "mode",
        thegraph: "mode-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.mode.network"
      ],
      short_name: "modesep",
      slip44: 60,
      slug: "mode_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:143",
      cashaddr_prefix: null,
      chain_id_hex: "0x8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://monad.xyz",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/monad.svg",
      mainnet: null,
      metadata: {},
      name: "Monad",
      native_asset: "eip155:143/slip44:268435779",
      native_name: "Monad",
      native_token: "MON",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "monad",
        coingecko_native: "monad",
        moralis: "monad",
        moralis_wallet: "monad",
        thegraph: "monad"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mon",
      slip44: 268435779,
      slug: "monad",
      status: "active",
      wrapped_native_address: "0x3bd359c1119da7da1d913d1c4d2b7c461115433a"
    },
    {
      aliases: [
        "monad-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10143",
      cashaddr_prefix: null,
      chain_id_hex: "0x279f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://monad.xyz",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/monad.svg",
      mainnet: "eip155:143",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Monad Testnet",
      native_asset: "eip155:10143/slip44:268435779",
      native_name: "Testnet MON Token",
      native_token: "MON",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "monad",
        thegraph: "monad-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.monad.xyz"
      ],
      short_name: "mon-testnet",
      slip44: 268435779,
      slug: "monad_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "monero-mainnet",
        "xmr"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 120,
      brand_color: "#FF6600",
      caip2: "monero:418015bb9ae982a1975da7d79277c270",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 12,
      explorer_paths: {
        address: null,
        token: null
      },
      explorer_url: "https://xmrchain.net",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://www.getmonero.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Monero",
      native_asset: "monero:418015bb9ae982a1975da7d79277c270/slip44:128",
      native_name: "Monero",
      native_token: "XMR",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "monero",
        blockchair_api: "monero",
        coingecko_native: "monero",
        xmrchain: "mainnet"
      },
      reorg_depth: 10,
      rpc_urls: [],
      short_name: null,
      slip44: 128,
      slug: "monero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1287",
      cashaddr_prefix: null,
      chain_id_hex: "0x507",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonbase.moonscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.moonbeam.network/learn/platform/networks/moonbase",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonbase.svg",
      mainnet: "eip155:1284",
      metadata: {},
      name: "Moonbase",
      native_asset: "eip155:1287/slip44:1284",
      native_name: "Dev",
      native_token: "DEV",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "mbase"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mbase",
      slip44: 1284,
      slug: "moonbase",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1284",
      cashaddr_prefix: null,
      chain_id_hex: "0x504",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://moonbeam.network/networks/moonbeam",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonbeam.svg",
      mainnet: null,
      metadata: {},
      name: "Moonbeam",
      native_asset: "eip155:1284/slip44:1284",
      native_name: "Glimmer",
      native_token: "GLMR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "moonbeam",
        coingecko_native: "moonbeam",
        defillama: "moonbeam",
        dexscreener: "moonbeam",
        metasleuth: "moonbeam",
        moralis: "moonbeam",
        thegraph: "moonbeam"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mbeam",
      slip44: 1284,
      slug: "moonbeam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#C0C0C0",
      caip2: "eip155:1285",
      cashaddr_prefix: null,
      chain_id_hex: "0x505",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://moonbeam.network/networks/moonriver",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonriver.svg",
      mainnet: null,
      metadata: {},
      name: "Moonriver",
      native_asset: "eip155:1285/slip44:1285",
      native_name: "Moonriver",
      native_token: "MOVR",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "moonriver",
        coingecko_native: "moonriver",
        defillama: "moonriver",
        dexscreener: "moonriver",
        moralis: "moonriver",
        thegraph: "moonriver"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mriver",
      slip44: 1285,
      slug: "moonriver",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2818",
      cashaddr_prefix: null,
      chain_id_hex: "0xb02",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.morphl2.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://morphl2.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/morph.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Morph",
      native_asset: "eip155:2818/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "morph",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.morphl2.io",
        "https://rpc-quicknode.morphl2.io",
        "wss://rpc.morphl2.io:8443",
        "wss://rpc-quicknode.morphl2.io"
      ],
      short_name: "morph",
      slip44: 60,
      slug: "morph",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "morph-holesky"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2810",
      cashaddr_prefix: null,
      chain_id_hex: "0xafa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-holesky.morphl2.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://morphl2.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/morph.svg",
      mainnet: "eip155:2818",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Morph Holesky",
      native_asset: "eip155:2810/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-quicknode-holesky.morphl2.io",
        "https://rpc-holesky.morphl2.io",
        "wss://rpc-quicknode-holesky.morphl2.io"
      ],
      short_name: "hmorph",
      slip44: 60,
      slug: "morph_holesky",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "morph-hoodi"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2910",
      cashaddr_prefix: null,
      chain_id_hex: "0xb5e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-hoodi.morphl2.io",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/morph.svg",
      mainnet: "eip155:2818",
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://docs.morphl2.io",
          "https://rpc-hoodi.morphl2.io"
        ]
      },
      name: "Morph Hoodi",
      native_asset: "eip155:2910/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:560048",
      providers: {
        ccip: "morph"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-hoodi.morphl2.io"
      ],
      short_name: null,
      slip44: 60,
      slug: "morph_hoodi",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "movement-evm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:30732",
      cashaddr_prefix: null,
      chain_id_hex: "0x780c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://movementlabs.xyz",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Movement EVM Testnet",
      native_asset: null,
      native_name: "Move",
      native_token: "MOVE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mevm.testnet.imola.movementlabs.xyz"
      ],
      short_name: "movetest",
      slip44: null,
      slug: "movement_evm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80001",
      cashaddr_prefix: null,
      chain_id_hex: "0x13881",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mumbai.svg",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Mumbai",
      native_asset: "eip155:80001/slip44:966",
      native_name: "MATIC",
      native_token: "MATIC",
      native_token_address: null,
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "maticmum",
      slip44: 966,
      slug: "mumbai",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:42018",
      cashaddr_prefix: null,
      chain_id_hex: "0xa422",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "relay"
        ]
      },
      name: "Mythos",
      native_asset: "eip155:42018/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mythos-mainnet.g.alchemy.com/public"
      ],
      short_name: null,
      slip44: 60,
      slug: "mythos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:22222",
      cashaddr_prefix: null,
      chain_id_hex: "0x56ce",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://nautscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.nautchain.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Nautilus",
      native_asset: null,
      native_name: "Zebec",
      native_token: "ZBC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "zebec-protocol"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.nautilus.nautchain.xyz"
      ],
      short_name: "NAUTCHAIN",
      slip44: null,
      slug: "nautilus",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:mainnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://nearblocks.io",
      family: "near",
      finality_blocks: null,
      info_url: "https://near.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "NEAR",
      native_asset: "near:mainnet/slip44:397",
      native_name: "NEAR",
      native_token: "NEAR",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko: "near-protocol",
        coingecko_native: "near",
        near_rpc: "https://archival-rpc.mainnet.near.org",
        thegraph: "near-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fastnear.com",
        "https://archival-rpc.mainnet.near.org"
      ],
      short_name: null,
      slip44: 397,
      slug: "near",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-protocol"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:397",
      cashaddr_prefix: null,
      chain_id_hex: "0x18d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://eth-explorer.near.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://near.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "NEAR Protocol",
      native_asset: null,
      native_name: "NEAR",
      native_token: "NEAR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://eth-rpc.mainnet.near.org"
      ],
      short_name: "near",
      slip44: null,
      slug: "near_protocol",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-protocol-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:398",
      cashaddr_prefix: null,
      chain_id_hex: "0x18e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://eth-explorer-testnet.near.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://near.org",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:397",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "NEAR Protocol Testnet",
      native_asset: null,
      native_name: "NEAR",
      native_token: "NEAR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://eth-rpc.testnet.near.org"
      ],
      short_name: "near-testnet",
      slip44: null,
      slug: "near_protocol_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:testnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://testnet.nearblocks.io",
      family: "near",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "near:mainnet",
      metadata: {},
      name: "NEAR Testnet",
      native_asset: "near:testnet/slip44:397",
      native_name: "NEAR",
      native_token: "NEAR",
      native_token_address: null,
      parent: null,
      providers: {
        near_rpc: "https://archival-rpc.testnet.near.org",
        thegraph: "near-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.near.org"
      ],
      short_name: null,
      slip44: 397,
      slug: "near_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "neo-x"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:47763",
      cashaddr_prefix: null,
      chain_id_hex: "0xba93",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://xexplorer.neo.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://neo.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/neo-x.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neo X",
      native_asset: null,
      native_name: "Gas",
      native_token: "GAS",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "neox",
        thegraph: "neox"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-1.rpc.banelabs.org",
        "https://mainnet-2.rpc.banelabs.org"
      ],
      short_name: "neox-mainnet",
      slip44: null,
      slug: "neo_x",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "neo-x-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:12227332",
      cashaddr_prefix: null,
      chain_id_hex: "0xba9304",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://xt4scan.ngd.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://neo.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/neo-x.svg",
      mainnet: "eip155:47763",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neo X Testnet T4",
      native_asset: null,
      native_name: "Gas",
      native_token: "GAS",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "neox",
        thegraph: "neox-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.rpc.banelabs.org"
      ],
      short_name: "neox-t4",
      slip44: null,
      slug: "neo_x_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "neon-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:245022934",
      cashaddr_prefix: null,
      chain_id_hex: "0xe9ac0d6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://neonscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://neonevm.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neon EVM",
      native_asset: null,
      native_name: "Neon",
      native_token: "NEON",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://neon-proxy-mainnet.solana.p2p.org",
        "https://neon-evm.drpc.org",
        "wss://neon-evm.drpc.org"
      ],
      short_name: "neonevm-mainnet",
      slip44: null,
      slug: "neon_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:259",
      cashaddr_prefix: null,
      chain_id_hex: "0x103",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.neonlink.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://neonlink.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neonlink",
      native_asset: null,
      native_name: "Neonlink Native Token",
      native_token: "NEON",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.neonlink.io"
      ],
      short_name: "neon",
      slip44: null,
      slug: "neonlink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "neonlink-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9559",
      cashaddr_prefix: null,
      chain_id_hex: "0x2557",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-scan.neonlink.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://neonlink.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:259",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neonlink Testnet",
      native_asset: null,
      native_name: "Neonlink Native Token",
      native_token: "tNEON",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.neonlink.io"
      ],
      short_name: "testneon",
      slip44: null,
      slug: "neonlink_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1689",
      cashaddr_prefix: null,
      chain_id_hex: "0x699",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.neroscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.nerochain.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "NERO",
      native_asset: null,
      native_name: "NERO",
      native_token: "NERO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "nerochain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.nerochain.io",
        "wss://ws.nerochain.io"
      ],
      short_name: "NERO",
      slip44: null,
      slug: "nero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41443",
      cashaddr_prefix: null,
      chain_id_hex: "0xa1e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.nesa.ai",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Nesa",
      native_asset: null,
      native_name: null,
      native_token: "NES",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "nesa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "nesa-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41444",
      cashaddr_prefix: null,
      chain_id_hex: "0xa1e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.ns.onyxpro.ai",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Nesa",
      native_asset: null,
      native_name: "NES",
      native_token: "NES",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "nesa-ai-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://erpc.ns.onyxpro.ai"
      ],
      short_name: null,
      slip44: null,
      slug: "nesa_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "neura-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:267",
      cashaddr_prefix: null,
      chain_id_hex: "0x10b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.explorer.neuraprotocol.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.neuraprotocol.io",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Neura Testnet",
      native_asset: null,
      native_name: "Testnet Ankr",
      native_token: "ANKR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.ankr.com/neura_testnet"
      ],
      short_name: "tneura",
      slip44: null,
      slug: "neura_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "neutron",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:neutron-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://neutron.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "neutron",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Neutron",
      native_asset: "cosmos:neutron-1/slip44:118",
      native_name: "Neutron",
      native_token: "NTRN",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "neutron-3"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "neutron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7208",
      cashaddr_prefix: null,
      chain_id_hex: "0x1c28",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://nexera.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Nexera",
      native_asset: null,
      native_name: "NXRA",
      native_token: "NXRA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.nexera.network"
      ],
      short_name: "nxra-mainnet",
      slip44: null,
      slug: "nexera",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3946",
      cashaddr_prefix: null,
      chain_id_hex: "0xf6a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.nexus.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Nexus",
      native_asset: null,
      native_name: "NEX",
      native_token: "NEX",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "nexus-4"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.rpc.nexus.xyz"
      ],
      short_name: null,
      slip44: null,
      slug: "nexus",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "nibiru-cataclysm-1"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6900",
      cashaddr_prefix: null,
      chain_id_hex: "0x1af4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://nibiru.fi",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Nibiru cataclysm-1",
      native_asset: null,
      native_name: "NIBI",
      native_token: "NIBI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "nibiru"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm-rpc.nibiru.fi"
      ],
      short_name: "cataclysm-1",
      slip44: null,
      slug: "nibiru_cataclysm_1",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "noble",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:noble-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        token: null,
        tx: "txs"
      },
      explorer_url: "https://www.mintscan.io/noble",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://nobleassets.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "noble",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Noble",
      native_asset: "cosmos:noble-1/slip44:118",
      native_name: "Stake",
      native_token: "STAKE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "noble-dollar-usdn"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "noble",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "noble-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "noble",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:grand-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null,
        tx: "txs"
      },
      explorer_url: "https://mintscan.io/noble-testnet",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://nobleassets.xyz",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:noble-1",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "nobletestnet",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Noble Testnet",
      native_asset: "cosmos:grand-1/slip44:118",
      native_name: "Stake",
      native_token: "STAKE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "noble_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "oasis-emerald"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:42262",
      cashaddr_prefix: null,
      chain_id_hex: "0xa516",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.oasis.io/mainnet/emerald",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.oasis.io/dapp/emerald",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Oasis Emerald",
      native_asset: null,
      native_name: "Emerald Rose",
      native_token: "ROSE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://emerald.oasis.io",
        "wss://emerald.oasis.io/ws"
      ],
      short_name: "emerald",
      slip44: null,
      slug: "oasis_emerald",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "oasis-sapphire-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:23295",
      cashaddr_prefix: null,
      chain_id_hex: "0x5aff",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.oasis.io/testnet/sapphire",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.oasis.io/dapp/sapphire",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Oasis Sapphire Testnet",
      native_asset: null,
      native_name: "Sapphire Test Rose",
      native_token: "TEST",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.sapphire.oasis.io",
        "wss://testnet.sapphire.oasis.io/ws"
      ],
      short_name: "sapphire-testnet",
      slip44: null,
      slug: "oasis_sapphire_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "odyssey-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:911867",
      cashaddr_prefix: null,
      chain_id_hex: "0xde9fb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://odyssey-explorer.ithaca.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ithaca.xyz",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Odyssey Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://odyssey.ithaca.xyz"
      ],
      short_name: "odyssey-testnet",
      slip44: null,
      slug: "odyssey_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:66",
      cashaddr_prefix: null,
      chain_id_hex: "0x42",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.oklink.com/en/okc",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.okex.com/okc",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "OKXChain",
      native_asset: null,
      native_name: "OKXChain Global Utility Token",
      native_token: "OKT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "oec-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://exchainrpc.okex.org",
        "https://okc-mainnet.gateway.pokt.network/v1/lb/6275309bea1b320039c893ff"
      ],
      short_name: "okt",
      slip44: null,
      slug: "okxchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ondo-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9000",
      cashaddr_prefix: null,
      chain_id_hex: "0x2328",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Ondo-Testnet",
      native_asset: null,
      native_name: null,
      native_token: "ONDO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://evmos-testnet.drpc.org",
        "https://evmos-testnet-evm-rpc.publicnode.com"
      ],
      short_name: null,
      slip44: null,
      slug: "ondo_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:21211",
      cashaddr_prefix: null,
      chain_id_hex: "0x52db",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Onemoney",
      native_asset: null,
      native_name: null,
      native_token: "FREE",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.sidechain.mainnet.1money.network"
      ],
      short_name: null,
      slip44: null,
      slug: "onemoney",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:58",
      cashaddr_prefix: null,
      chain_id_hex: "0x3a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.ont.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ont.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ontology",
      native_asset: null,
      native_name: "ONG",
      native_token: "ONG",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ong"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://dappnode1.ont.io:10339",
        "https://dappnode2.ont.io:10339",
        "https://dappnode3.ont.io:10339",
        "https://dappnode4.ont.io:10339"
      ],
      short_name: "OntologyMainnet",
      slip44: null,
      slug: "ontology",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:970",
      cashaddr_prefix: null,
      chain_id_hex: "0x3ca",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://oortech.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Oort",
      native_asset: null,
      native_name: "Oort",
      native_token: "OORT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "oort"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.oortech.com"
      ],
      short_name: "ccn",
      slip44: null,
      slug: "oort",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:204",
      cashaddr_prefix: null,
      chain_id_hex: "0xcc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnbscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://opbnb.bnbchain.org/en",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/opbnb.svg",
      mainnet: null,
      metadata: {},
      name: "opBNB",
      native_asset: "eip155:204/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "BNB",
      native_token_address: null,
      parent: "eip155:56",
      providers: {
        ccip: "opbnb",
        coingecko_native: "binancecoin",
        defillama: "op_bnb",
        thegraph: "bnb-op"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "obnb",
      slip44: 714,
      slug: "opbnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "opbnb-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5611",
      cashaddr_prefix: null,
      chain_id_hex: "0x15eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnb-testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://opbnb.bnbchain.org/en",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/opbnb.svg",
      mainnet: "eip155:204",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "opBNB Testnet",
      native_asset: "eip155:5611/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "tBNB",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "opbnb"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://opbnb-testnet-rpc.bnbchain.org",
        "https://opbnb-testnet.nodereal.io/v1/64a9df0874fb4a93b9d0a3849de012d3",
        "https://opbnb-testnet.nodereal.io/v1/e9a36765eb8a40b9bd12e680a1fd2bc5",
        "https://opbnb-testnet-rpc.publicnode.com"
      ],
      short_name: "obnbt",
      slip44: 714,
      slug: "opbnb_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "opengradient-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2970285607590380",
      cashaddr_prefix: null,
      chain_id_hex: "0xa8d75828599ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Opengradient Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "opengradient_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1612",
      cashaddr_prefix: null,
      chain_id_hex: "0x64c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.openledger.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.openledger.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "OpenLedger",
      native_asset: null,
      native_name: "Open",
      native_token: "OPEN",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.openledger.xyz"
      ],
      short_name: "open",
      slip44: null,
      slug: "openledger",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:222",
      cashaddr_prefix: null,
      chain_id_hex: "0xde",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Opn",
      native_asset: null,
      native_name: null,
      native_token: "IOPN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.iopn.tech",
        "https://mainnet-rpc2.iopn.tech"
      ],
      short_name: null,
      slip44: null,
      slug: "opn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "op",
        "op-mainnet",
        "optimism-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:10",
      cashaddr_prefix: null,
      chain_id_hex: "0xa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://optimistic.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: null,
      metadata: {},
      name: "Optimism",
      native_asset: "eip155:10/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        alchemy: "opt-mainnet",
        arkham: "optimism",
        blockchair: "optimism",
        blockscan: "10",
        blockscout: "https://explorer.optimism.io/api",
        blocksec: "10",
        ccip: "optimism",
        codeslaw: "optimism",
        coingecko: "optimistic-ethereum",
        coingecko_native: "ethereum",
        coinmarketcap: "optimism",
        defillama: "optimism",
        dexscreener: "optimism",
        etherscan_scan: "optimistic.etherscan.io",
        infura: "optimism-mainnet",
        metasleuth: "optimism",
        moralis: "optimism",
        moralis_wallet: "optimism",
        sourcify: "10",
        thegraph: "optimism"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.optimism.io",
        "https://optimism-rpc.publicnode.com"
      ],
      short_name: "oeth",
      slip44: 60,
      slug: "optimism",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:420",
      cashaddr_prefix: null,
      chain_id_hex: "0x1a4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Goerli",
      native_asset: "eip155:420/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ogor",
      slip44: 60,
      slug: "optimism_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:11155420",
      cashaddr_prefix: null,
      chain_id_hex: "0xaa37dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-optimism.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Sepolia",
      native_asset: "eip155:11155420/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        alchemy: "opt-sepolia",
        ccip: "optimism",
        etherscan_v2: "11155420",
        infura: "optimism-sepolia",
        sourcify: "11155420",
        thegraph: "optimism-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.optimism.io",
        "https://optimism-sepolia-rpc.publicnode.com"
      ],
      short_name: "opsep",
      slip44: 60,
      slug: "optimism_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:291",
      cashaddr_prefix: null,
      chain_id_hex: "0x123",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.orderly.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://orderly.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Orderly",
      native_asset: "eip155:291/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.orderly.network"
      ],
      short_name: "orderly",
      slip44: 60,
      slug: "orderly",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "osmosis-mainnet",
        "osmosis-1"
      ],
      base58_versions: [],
      bech32_hrp: "osmo",
      block_time_seconds: 1.6,
      brand_color: "#750BBB",
      caip2: "cosmos:osmosis-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/osmosis",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://osmosis.zone",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Osmosis",
      native_asset: "cosmos:osmosis-1/slip44:118",
      native_name: "Osmosis",
      native_token: "OSMO",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko: "osmosis",
        coingecko_native: "osmosis",
        cosmos_directory: "https://rest.cosmos.directory/osmosis",
        cosmos_lcd: "https://osmosis-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "osmosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ozean-poseidon-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:31911",
      cashaddr_prefix: null,
      chain_id_hex: "0x7ca7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://poseidon-testnet.explorer.caldera.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Ozean Poseidon Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://poseidon-testnet.rpc.caldera.xyz/http"
      ],
      short_name: null,
      slip44: null,
      slug: "ozean_poseidon_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11297108109",
      cashaddr_prefix: null,
      chain_id_hex: "0x2a15c308d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://palm.network",
      is_testnet: false,
      layer: "sidechain",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Palm",
      native_asset: null,
      native_name: "PALM",
      native_token: "PALM",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "palm",
      slip44: null,
      slug: "palm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3338",
      cashaddr_prefix: null,
      chain_id_hex: "0xd0a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://peaq.subscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.peaq.xyz",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "peaq",
      native_asset: "eip155:3338/slip44:3338",
      native_name: "peaq",
      native_token: "PEAQ",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "peaq-2",
        thegraph: "peaq"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "PEAQ",
      slip44: 3338,
      slug: "peaq",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pgn-public-goods-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:424",
      cashaddr_prefix: null,
      chain_id_hex: "0x1a8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.publicgoods.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://publicgoods.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "PGN (Public Goods Network)",
      native_asset: "eip155:424/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.publicgoods.network"
      ],
      short_name: "PGN",
      slip44: 60,
      slug: "pgn_public_goods_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1672",
      cashaddr_prefix: null,
      chain_id_hex: "0x688",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://pharosscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://pharos.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/pharos.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Pharos",
      native_asset: null,
      native_name: "PharosCoin",
      native_token: "PROS",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "pharos",
        coingecko_native: "prosper-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.pharos.xyz"
      ],
      short_name: "pharos",
      slip44: null,
      slug: "pharos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pharos-atlantic-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:688689",
      cashaddr_prefix: null,
      chain_id_hex: "0xa8231",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/pharos-atlantic-testnet.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Atlanticocean-Testnet",
      native_asset: null,
      native_name: null,
      native_token: "PHRS",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "pharos"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://atlantic.dplabs-internal.com"
      ],
      short_name: null,
      slip44: null,
      slug: "pharos_atlantic_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:9745",
      cashaddr_prefix: null,
      chain_id_hex: "0x2611",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://plasmascan.to",
      family: "evm",
      finality_blocks: null,
      info_url: "https://plasma.to",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/plasma.svg",
      mainnet: null,
      metadata: {},
      name: "Plasma",
      native_asset: null,
      native_name: "Plasma",
      native_token: "XPL",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "plasma",
        coingecko_native: "plasma",
        evm_rpc: "ingest"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.plasma.to"
      ],
      short_name: "plasma",
      slip44: null,
      slug: "plasma",
      status: "active",
      wrapped_native_address: "0x6100e367285b01f48d07953803a2d8dca5d19873"
    },
    {
      aliases: [
        "plasma-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:9746",
      cashaddr_prefix: null,
      chain_id_hex: "0x2612",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.plasmascan.to",
      family: "evm",
      finality_blocks: null,
      info_url: "https://plasma.to",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/plasma.svg",
      mainnet: "eip155:9745",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plasma Testnet",
      native_asset: null,
      native_name: "Testnet Plasma",
      native_token: "XPL",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "plasma"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.plasma.to"
      ],
      short_name: "plasma-testnet",
      slip44: null,
      slug: "plasma_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98866",
      cashaddr_prefix: null,
      chain_id_hex: "0x18232",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.plume.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://plume.org",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/plume.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plume",
      native_asset: null,
      native_name: "Plume",
      native_token: "PLUME",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "plume",
        coingecko_native: "plume"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.plume.org",
        "wss://rpc.plume.org"
      ],
      short_name: "plume-mainnet",
      slip44: null,
      slug: "plume",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "plume-devnet-legacy"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98864",
      cashaddr_prefix: null,
      chain_id_hex: "0x18230",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://plume.org",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:98865",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plume Devnet (Legacy)",
      native_asset: "eip155:98864/slip44:60",
      native_name: "Plume Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "plume-devnet",
      slip44: 60,
      slug: "plume_devnet_legacy",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "plume-legacy"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98865",
      cashaddr_prefix: null,
      chain_id_hex: "0x18231",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://plume.org",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plume (Legacy)",
      native_asset: "eip155:98865/slip44:60",
      native_name: "Plume Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "plume",
      slip44: 60,
      slug: "plume_legacy",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "plume-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98867",
      cashaddr_prefix: null,
      chain_id_hex: "0x18233",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-explorer.plume.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://plume.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/plume.svg",
      mainnet: "eip155:98866",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plume Testnet",
      native_asset: null,
      native_name: "Plume",
      native_token: "PLUME",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "plume"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.plume.org",
        "wss://testnet-rpc.plume.org"
      ],
      short_name: "plume-testnet",
      slip44: null,
      slug: "plume_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "plume-testnet-legacy"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:161221135",
      cashaddr_prefix: null,
      chain_id_hex: "0x99c0a0f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://plume.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/plume.svg",
      mainnet: "eip155:98866",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Plume Testnet (Legacy)",
      native_asset: null,
      native_name: "Plume Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "plume-testnet-legacy",
      slip44: null,
      slug: "plume_testnet_legacy",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polkadot-mainnet",
        "0x162",
        "dot"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#E6007A",
      caip2: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 10,
      explorer_paths: {},
      explorer_url: "https://polkadot.subscan.io",
      family: "other",
      finality_blocks: null,
      info_url: "https://polkadot.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Polkadot",
      native_asset: "polkadot:91b171bb158e2d3848fa23a9f1c25182/slip44:354",
      native_name: "Polkadot",
      native_token: "DOT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko: "polkadot",
        coingecko_native: "polkadot"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 354,
      slug: "polkadot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "matic",
        "matic-network",
        "polygon-mainnet",
        "polygon-pos"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:137",
      cashaddr_prefix: null,
      chain_id_hex: "0x89",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://polygonscan.com",
      family: "evm",
      finality_blocks: 512,
      info_url: "https://polygon.technology",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon.svg",
      mainnet: null,
      metadata: {},
      name: "Polygon",
      native_asset: "eip155:137/slip44:966",
      native_name: "Polygon Ecosystem Token",
      native_token: "POL",
      native_token_address: "0x0000000000000000000000000000000000001010",
      parent: "eip155:1",
      providers: {
        alchemy: "polygon-mainnet",
        arkham: "polygon",
        blockchair: "polygon",
        blockscan: "137",
        blockscout: "https://polygon.blockscout.com/api",
        blocksec: "137",
        ccip: "polygon",
        codeslaw: "polygon",
        coingecko: "polygon-pos",
        coingecko_native: "polygon-ecosystem-token",
        coinmarketcap: "polygon",
        cowswap: "polygon",
        defillama: "polygon",
        dexscreener: "polygon",
        etherscan_scan: "polygonscan.com",
        infura: "polygon-mainnet",
        metasleuth: "polygon",
        moralis: "polygon",
        moralis_wallet: "polygon",
        sourcify: "137",
        thegraph: "matic"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://polygon-bor-rpc.publicnode.com",
        "https://polygon.drpc.org",
        "https://polygon.gateway.tenderly.co"
      ],
      short_name: "pol",
      slip44: 966,
      slug: "polygon",
      status: "active",
      wrapped_native_address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80002",
      cashaddr_prefix: null,
      chain_id_hex: "0x13882",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://amoy.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon-amoy.svg",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Amoy",
      native_asset: "eip155:80002/slip44:966",
      native_name: "Polygon Ecosystem Token",
      native_token: "POL",
      native_token_address: "0x0000000000000000000000000000000000001010",
      parent: "eip155:11155111",
      providers: {
        alchemy: "polygon-amoy",
        ccip: "polygon",
        infura: "polygon-amoy",
        moralis: "polygon_amoy",
        sourcify: "80002",
        thegraph: "polygon-amoy"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://polygon-amoy.drpc.org",
        "https://polygon-amoy-bor-rpc.publicnode.com"
      ],
      short_name: "polygonamoy",
      slip44: 966,
      slug: "polygon_amoy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-testnet-tatara"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:129399",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f977",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.tatara.katana.network",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon.svg",
      mainnet: "eip155:137",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tatara Testnet",
      native_asset: "eip155:129399/slip44:966",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "polygon"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://rpc.tatara.katanarpc.com"
      ],
      short_name: "Tatara",
      slip44: 966,
      slug: "polygon_testnet_tatara",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm",
        "zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1101",
      cashaddr_prefix: null,
      chain_id_hex: "0x44d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://zkevm.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology/polygon-zkevm",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon-zkevm.svg",
      mainnet: null,
      metadata: {},
      name: "Polygon zkEVM",
      native_asset: "eip155:1101/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        arkham: "polygon_zkevm",
        ccip: "polygon-zkevm",
        coingecko_native: "ethereum",
        thegraph: "polygon-zkevm"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zkevm",
      slip44: 60,
      slug: "polygon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2442",
      cashaddr_prefix: null,
      chain_id_hex: "0x98a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology/polygon-zkevm",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon-zkevm-cardona.svg",
      mainnet: "eip155:1101",
      metadata: {},
      name: "Polygon zkEVM Cardona",
      native_asset: "eip155:2442/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "polygon",
        thegraph: "polygon-zkevm-cardona"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zkevm-testnet-cardona",
      slip44: 60,
      slug: "polygon_zkevm_cardona",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1442",
      cashaddr_prefix: null,
      chain_id_hex: "0x5a2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.public.zkevm-test.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology/solutions/polygon-zkevm",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon-zkevm.svg",
      mainnet: "eip155:1101",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Polygon zkEVM Testnet",
      native_asset: "eip155:1442/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.public.zkevm-test.net",
        "https://polygon-zkevm-testnet.drpc.org",
        "wss://polygon-zkevm-testnet.drpc.org"
      ],
      short_name: "testnet-zkEVM-mango",
      slip44: 60,
      slug: "polygon_zkevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8008",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f48",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://polynomial.fi",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Polynomial",
      native_asset: "eip155:8008/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.polynomial.fi"
      ],
      short_name: "polynomial",
      slip44: 60,
      slug: "polynomial",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:227",
      cashaddr_prefix: null,
      chain_id_hex: "0xe3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://prom-blockscout.eu-north-2.gateway.fm",
      family: "evm",
      finality_blocks: null,
      info_url: "https://prom.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Prom",
      native_asset: null,
      native_name: "Prom",
      native_token: "PROM",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "prometeus"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://prom-rpc.eu-north-2.gateway.fm"
      ],
      short_name: "PROM",
      slip44: null,
      slug: "prom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "proof-of-play-apex"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:70700",
      cashaddr_prefix: null,
      chain_id_hex: "0x1142c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.apex.proofofplay.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://proofofplay.com",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Proof of Play - Apex",
      native_asset: "eip155:70700/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:42161",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apex.proofofplay.com"
      ],
      short_name: "pop-apex",
      slip44: 60,
      slug: "proof_of_play_apex",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "pb",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:pio-mainnet-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/provenance",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://provenance.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "provenance",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Provenance",
      native_asset: "cosmos:pio-mainnet-1/slip44:505",
      native_name: "Hash",
      native_token: "HASH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 505,
      slug: "provenance",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pls"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:369",
      cashaddr_prefix: null,
      chain_id_hex: "0x171",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.pulsechain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://pulsechain.com",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/pulsechain.svg",
      mainnet: null,
      metadata: {},
      name: "PulseChain",
      native_asset: "eip155:369/slip44:1028",
      native_name: "Pulse",
      native_token: "PLS",
      native_token_address: null,
      parent: null,
      providers: {
        arkham: "pulsechain",
        coingecko_native: "pulsechain",
        moralis: "pulsechain",
        moralis_wallet: "pulsechain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "pls",
      slip44: 1028,
      slug: "pulsechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "rari-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1380012617",
      cashaddr_prefix: null,
      chain_id_hex: "0x52415249",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mainnet.explorer.rarichain.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://rarichain.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "RARI Chain",
      native_asset: "eip155:1380012617/slip44:60",
      native_name: "Ethereum",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rari.calderachain.xyz/http"
      ],
      short_name: "rari-mainnet",
      slip44: 60,
      slug: "rari_chain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:72957",
      cashaddr_prefix: null,
      chain_id_hex: "0x11cfd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.rayls.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://rayls.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Rayls",
      native_asset: null,
      native_name: "USD Rayls",
      native_token: "USDr",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.rayls.com"
      ],
      short_name: "rayls",
      slip44: null,
      slug: "rayls",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "re-al"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:111188",
      cashaddr_prefix: null,
      chain_id_hex: "0x1b254",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.re.al",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Re.Al",
      native_asset: null,
      native_name: "Real Ether",
      native_token: "reETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.realforreal.gelato.digital",
        "https://tangible-real.gateway.tenderly.co"
      ],
      short_name: null,
      slip44: null,
      slug: "re_al",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1597",
      cashaddr_prefix: null,
      chain_id_hex: "0x63d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://reactive.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Reactive",
      native_asset: null,
      native_name: "React",
      native_token: "REACT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "reactive-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet-rpc.rnk.dev"
      ],
      short_name: "react",
      slip44: null,
      slug: "reactive",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "reactive-kopli"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5318008",
      cashaddr_prefix: null,
      chain_id_hex: "0x512578",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://reactive.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Reactive Kopli",
      native_asset: null,
      native_name: "Kopli React",
      native_token: "REACT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://kopli-rpc.rnk.dev"
      ],
      short_name: "kreact",
      slip44: null,
      slug: "reactive_kopli",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "redbelly-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:151",
      cashaddr_prefix: null,
      chain_id_hex: "0x97",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://redbelly.routescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://redbelly.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Redbelly Network",
      native_asset: "eip155:151/slip44:824",
      native_name: "Redbelly Network Coin",
      native_token: "RBNT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "redbelly-network-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://governors.mainnet.redbelly.network"
      ],
      short_name: "rbn",
      slip44: 824,
      slug: "redbelly_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:690",
      cashaddr_prefix: null,
      chain_id_hex: "0x2b2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.redstone.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://redstone.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Redstone",
      native_asset: "eip155:690/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.redstonechain.com",
        "wss://rpc.redstonechain.com"
      ],
      short_name: "redstone",
      slip44: 60,
      slug: "redstone",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "reya-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1729",
      cashaddr_prefix: null,
      chain_id_hex: "0x6c1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.reya.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://reya.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Reya Network",
      native_asset: "eip155:1729/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.reya.network",
        "wss://ws.reya.network"
      ],
      short_name: "reya",
      slip44: 60,
      slug: "reya_network",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4153",
      cashaddr_prefix: null,
      chain_id_hex: "0x1039",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.risechain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://risechain.com",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "RISE",
      native_asset: "eip155:4153/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.risechain.com",
        "wss://rpc.risechain.com/ws"
      ],
      short_name: "rise",
      slip44: 60,
      slug: "rise",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "rise-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11155931",
      cashaddr_prefix: null,
      chain_id_hex: "0xaa39db",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.riselabs.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://risechain.com",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:4153",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "RISE Testnet",
      native_asset: "eip155:11155931/slip44:60",
      native_name: "RISE Testnet Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.riselabs.xyz",
        "wss://testnet.riselabs.xyz/ws"
      ],
      short_name: "rise-testnet",
      slip44: 60,
      slug: "rise_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:784",
      cashaddr_prefix: null,
      chain_id_hex: "0x310",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "Ritual",
      native_asset: null,
      native_name: null,
      native_token: "RITUAL",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-ritual.chainworks.us"
      ],
      short_name: null,
      slip44: null,
      slug: "ritual",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:753",
      cashaddr_prefix: null,
      chain_id_hex: "0x2f1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://rivalz.calderaexplorer.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://rivalz.hub.caldera.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Rivalz",
      native_asset: "eip155:753/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rivalz.calderachain.xyz/http"
      ],
      short_name: "rivalz",
      slip44: 60,
      slug: "rivalz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4663",
      cashaddr_prefix: null,
      chain_id_hex: "0x1237",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://robinhoodchain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.robinhood.com/chain",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/robinhood.svg",
      mainnet: null,
      metadata: {},
      name: "Robinhood Chain",
      native_asset: "eip155:4663/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        blockscout_v2: "https://robinhoodchain.blockscout.com/api/v2",
        ccip: "robinhood-chain",
        coingecko: "robinhood",
        coingecko_native: "ethereum",
        defillama: "robinhood",
        evm_rpc: "discovery",
        moralis: "robinhood",
        moralis_wallet: "robinhood",
        thegraph: "robinhood"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.chain.robinhood.com"
      ],
      short_name: "robinhoodchain",
      slip44: 60,
      slug: "robinhood",
      status: "active",
      wrapped_native_address: "0x0bd7d308f8e1639fab988df18a8011f41eacad73"
    },
    {
      aliases: [
        "robinhood-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:46630",
      cashaddr_prefix: null,
      chain_id_hex: "0xb626",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.chain.robinhood.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.robinhood.com/chain",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/robinhood.svg",
      mainnet: "eip155:4663",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Robinhood Chain Testnet",
      native_asset: "eip155:46630/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "robinhood-chain",
        thegraph: "robinhood-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.chain.robinhood.com/rpc",
        "https://robinhood-sepolia-rpc.publicnode.com",
        "https://robinhood-testnet.drpc.org",
        "wss://robinhood-sepolia-rpc.publicnode.com"
      ],
      short_name: "rh-testnet",
      slip44: 60,
      slug: "robinhood_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "rome-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:200018",
      cashaddr_prefix: null,
      chain_id_hex: "0x30d52",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://node1.testnet.romeprotocol.xyz:1000",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Rome Testnet",
      native_asset: null,
      native_name: "ROME",
      native_token: "ROME",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://node1.testnet.romeprotocol.xyz"
      ],
      short_name: null,
      slip44: null,
      slug: "rome_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "rome-testnet-2"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3001",
      cashaddr_prefix: null,
      chain_id_hex: "0xbb9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://romescout-testnet-1.testnet.romeprotocol.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Rome Testnet",
      native_asset: null,
      native_name: "rSOL",
      native_token: "rSOL",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-1.testnet.romeprotocol.xyz"
      ],
      short_name: null,
      slip44: null,
      slug: "rome_testnet_2",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ron"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2020",
      cashaddr_prefix: null,
      chain_id_hex: "0x7e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://roninchain.com",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/ronin.svg",
      mainnet: null,
      metadata: {},
      name: "Ronin",
      native_asset: null,
      native_name: "RON",
      native_token: "RON",
      native_token_address: null,
      parent: null,
      providers: {
        arkham: "ronin",
        ccip: "ronin",
        coingecko_native: "ronin",
        defillama: "ronin",
        moralis: "ronin",
        moralis_wallet: "ronin",
        thegraph: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ron",
      slip44: null,
      slug: "ronin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2021",
      cashaddr_prefix: null,
      chain_id_hex: "0x7e5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://roninchain.com",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/ronin-saigon.svg",
      mainnet: "eip155:2020",
      metadata: {},
      name: "Ronin Saigon Testnet",
      native_asset: null,
      native_name: "RON",
      native_token: "RON",
      native_token_address: null,
      parent: null,
      providers: {
        moralis: "ronin_saigon"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "ronin_saigon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ronin-saigon-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:202601",
      cashaddr_prefix: null,
      chain_id_hex: "0x31769",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-explorer.roninchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://roninchain.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/ronin.svg",
      mainnet: "eip155:2020",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Ronin Saigon Testnet",
      native_asset: null,
      native_name: "Ronin",
      native_token: "RON",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://saigon-testnet.roninchain.com/rpc",
        "https://ronin-saigon.drpc.org"
      ],
      short_name: "ronin-saigon",
      slip44: null,
      slug: "ronin_saigon_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:30",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.rsk.co",
      family: "evm",
      finality_blocks: null,
      info_url: "https://rootstock.io",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/rootstock.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Rootstock",
      native_asset: "eip155:30/slip44:137",
      native_name: "Smart Bitcoin",
      native_token: "RBTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "rootstock",
        coingecko_native: "rootstock",
        thegraph: "rootstock"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://public-node.rsk.co",
        "https://mycrypto.rsk.co"
      ],
      short_name: "rsk",
      slip44: 137,
      slug: "rootstock",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "rootstock-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:31",
      cashaddr_prefix: null,
      chain_id_hex: "0x1f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.rsk.co",
      family: "evm",
      finality_blocks: null,
      info_url: "https://rootstock.io",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/rootstock.svg",
      mainnet: "eip155:30",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Rootstock Testnet",
      native_asset: "eip155:31/slip44:137",
      native_name: "Testnet Smart Bitcoin",
      native_token: "tRBTC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "rootstock",
        thegraph: "rootstock-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://public-node.testnet.rsk.co",
        "https://mycrypto.testnet.rsk.co"
      ],
      short_name: "trsk",
      slip44: 137,
      slug: "rootstock_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7225878",
      cashaddr_prefix: null,
      chain_id_hex: "0x6e4216",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.saakuru.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://saakuru.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Saakuru",
      native_asset: null,
      native_name: "OAS",
      native_token: "OAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "saakuru-labs"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.saakuru.network"
      ],
      short_name: "saakuru",
      slip44: null,
      slug: "saakuru",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5464",
      cashaddr_prefix: null,
      chain_id_hex: "0x1558",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sagaevm.sagaexplorer.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.saga.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "SagaEVM",
      native_asset: null,
      native_name: "gas",
      native_token: "GAS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "saga-dollar"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sagaevm.jsonrpc.sagarpc.io"
      ],
      short_name: "sagaevm",
      slip44: null,
      slug: "sagaevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1996",
      cashaddr_prefix: null,
      chain_id_hex: "0x7cc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.sanko.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://sanko.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sanko",
      native_asset: null,
      native_name: "DMT",
      native_token: "DMT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "dream-machine-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.sanko.xyz"
      ],
      short_name: "Sanko",
      slip44: null,
      slug: "sanko",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "scroll-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534352",
      cashaddr_prefix: null,
      chain_id_hex: "0x82750",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scrollscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://scroll.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/scroll.svg",
      mainnet: null,
      metadata: {},
      name: "Scroll",
      native_asset: "eip155:534352/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "scroll",
        codeslaw: "scroll",
        coingecko_native: "ethereum",
        defillama: "scroll",
        dexscreener: "scroll",
        etherscan_scan: "scrollscan.com",
        evm_rpc: "ingest",
        thegraph: "scroll"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.scroll.io"
      ],
      short_name: "scr",
      slip44: 60,
      slug: "scroll",
      status: "active",
      wrapped_native_address: "0x5300000000000000000000000000000000000004"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534351",
      cashaddr_prefix: null,
      chain_id_hex: "0x8274f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://scroll.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/scroll-testnet.svg",
      mainnet: "eip155:534352",
      metadata: {},
      name: "Scroll Sepolia",
      native_asset: "eip155:534351/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "scroll",
        thegraph: "scroll-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "scr-sepolia",
      slip44: 60,
      slug: "scroll_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "seda",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:seda-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.seda.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "seda",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "SEDA",
      native_asset: "cosmos:seda-1/slip44:118",
      native_name: "Seda",
      native_token: "SEDA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "seda",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1329",
      cashaddr_prefix: null,
      chain_id_hex: "0x531",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seitrace.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.sei.io",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sei.svg",
      mainnet: null,
      metadata: {},
      name: "Sei",
      native_asset: "eip155:1329/slip44:19000118",
      native_name: "Sei",
      native_token: "SEI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "sei",
        coingecko_native: "sei-network",
        moralis: "sei",
        moralis_wallet: "sei",
        thegraph: "sei-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "sei",
      slip44: 19000118,
      slug: "sei",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-cosmos"
      ],
      base58_versions: [],
      bech32_hrp: "sei",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:pacific-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/sei",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.sei.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "sei",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Sei",
      native_asset: "cosmos:pacific-1/slip44:118",
      native_name: "Sei",
      native_token: "SEI",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "sei_cosmos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1328",
      cashaddr_prefix: null,
      chain_id_hex: "0x530",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.sei.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sei-testnet.svg",
      mainnet: "eip155:1329",
      metadata: {},
      name: "Sei Testnet",
      native_asset: "eip155:1328/slip44:19000118",
      native_name: "Sei",
      native_token: "SEI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "sei",
        moralis: "sei_testnet",
        thegraph: "sei-atlantic"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "sei-testnet",
      slip44: 19000118,
      slug: "sei_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "seismic-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5124",
      cashaddr_prefix: null,
      chain_id_hex: "0x1404",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seismic-testnet.socialscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://seismic.systems",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Seismic Testnet",
      native_asset: null,
      native_name: "Seismic Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://gcp-1.seismictest.net/rpc",
        "https://gcp-2.seismictest.net/rpc",
        "wss://gcp-1.seismictest.net/ws",
        "wss://gcp-2.seismictest.net/ws"
      ],
      short_name: "seismic-testnet",
      slip44: null,
      slug: "seismic_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:11155111",
      cashaddr_prefix: null,
      chain_id_hex: "0xaa36a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://sepolia.otterscan.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sepolia.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Sepolia",
      native_asset: "eip155:11155111/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        alchemy: "eth-sepolia",
        ankr: "eth_sepolia",
        ccip: "ethereum",
        cowswap: "sepolia",
        etherscan_v2: "11155111",
        infura: "sepolia",
        moralis: "sepolia",
        sourcify: "11155111",
        thegraph: "sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-sepolia-rpc.publicnode.com"
      ],
      short_name: "sep",
      slip44: 60,
      slug: "sepolia",
      status: "active",
      wrapped_native_address: "0xfff9976782d46cc05630d1f6ebab18b2324d6b14"
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:109",
      cashaddr_prefix: null,
      chain_id_hex: "0x6d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://shibariumecosystem.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/shibarium.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Shibarium",
      native_asset: null,
      native_name: "BONE Shibarium",
      native_token: "BONE",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "shibarium",
        coingecko_native: "bone-shibaswap"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.shibarium.shib.io",
        "https://shib.nownodes.io"
      ],
      short_name: "shibariumecosystem",
      slip44: null,
      slug: "shibarium",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "shibarium-puppynet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:157",
      cashaddr_prefix: null,
      chain_id_hex: "0x9d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://shibariumecosystem.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/shibarium.svg",
      mainnet: "eip155:109",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Puppynet Testnet",
      native_asset: null,
      native_name: "BONE",
      native_token: "BONE",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "shibarium"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://puppynet.shibrpc.com"
      ],
      short_name: "puppynet",
      slip44: null,
      slug: "shibarium_puppynet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:148",
      cashaddr_prefix: null,
      chain_id_hex: "0x94",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.evm.shimmer.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://shimmer.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "ShimmerEVM",
      native_asset: null,
      native_name: "SMR",
      native_token: "SMR",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "shimmer"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://json-rpc.evm.shimmer.network"
      ],
      short_name: "shimmerevm",
      slip44: null,
      slug: "shimmerevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "shrapnel-subnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2044",
      cashaddr_prefix: null,
      chain_id_hex: "0x7fc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.shrapnel.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Shrapnel Subnet",
      native_asset: null,
      native_name: "Shrapnel Gas Token",
      native_token: "SHRAPG",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "shrapnel-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/shrapnel/mainnet/rpc"
      ],
      short_name: "Shrapnel",
      slip44: null,
      slug: "shrapnel_subnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "silicon-zkevm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2355",
      cashaddr_prefix: null,
      chain_id_hex: "0x933",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scope.silicon.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.silicon.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Silicon zkEVM",
      native_asset: "eip155:2355/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.silicon.network",
        "https://silicon-mainnet.nodeinfra.com"
      ],
      short_name: "silicon-zk",
      slip44: 60,
      slug: "silicon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "skale-europa-hub"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2046399126",
      cashaddr_prefix: null,
      chain_id_hex: "0x79f99296",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://elated-tan-skat.explorer.mainnet.skalenodes.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://europahub.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "SKALE Europa Hub",
      native_asset: null,
      native_name: "sFUEL",
      native_token: "sFUEL",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "polygon-ecosystem-token"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.skalenodes.com/v1/elated-tan-skat",
        "wss://mainnet.skalenodes.com/v1/elated-tan-skat"
      ],
      short_name: "europa",
      slip44: null,
      slug: "skale_europa_hub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "smart-bitcoin-cash"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:10000",
      cashaddr_prefix: null,
      chain_id_hex: "0x2710",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://smartbch.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Smart Bitcoin Cash",
      native_asset: null,
      native_name: "Bitcoin Cash",
      native_token: "BCH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://smartbch.greyh.at",
        "https://rpc-mainnet.smartbch.org",
        "https://smartbch.fountainhead.cash/mainnet",
        "https://smartbch.devops.cash/mainnet"
      ],
      short_name: "smartbch",
      slip44: null,
      slug: "smart_bitcoin_cash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2192",
      cashaddr_prefix: null,
      chain_id_hex: "0x890",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.snaxchain.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://synthetix.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "SnaxChain",
      native_asset: "eip155:2192/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.snaxchain.io"
      ],
      short_name: "snax",
      slip44: 60,
      slug: "snaxchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-mainnet",
        "0x1399c",
        "sol"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account"
      },
      explorer_url: "https://solscan.io",
      family: "solana",
      finality_blocks: null,
      info_url: "https://solana.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana.svg",
      mainnet: null,
      metadata: {},
      name: "Solana",
      native_asset: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      native_token_address: null,
      parent: null,
      providers: {
        arkham: "solana",
        blockchair: "solana",
        ccip: "solana",
        coingecko: "solana",
        coingecko_native: "solana",
        defillama: "solana",
        dexscreener: "solana",
        helius: "mainnet",
        thegraph: "solana-mainnet-beta"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://solana-rpc.publicnode.com",
        "https://api.mainnet-beta.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana",
      status: "active",
      wrapped_native_address: "So11111111111111111111111111111111111111112"
    },
    {
      aliases: [
        "solana-devnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana-devnet.svg",
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Devnet",
      native_asset: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "solana",
        helius: "devnet",
        thegraph: "solana-devnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.devnet.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana_devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana.svg",
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Testnet",
      native_asset: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      native_token_address: null,
      parent: null,
      providers: {
        helius: "testnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.testnet.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solo-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8884571",
      cashaddr_prefix: null,
      chain_id_hex: "0x87915b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://solo-testnet.explorer.caldera.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Solo Testnet",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://solo-testnet.rpc.caldera.xyz/http"
      ],
      short_name: null,
      slip44: null,
      slug: "solo_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5031",
      cashaddr_prefix: null,
      chain_id_hex: "0x13a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://mainnet.somnia.w3us.site",
      family: "evm",
      finality_blocks: null,
      info_url: "https://somnia.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Somnia",
      native_asset: null,
      native_name: "Somnia Mainnet",
      native_token: "SOMI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "somnia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.infra.mainnet.somnia.network"
      ],
      short_name: "SomniaMainnet",
      slip44: null,
      slug: "somnia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "somnia-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:50312",
      cashaddr_prefix: null,
      chain_id_hex: "0xc488",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://somnia-testnet.socialscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://somnia.network",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:5031",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Somnia Testnet",
      native_asset: null,
      native_name: "Somnia Testnet",
      native_token: "STT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://dream-rpc.somnia.network"
      ],
      short_name: "SomniaTestnet",
      slip44: null,
      slug: "somnia_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1868",
      cashaddr_prefix: null,
      chain_id_hex: "0x74c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://soneium.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://soneium.org",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/soneium.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Soneium",
      native_asset: "eip155:1868/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "soneium",
        coingecko_native: "ethereum",
        thegraph: "soneium"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.soneium.org"
      ],
      short_name: "soneium",
      slip44: 60,
      slug: "soneium",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "soneium-minato"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1946",
      cashaddr_prefix: null,
      chain_id_hex: "0x79a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://soneium-minato.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://soneium.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/soneium-minato.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Soneium Testnet Minato",
      native_asset: null,
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "soneium",
        thegraph: "soneium-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.minato.soneium.org"
      ],
      short_name: "soneium-minato",
      slip44: null,
      slug: "soneium_minato",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: null,
      caip2: "eip155:146",
      cashaddr_prefix: null,
      chain_id_hex: "0x92",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sonicscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://soniclabs.com",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sonic.svg",
      mainnet: null,
      metadata: {},
      name: "Sonic",
      native_asset: "eip155:146/slip44:10007",
      native_name: "Sonic",
      native_token: "S",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "sonic",
        coingecko_native: "sonic-3",
        etherscan_scan: "sonicscan.org",
        etherscan_v2: "146",
        evm_rpc: "ingest",
        sourcify: "146",
        thegraph: "sonic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.soniclabs.com"
      ],
      short_name: "sonic",
      slip44: 10007,
      slug: "sonic",
      status: "active",
      wrapped_native_address: "0x039e2fb66102314ce7b64ce5ce3e5183bc94ad38"
    },
    {
      aliases: [
        "sonic-blaze-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: null,
      caip2: "eip155:57054",
      cashaddr_prefix: null,
      chain_id_hex: "0xdede",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://blaze.soniclabs.com",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sonic.svg",
      mainnet: "eip155:146",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sonic Blaze Testnet",
      native_asset: "eip155:57054/slip44:10007",
      native_name: "Sonic",
      native_token: "S",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.blaze.soniclabs.com",
        "https://sonic-blaze-rpc.publicnode.com",
        "wss://sonic-blaze-rpc.publicnode.com"
      ],
      short_name: "blaze",
      slip44: 10007,
      slug: "sonic_blaze_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sonic-legacy-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: null,
      caip2: "eip155:64165",
      cashaddr_prefix: null,
      chain_id_hex: "0xfaa5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.soniclabs.com",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sonic.svg",
      mainnet: "eip155:146",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "hyperlane"
        ]
      },
      name: "Sonic Testnet",
      native_asset: "eip155:64165/slip44:10007",
      native_name: "Sonic",
      native_token: "S",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.soniclabs.com"
      ],
      short_name: null,
      slip44: 10007,
      slug: "sonic_legacy_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sonic-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: null,
      caip2: "eip155:14601",
      cashaddr_prefix: null,
      chain_id_hex: "0x3909",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://testnet.soniclabs.com",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sonic.svg",
      mainnet: "eip155:146",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sonic Testnet",
      native_asset: "eip155:14601/slip44:10007",
      native_name: "Sonic",
      native_token: "S",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "sonic",
        thegraph: "sonic-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.soniclabs.com"
      ],
      short_name: "sonic-testnet",
      slip44: 10007,
      slug: "sonic_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:50104",
      cashaddr_prefix: null,
      chain_id_hex: "0xc3b8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sophon",
      native_asset: null,
      native_name: "Sophon",
      native_token: "SOPH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "sophon"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.sophon.xyz",
        "wss://rpc.sophon.xyz/ws"
      ],
      short_name: "sophon",
      slip44: null,
      slug: "sophon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sophon-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:531050104",
      cashaddr_prefix: null,
      chain_id_hex: "0x1fa72e78",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:50104",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sophon Testnet",
      native_asset: null,
      native_name: "Sophon",
      native_token: "SOPH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.sophon.xyz",
        "wss://rpc.testnet.sophon.xyz/ws"
      ],
      short_name: "sophon-testnet",
      slip44: null,
      slug: "sophon_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:100021",
      cashaddr_prefix: null,
      chain_id_hex: "0x186b5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.sova.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://sova.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Sova",
      native_asset: "eip155:100021/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.sova.io"
      ],
      short_name: "sova",
      slip44: 60,
      slug: "sova",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "space-subnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8227",
      cashaddr_prefix: null,
      chain_id_hex: "0x2023",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets.avax.network/space",
      family: "evm",
      finality_blocks: null,
      info_url: "https://otherworld.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Space Subnet",
      native_asset: null,
      native_name: "FUEL",
      native_token: "FUEL",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "usd-coin"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/space/mainnet/rpc"
      ],
      short_name: "space",
      slip44: null,
      slug: "space_subnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:988",
      cashaddr_prefix: null,
      chain_id_hex: "0x3dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://stablescan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://stable.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/stable.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Stable",
      native_asset: null,
      native_name: "USDT0",
      native_token: "USDT0",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "stable",
        coingecko_native: "tether",
        thegraph: "stable"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.stable.xyz"
      ],
      short_name: "stable",
      slip44: null,
      slug: "stable",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "stable-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2201",
      cashaddr_prefix: null,
      chain_id_hex: "0x899",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.stablescan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://stable.xyz",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/stable.svg",
      mainnet: "eip155:988",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Stable Testnet",
      native_asset: null,
      native_name: "USDT0",
      native_token: "USDT0",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "stable"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.stable.xyz"
      ],
      short_name: "stable-testnet",
      slip44: null,
      slug: "stable_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "stars",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:stargaze-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://stargaze.zone",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "stargaze",
        sources: [
          "cosmos/chain-registry"
        ]
      },
      name: "Stargaze",
      native_asset: "cosmos:stargaze-1/slip44:118",
      native_name: "Stargaze",
      native_token: "STARS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "stargaze",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "starknet:SN_MAIN",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {
        address: "contract"
      },
      explorer_url: "https://voyager.online",
      family: "other",
      finality_blocks: null,
      info_url: "https://www.starknet.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/starknet/caip2",
          "https://docs.starknet.io"
        ]
      },
      name: "Starknet",
      native_asset: "starknet:SN_MAIN/slip44:9004",
      native_name: "Starknet Token",
      native_token: "STRK",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "starknet",
        thegraph: "starknet-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 9004,
      slug: "starknet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "starknet-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "starknet:SN_SEPOLIA",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {
        address: "contract"
      },
      explorer_url: "https://sepolia.voyager.online",
      family: "other",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "starknet:SN_MAIN",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://namespaces.chainagnostic.org/starknet/caip2"
        ]
      },
      name: "Starknet Sepolia",
      native_asset: "starknet:SN_SEPOLIA/slip44:9004",
      native_name: "Starknet Token",
      native_token: "STRK",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        thegraph: "starknet-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 9004,
      slug: "starknet_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "stellar:pubnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 7,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: "asset"
      },
      explorer_url: "https://stellar.expert/explorer/public",
      family: "other",
      finality_blocks: null,
      info_url: "https://stellar.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/stellar/caip2",
          "https://developers.stellar.org"
        ]
      },
      name: "Stellar",
      native_asset: "stellar:pubnet/slip44:148",
      native_name: "Lumen",
      native_token: "XLM",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "stellar",
        thegraph: "stellar"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 148,
      slug: "stellar",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "stellar-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "stellar:testnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 7,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: "asset"
      },
      explorer_url: "https://stellar.expert/explorer/testnet",
      family: "other",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "stellar:pubnet",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://namespaces.chainagnostic.org/stellar/caip2"
        ]
      },
      name: "Stellar Testnet",
      native_asset: "stellar:testnet/slip44:148",
      native_name: "Lumen",
      native_token: "XLM",
      native_token_address: null,
      parent: null,
      providers: {
        thegraph: "stellar-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 148,
      slug: "stellar_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "story-odyssey-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1516",
      cashaddr_prefix: null,
      chain_id_hex: "0x5ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://odyssey-testnet-explorer.storyscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.story.foundation",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Story Odyssey Testnet",
      native_asset: null,
      native_name: "IP",
      native_token: "IP",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://odyssey.storyrpc.io"
      ],
      short_name: "story-testnet",
      slip44: null,
      slug: "story_odyssey_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "story-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1513",
      cashaddr_prefix: null,
      chain_id_hex: "0x5e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.storyscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.story.foundation",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Story Testnet",
      native_asset: null,
      native_name: "IP",
      native_token: "IP",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.storyrpc.io"
      ],
      short_name: "Story",
      slip44: null,
      slug: "story_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "stride",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:stride-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/stride",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://stride.zone",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        chain_name: "stride",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Stride",
      native_asset: "cosmos:stride-1/slip44:118",
      native_name: "Stride",
      native_token: "STRD",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "stride"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "stride",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "stride-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "stride",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:stride-internal-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:stride-1",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "stridetestnet",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Stride Testnet",
      native_asset: "cosmos:stride-internal-1/slip44:118",
      native_name: "Stride",
      native_token: "STRD",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "stride_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "subtensor-evm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:945",
      cashaddr_prefix: null,
      chain_id_hex: "0x3b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm-testscan.dev.opentensor.ai",
      family: "evm",
      finality_blocks: null,
      info_url: "https://bittensor.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/bittensor.svg",
      mainnet: "eip155:964",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Subtensor EVM Testnet",
      native_asset: "eip155:945/slip44:1005",
      native_name: "testTAO",
      native_token: "TAO",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://test.chain.opentensor.ai"
      ],
      short_name: "bittensor-evm-testnet",
      slip44: 1005,
      slug: "subtensor_evm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "sui:mainnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account",
        token: "coin"
      },
      explorer_url: "https://suiscan.xyz/mainnet",
      family: "other",
      finality_blocks: null,
      info_url: "https://sui.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/sui/caip2",
          "https://docs.sui.io"
        ]
      },
      name: "Sui",
      native_asset: "sui:mainnet/slip44:784",
      native_name: "Sui",
      native_token: "SUI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "sui"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 784,
      slug: "sui",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sui-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "sui:testnet",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account",
        token: "coin"
      },
      explorer_url: "https://suiscan.xyz/testnet",
      family: "other",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/sui-testnet.svg",
      mainnet: "sui:mainnet",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://namespaces.chainagnostic.org/sui/caip2"
        ]
      },
      name: "Sui Testnet",
      native_asset: "sui:testnet/slip44:784",
      native_name: "Sui",
      native_token: "SUI",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "sui"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 784,
      slug: "sui_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:55244",
      cashaddr_prefix: null,
      chain_id_hex: "0xd7cc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.superposition.so",
      family: "evm",
      finality_blocks: null,
      info_url: "https://superposition.so",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Superposition",
      native_asset: "eip155:55244/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:42161",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.superposition.so"
      ],
      short_name: "spn",
      slip44: 60,
      slug: "superposition",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "superposition-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:98985",
      cashaddr_prefix: null,
      chain_id_hex: "0x182a9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-explorer.superposition.so",
      family: "evm",
      finality_blocks: null,
      info_url: "https://superposition.so",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:55244",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Superposition Testnet",
      native_asset: "eip155:98985/slip44:60",
      native_name: "SPN",
      native_token: "SPN",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.superposition.so"
      ],
      short_name: "superposition-testnet",
      slip44: 60,
      slug: "superposition_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5330",
      cashaddr_prefix: null,
      chain_id_hex: "0x14d2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.superseed.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.superseed.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/superseed.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Superseed",
      native_asset: "eip155:5330/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "superseed",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.superseed.xyz",
        "wss://mainnet.superseed.xyz"
      ],
      short_name: "sseed",
      slip44: 60,
      slug: "superseed",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "superseed-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:53302",
      cashaddr_prefix: null,
      chain_id_hex: "0xd036",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-explorer.superseed.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.superseed.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/superseed.svg",
      mainnet: "eip155:5330",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Superseed Sepolia Testnet",
      native_asset: "eip155:53302/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "superseed"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.superseed.xyz",
        "wss://sepolia.superseed.xyz"
      ],
      short_name: "seedsep",
      slip44: 60,
      slug: "superseed_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1923",
      cashaddr_prefix: null,
      chain_id_hex: "0x783",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://app.swellnetwork.io/layer2/swell-l2",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Swellchain",
      native_asset: "eip155:1923/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum",
        thegraph: "swellchain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://swell-mainnet.alt.technology",
        "https://rpc.ankr.com/swell"
      ],
      short_name: "swellchain",
      slip44: 60,
      slug: "swellchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "swisstronik-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1291",
      cashaddr_prefix: null,
      chain_id_hex: "0x50b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.swisstronik.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Swisstronik Testnet",
      native_asset: null,
      native_name: "Swisstronik",
      native_token: "SWTR",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://json-rpc.testnet.swisstronik.com"
      ],
      short_name: "swtr-testnet",
      slip44: null,
      slug: "swisstronik_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57",
      cashaddr_prefix: null,
      chain_id_hex: "0x39",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.syscoin.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.syscoin.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Syscoin",
      native_asset: null,
      native_name: "Syscoin",
      native_token: "SYS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.syscoin.org",
        "https://syscoin.public-rpc.com",
        "https://57.rpc.thirdweb.com",
        "https://syscoin-evm.publicnode.com"
      ],
      short_name: "sys",
      slip44: null,
      slug: "syscoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:239",
      cashaddr_prefix: null,
      chain_id_hex: "0xef",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.tac.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://tac.build",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tac.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "TAC",
      native_asset: null,
      native_name: "TAC",
      native_token: "TAC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "tac",
        coingecko_native: "tac-ton-evm"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.tac.build",
        "https://rpc.ankr.com/tac",
        "https://ws.rpc.tac.build"
      ],
      short_name: "tacchain_239-1",
      slip44: null,
      slug: "tac",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tac-saint-petersburg-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2391",
      cashaddr_prefix: null,
      chain_id_hex: "0x957",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://spb.explorer.tac.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://tac.build",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tac.svg",
      mainnet: "eip155:239",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "TAC Saint Petersburg Testnet",
      native_asset: null,
      native_name: "TAC",
      native_token: "TAC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "tac"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://spb.rpc.tac.build",
        "https://rpc.ankr.com/tac_spb",
        "https://spb-ws.rpc.tac.build"
      ],
      short_name: "tacchain_2391-1",
      slip44: null,
      slug: "tac_saint_petersburg_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:167000",
      cashaddr_prefix: null,
      chain_id_hex: "0x28c58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://taikoscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://taiko.xyz",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/taiko.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Taiko",
      native_asset: "eip155:167000/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "taiko",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.taiko.xyz",
        "https://taiko-rpc.publicnode.com",
        "wss://taiko-rpc.publicnode.com"
      ],
      short_name: "tko-mainnet",
      slip44: 60,
      slug: "taiko",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "taiko-holesky"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:167009",
      cashaddr_prefix: null,
      chain_id_hex: "0x28c61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://taiko.xyz",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/taiko.svg",
      mainnet: "eip155:167000",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Taiko Hekla (deprecated) Testnet",
      native_asset: "eip155:167009/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "taiko"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tko-hekla",
      slip44: 60,
      slug: "taiko_holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "taiko-hoodi"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:167013",
      cashaddr_prefix: null,
      chain_id_hex: "0x28c65",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.hoodi.taiko.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://taiko.xyz",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/taiko.svg",
      mainnet: "eip155:167000",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Taiko Hoodi",
      native_asset: "eip155:167013/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.hoodi.taiko.xyz"
      ],
      short_name: "tko-hoodi",
      slip44: 60,
      slug: "taiko_hoodi",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:5845",
      cashaddr_prefix: null,
      chain_id_hex: "0x16d5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.tangle.tools",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.tangle.tools",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tangle",
      native_asset: null,
      native_name: "Tangle",
      native_token: "TNT",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "tangle-network"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.tangle.tools",
        "wss://rpc.tangle.tools"
      ],
      short_name: "tangle",
      slip44: null,
      slug: "tangle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tangle-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3799",
      cashaddr_prefix: null,
      chain_id_hex: "0xed7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet-explorer.tangle.tools",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.tangle.tools",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:5845",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tangle Testnet",
      native_asset: null,
      native_name: "Testnet Tangle Network Token",
      native_token: "tTNT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet-rpc.tangle.tools",
        "https://testnet-rpc-archive.tangle.tools",
        "wss://testnet-rpc.tangle.tools",
        "wss://testnet-rpc-archive.tangle.tools"
      ],
      short_name: "tTangle",
      slip44: null,
      slug: "tangle_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:841",
      cashaddr_prefix: null,
      chain_id_hex: "0x349",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://tara.to",
      family: "evm",
      finality_blocks: null,
      info_url: "https://taraxa.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Taraxa",
      native_asset: "eip155:841/slip44:726",
      native_name: "Tara",
      native_token: "TARA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "tara"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.taraxa.io",
        "https://ws.mainnet.taraxa.io"
      ],
      short_name: "tara",
      slip44: 726,
      slug: "taraxa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:6122",
      cashaddr_prefix: null,
      chain_id_hex: "0x17ea",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.tea.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://tea.xyz",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tea",
      native_asset: null,
      native_name: "Tea",
      native_token: "TEA",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "tea-protocol"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.tea.xyz",
        "https://tea.drpc.org"
      ],
      short_name: "tea",
      slip44: null,
      slug: "tea",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "telos-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:40",
      cashaddr_prefix: null,
      chain_id_hex: "0x28",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://teloscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://telos.net",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Telos EVM",
      native_asset: null,
      native_name: "Telos",
      native_token: "TLOS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "telos"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.telos.net",
        "https://telos.drpc.org",
        "wss://telos.drpc.org"
      ],
      short_name: "TelosEVM",
      slip44: null,
      slug: "telos_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "telos-evm-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41",
      cashaddr_prefix: null,
      chain_id_hex: "0x29",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.teloscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://telos.net",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "eip155:40",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Telos EVM Testnet",
      native_asset: null,
      native_name: "Telos",
      native_token: "TLOS",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.telos.net",
        "https://telos-testnet.drpc.org",
        "wss://telos-testnet.drpc.org"
      ],
      short_name: "TelosEVMTestnet",
      slip44: null,
      slug: "telos_evm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1559",
      cashaddr_prefix: null,
      chain_id_hex: "0x617",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://tenetscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://tenet.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tenet",
      native_asset: null,
      native_name: "TENET",
      native_token: "TENET",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "tenet-1b000f7b-59cb-4e06-89ce-d62b32d362b9"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.tenet.org",
        "https://tenet-evm.publicnode.com",
        "wss://tenet-evm.publicnode.com"
      ],
      short_name: "tenet",
      slip44: null,
      slug: "tenet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: "terra",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:phoenix-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/terra",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.phoenix.money",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "terra2",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Terra",
      native_asset: "cosmos:phoenix-1/slip44:330",
      native_name: "Luna",
      native_token: "LUNA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 330,
      slug: "terra",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "terra-classic"
      ],
      base58_versions: [],
      bech32_hrp: "terra",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:columbus-5",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.terra-classic.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "terra",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Terra Classic",
      native_asset: "cosmos:columbus-5/slip44:330",
      native_name: "Luna Classic",
      native_token: "LUNC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 330,
      slug: "terra_classic",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "terra-classic-testnet"
      ],
      base58_versions: [],
      bech32_hrp: "terra",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:rebel-2",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://www.terra-Classic.money",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "cosmos:columbus-5",
      metadata: {
        as_of: "2026-10-01",
        chain_name: "terratestnet",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Terra Classic Testnet",
      native_asset: "cosmos:rebel-2/slip44:330",
      native_name: "Luna Classic",
      native_token: "LUNC",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 330,
      slug: "terra_classic_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "thorchain-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: "thor",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:thorchain-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        address: "address",
        block: "block",
        token: null,
        tx: "tx"
      },
      explorer_url: "https://thorchain.net",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://thorchain.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "THORChain",
      native_asset: "cosmos:thorchain-1/slip44:931",
      native_name: "Rune",
      native_token: "RUNE",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko: "thorchain",
        coingecko_native: "thorchain",
        cosmos_directory: "https://rest.cosmos.directory/thorchain",
        cosmos_lcd: "https://gateway.liquify.com/chain/thorchain_api"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://gateway.liquify.com/chain/thorchain_api"
      ],
      short_name: null,
      slip44: 931,
      slug: "thorchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tiltyard-mainnet-subnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:710420",
      cashaddr_prefix: null,
      chain_id_hex: "0xad714",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://subnets.avax.network/tiltyard",
      family: "evm",
      finality_blocks: null,
      info_url: "https://play.tiltyard.gg",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Tiltyard Mainnet Subnet",
      native_asset: null,
      native_name: "TILT",
      native_token: "TILT",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://subnets.avax.network/tiltyard/mainnet/rpc"
      ],
      short_name: "tiltyardmainnet",
      slip44: null,
      slug: "tiltyard_mainnet_subnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "toliman-suave-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:33626250",
      cashaddr_prefix: null,
      chain_id_hex: "0x201188a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.toliman.suave.flashbots.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://suave-alpha.flashbots.net/toliman",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Toliman Suave Testnet",
      native_asset: null,
      native_name: "SUAVE Toliman Eth",
      native_token: "TEEth",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.toliman.suave.flashbots.net"
      ],
      short_name: "suave-toliman",
      slip44: null,
      slug: "toliman_suave_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ton:-239"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tvm:-239",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "other",
      finality_blocks: null,
      info_url: "https://ton.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/ton.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "https://namespaces.chainagnostic.org/tvm/caip2",
          "https://docs.ton.org"
        ]
      },
      name: "TON",
      native_asset: "tvm:-239/slip44:607",
      native_name: "Toncoin",
      native_token: "TON",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ton",
        coingecko_native: "the-open-network"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 607,
      slug: "ton",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ton:-3",
        "ton-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tvm:-3",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "other",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/ton.svg",
      mainnet: "tvm:-239",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://namespaces.chainagnostic.org/tvm/caip2"
        ]
      },
      name: "TON Testnet",
      native_asset: "tvm:-3/slip44:607",
      native_name: "Toncoin",
      native_token: "TON",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "ton"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 607,
      slug: "ton_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:21000",
      cashaddr_prefix: null,
      chain_id_hex: "0x5208",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.torus.network",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Torus",
      native_asset: null,
      native_name: "Torus",
      native_token: "TORUS",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "torus"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api-hyperlane.nodes.torus.network",
        "https://api.torus.network"
      ],
      short_name: null,
      slip44: null,
      slug: "torus",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:61166",
      cashaddr_prefix: null,
      chain_id_hex: "0xeeee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://treasurescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://app.treasure.lol",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Treasure",
      native_asset: null,
      native_name: "MAGIC",
      native_token: "MAGIC",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        coingecko_native: "magic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.treasure.lol",
        "wss://rpc.treasure.lol/ws"
      ],
      short_name: "treasure",
      slip44: null,
      slug: "treasure",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "treasure-ruby-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:978657",
      cashaddr_prefix: null,
      chain_id_hex: "0xeeee1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://app.treasure.lol",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Treasure Ruby Testnet",
      native_asset: null,
      native_name: "Testnet MAGIC",
      native_token: "MAGIC",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "treasure-ruby",
      slip44: null,
      slug: "treasure_ruby_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "treasure-topaz-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:978658",
      cashaddr_prefix: null,
      chain_id_hex: "0xeeee2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://topaz.treasurescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://app.treasure.lol",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:61166",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Treasure Topaz Testnet",
      native_asset: null,
      native_name: "Testnet MAGIC",
      native_token: "MAGIC",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.topaz.treasure.lol",
        "wss://rpc.topaz.treasure.lol/ws"
      ],
      short_name: "treasure-topaz",
      slip44: null,
      slug: "treasure_topaz_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "trx",
        "tron-mainnet",
        "tron:0x2b6653dc"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:728126428",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: "https://tron.network",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tron.svg",
      mainnet: null,
      metadata: {},
      name: "TRON",
      native_asset: "tron:728126428/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "tron",
        ccip: "tron",
        coingecko: "tron",
        coingecko_native: "tron",
        tronscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-devnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3360022319",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "tron",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tron.svg",
      mainnet: "tron:728126428",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "https://github.com/smartcontractkit/chain-selectors/blob/main/selectors_tron.yml"
        ]
      },
      name: "TRON Devnet",
      native_asset: "tron:3360022319/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 195,
      slug: "tron_devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-nile",
        "tron-testnet-nile",
        "tron:0xcd8690dc"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3448148188",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://nile.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tron-nile.svg",
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Nile",
      native_asset: "tron:3448148188/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "tron-nile",
        tronscan: "nile"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nile.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron_nile",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-shasta",
        "tron-testnet-shasta",
        "tron:0x94a9059e"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:2494104990",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://shasta.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/tron.svg",
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Shasta",
      native_asset: "tron:2494104990/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      native_token_address: null,
      parent: null,
      providers: {
        tronscan: "shasta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.shasta.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron_shasta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4547",
      cashaddr_prefix: null,
      chain_id_hex: "0x11c3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.trumpchain.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://trumpchain.dev",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "TRUMPCHAIN",
      native_asset: null,
      native_name: "Official Trump",
      native_token: "TRUMP",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "official-trump"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testnet.trumpchain.dev/http",
        "wss://testnet.trumpchain.dev/ws"
      ],
      short_name: "TRUMPCHAIN",
      slip44: null,
      slug: "trumpchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1919",
      cashaddr_prefix: null,
      chain_id_hex: "0x77f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://turkscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://turkscan.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "TurkChain",
      native_asset: null,
      native_name: "TC",
      native_token: "TURK",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.turkscan.com"
      ],
      short_name: "turk",
      slip44: null,
      slug: "turkchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:130",
      cashaddr_prefix: null,
      chain_id_hex: "0x82",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://unichain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://unichain.org",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/unichain.svg",
      mainnet: null,
      metadata: {},
      name: "Unichain",
      native_asset: "eip155:130/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        blockscout: "https://unichain.blockscout.com/api",
        blockscout_v2: "https://unichain.blockscout.com/api/v2",
        ccip: "unichain",
        coingecko_native: "ethereum",
        etherscan_scan: "uniscan.xyz",
        etherscan_v2: "130",
        evm_rpc: "discovery",
        sourcify: "130",
        thegraph: "unichain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.unichain.org"
      ],
      short_name: "unichain",
      slip44: 60,
      slug: "unichain",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "unichain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:1301",
      cashaddr_prefix: null,
      chain_id_hex: "0x515",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://unichain-sepolia.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://unichain.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/unichain.svg",
      mainnet: "eip155:130",
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Unichain Sepolia Testnet",
      native_asset: "eip155:1301/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "unichain",
        thegraph: "unichain-testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.unichain.org",
        "https://unichain-sepolia-rpc.publicnode.com",
        "wss://unichain-sepolia-rpc.publicnode.com"
      ],
      short_name: "unichain-sep",
      slip44: 60,
      slug: "unichain_testnet",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "unicorn-ultra-nebulas-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2484",
      cashaddr_prefix: null,
      chain_id_hex: "0x9b4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.u2uscan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://uniultra.xyz",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Unicorn Ultra Nebulas Testnet",
      native_asset: null,
      native_name: "Unicorn Ultra Nebulas Testnet",
      native_token: "U2U",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc-nebulas-testnet.uniultra.xyz"
      ],
      short_name: "u2u_nebulas",
      slip44: null,
      slug: "unicorn_ultra_nebulas_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "unit-zero"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88811",
      cashaddr_prefix: null,
      chain_id_hex: "0x15aeb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://units.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Unit Zero",
      native_asset: null,
      native_name: "UNIT0",
      native_token: "UNIT0",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "unit0"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.unit0.dev"
      ],
      short_name: "unit0-mainnet",
      slip44: null,
      slug: "unit_zero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "velas-evm"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:106",
      cashaddr_prefix: null,
      chain_id_hex: "0x6a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evmexplorer.velas.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://velas.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Velas EVM",
      native_asset: null,
      native_name: "Velas",
      native_token: "VLX",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://evmexplorer.velas.com/rpc",
        "https://explorer.velas.com/rpc"
      ],
      short_name: "vlx",
      slip44: null,
      slug: "velas_evm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88",
      cashaddr_prefix: null,
      chain_id_hex: "0x58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://viction.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Viction",
      native_asset: null,
      native_name: "Viction",
      native_token: "VIC",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "tomochain",
        thegraph: "viction"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.viction.xyz"
      ],
      short_name: "vic",
      slip44: null,
      slug: "viction",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "warden-protocol"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:8765",
      cashaddr_prefix: null,
      chain_id_hex: "0x223d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.wardenprotocol.org",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "Warden Protocol",
      native_asset: null,
      native_name: "WARD",
      native_token: "WARD",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "warden-2"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://evm.wardenprotocol.org"
      ],
      short_name: null,
      slip44: null,
      slug: "warden_protocol",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1111",
      cashaddr_prefix: null,
      chain_id_hex: "0x457",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.wemix.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://wemix.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/wemix.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "WEMIX3.0",
      native_asset: null,
      native_name: "WEMIX",
      native_token: "WEMIX",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "wemix"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.wemix.com",
        "wss://ws.wemix.com"
      ],
      short_name: "wemix",
      slip44: null,
      slug: "wemix",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "wemix-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1112",
      cashaddr_prefix: null,
      chain_id_hex: "0x458",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://microscope.test.wemix.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://wemix.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/wemix.svg",
      mainnet: "eip155:1111",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "WEMIX3.0 Testnet",
      native_asset: null,
      native_name: "TestnetWEMIX",
      native_token: "tWEMIX",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "wemix"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.test.wemix.com",
        "wss://ws.test.wemix.com"
      ],
      short_name: "twemix",
      slip44: null,
      slug: "wemix_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "world-chain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:480",
      cashaddr_prefix: null,
      chain_id_hex: "0x1e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://worldscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://world.org/world-chain",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/world-chain.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "World Chain",
      native_asset: "eip155:480/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {
        ccip: "world-chain",
        coingecko_native: "ethereum",
        thegraph: "worldchain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://worldchain-mainnet.g.alchemy.com/public",
        "https://480.rpc.thirdweb.com",
        "https://worldchain-mainnet.gateway.tenderly.co",
        "wss://worldchain-mainnet.gateway.tenderly.co"
      ],
      short_name: "wc",
      slip44: 60,
      slug: "world_chain",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "world-chain-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4801",
      cashaddr_prefix: null,
      chain_id_hex: "0x12c1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.worldscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://world.org/world-chain",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/world-chain.svg",
      mainnet: "eip155:480",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "World Chain Sepolia Testnet",
      native_asset: "eip155:4801/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:11155111",
      providers: {
        ccip: "world-chain"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://worldchain-sepolia.g.alchemy.com/public",
        "https://4801.rpc.thirdweb.com",
        "https://worldchain-sepolia.gateway.tenderly.co",
        "wss://worldchain-sepolia.gateway.tenderly.co"
      ],
      short_name: "wcsep",
      slip44: 60,
      slug: "world_chain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "wormhole-gateway"
      ],
      base58_versions: [],
      bech32_hrp: "wormhole",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:wormchain",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {},
      explorer_url: null,
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://wormhole.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "gateway",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "Wormhole Gateway",
      native_asset: "cosmos:wormchain/slip44:118",
      native_name: "Test",
      native_token: "TEST",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "wormhole_gateway",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "x-layer"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:196",
      cashaddr_prefix: null,
      chain_id_hex: "0xc4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.oklink.com/xlayer",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.okx.com/xlayer",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/x-layer.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "X Layer",
      native_asset: null,
      native_name: "X Layer Global Utility Token",
      native_token: "OKB",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "x-layer",
        coingecko_native: "okb",
        thegraph: "xlayer-mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.xlayer.tech",
        "https://xlayerrpc.okx.com"
      ],
      short_name: "okb",
      slip44: null,
      slug: "x_layer",
      status: "active",
      wrapped_native_address: "0xe538905cf8410324e03a5a23c1c177a474d59b2b"
    },
    {
      aliases: [
        "x-layer-sepolia"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:195",
      cashaddr_prefix: null,
      chain_id_hex: "0xc3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://www.oklink.com/zh-hans/x-layer-testnet",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/x-layer-sepolia.svg",
      mainnet: "eip155:196",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "X Layer Testnet(Deprecated)",
      native_asset: null,
      native_name: "X Layer Global Utility Token in testnet(Deprecated)",
      native_token: "OKBDep",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "xlayer",
        thegraph: "xlayer-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tokb-dep",
      slip44: null,
      slug: "x_layer_sepolia",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:660279",
      cashaddr_prefix: null,
      chain_id_hex: "0xa1337",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.xai-chain.net",
      family: "evm",
      finality_blocks: null,
      info_url: "https://xai.games",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Xai",
      native_asset: null,
      native_name: "Xai",
      native_token: "XAI",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "xai-blockchain",
        thegraph: "xai"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://xai-chain.net/rpc"
      ],
      short_name: "xai",
      slip44: null,
      slug: "xai",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:94524",
      cashaddr_prefix: null,
      chain_id_hex: "0x1713c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://xchain-explorer.kuma.bid",
      family: "evm",
      finality_blocks: null,
      info_url: "https://kuma.bid",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "XCHAIN",
      native_asset: "eip155:94524/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://xchain-rpc.kuma.bid"
      ],
      short_name: "xc",
      slip44: 60,
      slug: "xchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:50",
      cashaddr_prefix: null,
      chain_id_hex: "0x32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://xdcscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://xinfin.org",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/xdc.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "XDC Network",
      native_asset: null,
      native_name: "XinFin",
      native_token: "XDC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "xdc"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://erpc.xinfin.network",
        "https://rpc.xinfin.network",
        "https://rpc1.xinfin.network",
        "https://rpc.xdcrpc.com"
      ],
      short_name: "xdc",
      slip44: null,
      slug: "xdc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdc-apothem-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:51",
      cashaddr_prefix: null,
      chain_id_hex: "0x33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.xdcscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://xinfin.org",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/xdc.svg",
      mainnet: "eip155:50",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "XDC Apothem Network Testnet",
      native_asset: null,
      native_name: "XinFin",
      native_token: "TXDC",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "xdc"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apothem.network",
        "https://erpc.apothem.network",
        "https://apothem.xdcrpc.com"
      ],
      short_name: "txdc",
      slip44: null,
      slug: "xdc_apothem_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xlayer-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1952",
      cashaddr_prefix: null,
      chain_id_hex: "0x7a0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://web3.okx.com/explorer/x-layer-testnet",
      family: "evm",
      finality_blocks: null,
      info_url: "https://web3.okx.com/xlayer",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/xlayer-testnet.svg",
      mainnet: "eip155:196",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "X Layer Testnet",
      native_asset: null,
      native_name: "X Layer Global Utility Token in testnet",
      native_token: "OKB",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "xlayer"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://testrpc.xlayer.tech",
        "https://xlayertestrpc.okx.com"
      ],
      short_name: "tokb",
      slip44: null,
      slug: "xlayer_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:37",
      cashaddr_prefix: null,
      chain_id_hex: "0x25",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "hyperlane"
        ]
      },
      name: "XPLA",
      native_asset: null,
      native_name: "XPLA",
      native_token: "XPLA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "xpla"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://dimension-evm-rpc.xpla.dev"
      ],
      short_name: null,
      slip44: null,
      slug: "xpla",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xpla-cosmos"
      ],
      base58_versions: [],
      bech32_hrp: "xpla",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:dimension_37-1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 18,
      explorer_paths: {
        address: "accounts",
        token: null,
        tx: "transactions"
      },
      explorer_url: "https://www.mintscan.io/xpla",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://xpla.io",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        chain_name: "xpla",
        sources: [
          "cosmos.directory"
        ]
      },
      name: "XPLA",
      native_asset: "cosmos:dimension_37-1/slip44:60",
      native_name: "Xpla",
      native_token: "XPLA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "xpla_cosmos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl",
        "ripple",
        "xrp-mainnet",
        "xrpl-mainnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:0",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      info_url: "https://xrpl.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "XRP Ledger",
      native_asset: "xrpl:0/slip44:144",
      native_name: "XRP",
      native_token: "XRP",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "ripple",
        coingecko: "xrp",
        coingecko_native: "ripple",
        xrpscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s1.ripple.com:51234",
        "https://s2.ripple.com:51234"
      ],
      short_name: null,
      slip44: 144,
      slug: "xrp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrp-testnet",
        "xrpl-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:1",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://testnet.xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "xrpl:0",
      metadata: {},
      name: "XRP Ledger Testnet",
      native_asset: "xrpl:1/slip44:144",
      native_name: "XRP",
      native_token: "XRP",
      native_token_address: null,
      parent: null,
      providers: {
        xrpscan: "testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s.altnet.rippletest.net:51234"
      ],
      short_name: null,
      slip44: 144,
      slug: "xrp_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl-evm-sidechain"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1440000",
      cashaddr_prefix: null,
      chain_id_hex: "0x15f900",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.xrplevm.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://xrplevm.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "XRPL EVM Sidechain",
      native_asset: null,
      native_name: "XRP",
      native_token: "XRP",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ripple"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.xrplevm.org",
        "wss://ws.xrplevm.org"
      ],
      short_name: "xrplevm",
      slip44: null,
      slug: "xrpl_evm_sidechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:261131",
      cashaddr_prefix: null,
      chain_id_hex: "0x3fc0b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "layerzero"
        ]
      },
      name: "Zama",
      native_asset: "eip155:261131/slip44:60",
      native_name: null,
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 60,
      slug: "zama",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zcash-mainnet",
        "zec"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 75,
      brand_color: "#F4B728",
      caip2: "bip122:00040fe8ec8471911baa1db1266ea15d",
      cashaddr_prefix: null,
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/zcash",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://z.cash",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Zcash",
      native_asset: "bip122:00040fe8ec8471911baa1db1266ea15d/slip44:133",
      native_name: "Zcash",
      native_token: "ZEC",
      native_token_address: null,
      parent: null,
      providers: {
        blockchair: "zcash",
        blockchair_api: "zcash",
        coingecko_native: "zcash"
      },
      reorg_depth: 24,
      rpc_urls: [],
      short_name: null,
      slip44: 133,
      slug: "zcash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zero-network"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:543210",
      cashaddr_prefix: null,
      chain_id_hex: "0x849ea",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zero.network",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.zero.network",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "ZERO Network",
      native_asset: "eip155:543210/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.zerion.io/v1/zero"
      ],
      short_name: "zero-network",
      slip44: 60,
      slug: "zero_network",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7000",
      cashaddr_prefix: null,
      chain_id_hex: "0x1b58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zetachain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zetachain.com/docs",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/zetachain.svg",
      mainnet: null,
      metadata: {},
      name: "ZetaChain",
      native_asset: "eip155:7000/slip44:7000",
      native_name: "Zeta",
      native_token: "ZETA",
      native_token_address: null,
      parent: null,
      providers: {
        coingecko_native: "zetachain",
        thegraph: "zetachain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zetachain-mainnet",
      slip44: 7e3,
      slug: "zetachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7001",
      cashaddr_prefix: null,
      chain_id_hex: "0x1b59",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.zetascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zetachain.com/docs",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/zetachain.svg",
      mainnet: "eip155:7000",
      metadata: {},
      name: "Zetachain Testnet",
      native_asset: "eip155:7001/slip44:7000",
      native_name: "Zeta",
      native_token: "ZETA",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zetachain-testnet",
      slip44: 7e3,
      slug: "zetachain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:48900",
      cashaddr_prefix: null,
      chain_id_hex: "0xbf04",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.zircuit.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/zircuit.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Zircuit",
      native_asset: "eip155:48900/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "zircuit",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.zircuit.com"
      ],
      short_name: "zircuit-mainnet",
      slip44: 60,
      slug: "zircuit",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zircuit-garfield-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:48898",
      cashaddr_prefix: null,
      chain_id_hex: "0xbf02",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.zircuit.com",
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Zircuit Garfield Testnet",
      native_asset: null,
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://garfield-testnet.zircuit.com"
      ],
      short_name: "zircuit-garfield-testnet",
      slip44: null,
      slug: "zircuit_garfield_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zircuit-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:48899",
      cashaddr_prefix: null,
      chain_id_hex: "0xbf03",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.zircuit.com",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/zircuit.svg",
      mainnet: "eip155:48900",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Zircuit Testnet",
      native_asset: "eip155:48899/slip44:60",
      native_name: "ETH",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zircuit-testnet",
      slip44: 60,
      slug: "zircuit_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zklink-nova"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:810180",
      cashaddr_prefix: null,
      chain_id_hex: "0xc5cc4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zklink.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zklink.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "zkLink Nova",
      native_asset: "eip155:810180/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:59144",
      providers: {
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.zklink.io",
        "wss://rpc.zklink.io"
      ],
      short_name: "zklink-nova",
      slip44: 60,
      slug: "zklink_nova",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zklink-nova-sepolia-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:810181",
      cashaddr_prefix: null,
      chain_id_hex: "0xc5cc5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zklink.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zklink.io",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:810180",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "zkLink Nova Sepolia Testnet",
      native_asset: "eip155:810181/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:59141",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.rpc.zklink.io",
        "wss://sepolia.rpc.zklink.io"
      ],
      short_name: "zklink-nova-sepolia",
      slip44: 60,
      slug: "zklink_nova_sepolia_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zksync",
        "zksync-era",
        "era"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:324",
      cashaddr_prefix: null,
      chain_id_hex: "0x144",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-era.svg",
      mainnet: null,
      metadata: {},
      name: "zkSync Era",
      native_asset: "eip155:324/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: "0x000000000000000000000000000000000000800a",
      parent: "eip155:1",
      providers: {
        alchemy: "zksync-mainnet",
        arkham: "zksync_era",
        ccip: "zksync",
        coingecko: "zksync",
        coingecko_native: "ethereum",
        defillama: "era",
        dexscreener: "zksync",
        infura: "zksync-mainnet",
        sourcify: "324",
        thegraph: "zksync-era"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.era.zksync.io"
      ],
      short_name: "zksync",
      slip44: 60,
      slug: "zksync_era",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:300",
      cashaddr_prefix: null,
      chain_id_hex: "0x12c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-sepolia.svg",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Sepolia",
      native_asset: "eip155:300/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: "0x000000000000000000000000000000000000800a",
      parent: "eip155:11155111",
      providers: {
        ccip: "zksync",
        thegraph: "zksync-era-sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zksync-sepolia",
      slip44: 60,
      slug: "zksync_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:280",
      cashaddr_prefix: null,
      chain_id_hex: "0x118",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-era.svg",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Goerli",
      native_asset: "eip155:280/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zksync-goerli",
      slip44: 60,
      slug: "zksync_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1408",
      cashaddr_prefix: null,
      chain_id_hex: "0x580",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "layerzero"
        ]
      },
      name: "zkVerify",
      native_asset: null,
      native_name: null,
      native_token: "VFY",
      native_token_address: null,
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://vflow-rpc.zkverify.io"
      ],
      short_name: null,
      slip44: null,
      slug: "zkverify",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7777777",
      cashaddr_prefix: null,
      chain_id_hex: "0x76adf1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zora.energy",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zora.energy",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/zora.svg",
      mainnet: null,
      metadata: {
        as_of: "2026-10-02",
        sources: [
          "chainid.network"
        ]
      },
      name: "Zora",
      native_asset: "eip155:7777777/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "zora",
        coingecko_native: "ethereum",
        thegraph: "zora"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.zora.energy"
      ],
      short_name: "zora",
      slip44: 60,
      slug: "zora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zora-testnet"
      ],
      base58_versions: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:999999999",
      cashaddr_prefix: null,
      chain_id_hex: "0x3b9ac9ff",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zora.energy",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zora.energy",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/zora.svg",
      mainnet: "eip155:7777777",
      metadata: {
        as_of: "2026-10-01",
        sources: [
          "chainid.network"
        ]
      },
      name: "Zora Sepolia Testnet",
      native_asset: "eip155:999999999/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      native_token_address: null,
      parent: null,
      providers: {
        ccip: "zora"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.rpc.zora.energy"
      ],
      short_name: "zsep",
      slip44: 60,
      slug: "zora_testnet",
      status: "active",
      wrapped_native_address: null
    }
  ],
  provider_keys: {
    alchemy: "Alchemy network subdomain: https://{id}.g.alchemy.com/v2/<key>",
    ankr: "Ankr RPC path: https://rpc.ankr.com/{id}/<key>",
    arkham: "Arkham chain name",
    blockchair: "Blockchair chain name",
    blockchair_api: "Blockchair API chain (UTXO ingest alongside xmrchain)",
    blockscan: "Blockscan chain id",
    blockscout: "Blockscout Etherscan-compatible API base (ABIs, source)",
    blockscout_v2: "Blockscout v2 API base that ingests the chain's transactions",
    blocksec: "BlockSec (MetaSleuth API) chain id",
    ccip: "Chainlink CCIP icon id: the CCIP CDN filename stem of the chain's logo (/bridge/icons/networks/{id}.svg). The logo seeder mirrors it to the chain's `logo` key for every chain carrying it. A testnet often shares its mainnet's artwork, so the value is shared; CCIP's own network name for a chain is the `ccip_network` chain code",
    codeslaw: "Codeslaw platform name",
    coingecko: "CoinGecko asset platform id",
    coingecko_native: "CoinGecko coin id of the chain's native (gas) coin \u2014 what prices its native transfers. Measured per chain, not derived from the platform: CoinGecko's own platform \u2192 native-coin map names AURORA for Aurora and wrapped SEI for Sei, whose gas is ETH and SEI",
    coinmarketcap: "CoinMarketCap DEX API platform name (the `platform` of /v1/dex/token/price)",
    cosmos_directory: "cosmos.directory LCD proxy base URL, the Cosmos fallback",
    cosmos_lcd: "Cosmos SDK LCD (REST) base URL, first choice (Cosmos ingest)",
    cowswap: "CoW Protocol orderbook network: the path segment of https://api.cow.fi/{id}/api/v1/\u2026 (app-data documents, trades). CoW serves a chain exactly when it carries this",
    defillama: "DefiLlama chain name",
    dexscreener: "DexScreener chain id",
    esplora: "Esplora network (UTXO ingest)",
    etherscan_scan: "Etherscan-family explorer host scraped for labels",
    etherscan_v2: "Etherscan API V2 chainid (decimal) the chain's transactions are read through by hash, on the platform's Etherscan key. The Etherscan route ingests a chain carrying it that no Moralis / Blockscout / JSON-RPC route claims first",
    evm_rpc: "'ingest' (transactions read over the chain's JSON-RPC) or 'discovery' (block windows found over it, transactions read through another route)",
    helius: "Helius cluster (Solana ingest)",
    hyperliquid: "Hyperliquid network (HyperCore ingest)",
    infura: "Infura network subdomain: https://{id}.infura.io/v3/<key>",
    metasleuth: "MetaSleuth chain name",
    moralis: "Moralis chain name; the Moralis ingest route reads every chain carrying it",
    moralis_wallet: "Moralis chain name, where Moralis's mainnet-only WALLET endpoints (wallet chains / history / net worth) accept the chain \u2014 measured per chain, because Moralis's idea of mainnet is not the registry's (it refuses Moonriver)",
    near_rpc: "NEAR archival RPC URL (NEAR ingest)",
    scanscope: "ScanScope chain name",
    sourcify: "Sourcify chain id",
    thegraph: "The Graph network id (`mainnet`, `arbitrum-one`, `matic`) \u2014 the `network` a subgraph is deployed on, as The Graph networks registry names it; the provider's subgraph table is keyed by it",
    tronscan: "TronScan network (TRON ingest)",
    xmrchain: "xmrchain onion-explorer network (Monero ingest)",
    xrpscan: "XRPScan network (XRP Ledger ingest)"
  }
};

// src/assets/naming.ts
var ASSET_FORMATS = ["svg", "webp", "png", "jpg", "gif", "avif"];
var ASSET_ROOT = "logos";
var CDN_ASSET_ROOT = "public/GET/cdn";
var PRIVATE_ASSET_ROOT = "assets/private";
var ALLOWED_ASSET_ROOTS = [ASSET_ROOT, CDN_ASSET_ROOT, PRIVATE_ASSET_ROOT];
var AssetNamingError = class extends Error {
  static {
    brandClass(this, "assets/AssetNamingError");
  }
  constructor(message) {
    super(message);
    this.name = "AssetNamingError";
  }
};
var MAX_SEGMENT = 80;
function isAssetFormat(value) {
  return typeof value === "string" && ASSET_FORMATS.includes(value);
}
function assetFormatFromExtension(pathOrExt) {
  const ext = pathOrExt.split(".").pop()?.toLowerCase().replace("jpeg", "jpg");
  return isAssetFormat(ext) ? ext : void 0;
}
function assertValidAssetKey(key) {
  if (!key || key.startsWith("/") || key.includes("\\") || key.includes("..") || key.includes("//")) {
    throw new AssetNamingError(`Invalid asset key "${key}"`);
  }
  if (!ALLOWED_ASSET_ROOTS.some((root) => key.startsWith(`${root}/`))) {
    const roots = ALLOWED_ASSET_ROOTS.map((r) => `"${r}/"`).join(" or ");
    throw new AssetNamingError(`Asset key "${key}" must start with ${roots}`);
  }
  if (!assetFormatFromExtension(key)) {
    throw new AssetNamingError(`Asset key "${key}" must end in one of: ${ASSET_FORMATS.join(", ")}`);
  }
  if (new TextEncoder().encode(key).length > 1024) {
    throw new AssetNamingError("Asset key exceeds S3's 1024-byte limit");
  }
  if (key.startsWith(`${ASSET_ROOT}/`)) {
    for (const segment of key.split("/")) {
      if (segment.length > MAX_SEGMENT) {
        throw new AssetNamingError(`Asset key segment "${segment}" exceeds ${MAX_SEGMENT} chars`);
      }
    }
  }
}
function assetUrl(key, opts) {
  assertValidAssetKey(key);
  const base = opts.cdnBase.replace(/\/+$/, "");
  return `${base}/${key.split("/").map(encodeURIComponent).join("/")}`;
}

// src/assets/public-cdn.ts
var publicCdn = globalSingleton("assets/public-cdn.v1", () => ({
  base: null
}));
function publicAssetUrl(key) {
  const base = publicCdn.base;
  const trimmed = key?.trim().replace(/^\/+/, "");
  if (!base || !trimmed) return null;
  try {
    return assetUrl(trimmed, { cdnBase: base });
  } catch (err) {
    if (err instanceof AssetNamingError) return null;
    throw err;
  }
}

// src/chains/model.ts
var ChainStatus = {
  /** Offered in pickers and processed. */
  ACTIVE: "active"};
var EXPLORER_LINK_TYPES = ["tx", "address", "block", "token"];
var ChainWireSchema = z.object({
  caip2: z.string().min(1),
  slug: z.string().min(1),
  chain_id_hex: z.string().nullable().default(null),
  family: z.string().refine(isChainFamily, { message: "unknown chain family" }),
  aliases: z.array(z.string()).default([]),
  name: z.string().min(1),
  short_name: z.string().nullable().default(null),
  native_token: z.string().min(1),
  native_name: z.string().nullable().default(null),
  // Required, like `reorg_depth`: Python always dumps it, and a default here
  // would be the FE guessing a chain's scale.
  decimals: z.number().int().min(0),
  slip44: z.number().int().min(0).nullable().default(null),
  native_asset: z.string().nullable().default(null),
  wrapped_native_address: z.string().nullable().default(null),
  native_token_address: z.string().nullable().default(null),
  status: z.enum(["active", "deprecated", "disabled"]).default("active"),
  is_testnet: z.boolean().default(false),
  mainnet: z.string().nullable().default(null),
  layer: z.enum(["l1", "l2", "sidechain"]).nullable().default(null),
  parent: z.string().nullable().default(null),
  // Required: rows arrive resolved (Python fills the mainnet's / family's depth).
  reorg_depth: z.number().int().min(0),
  block_time_seconds: z.number().positive().nullable().default(null),
  finality_blocks: z.number().int().positive().nullable().default(null),
  explorer_url: z.string().nullable().default(null),
  explorer_paths: z.record(z.string(), z.string().nullable()).default({}),
  rpc_urls: z.array(z.string()).default([]),
  providers: z.record(z.string(), z.string()).default({}),
  bech32_hrp: z.string().nullable().default(null),
  base58_versions: z.array(z.number().int().min(0).max(255)).default([]),
  cashaddr_prefix: z.string().nullable().default(null),
  brand_color: z.string().nullable().default(null),
  logo: z.string().nullable().default(null),
  info_url: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
function foldAliases(aliases) {
  const seen = /* @__PURE__ */ new Set();
  for (const raw of aliases ?? []) {
    const alias = raw.trim().toLowerCase();
    if (alias) seen.add(alias);
  }
  return [...seen];
}
function httpsOrNull(url) {
  const trimmed = url?.trim();
  return trimmed && /^https:\/\/[^\s]+$/i.test(trimmed) ? trimmed : null;
}
function explorerPathsOf(raw) {
  const out = {};
  for (const type of EXPLORER_LINK_TYPES) if (type in raw) out[type] = raw[type] ?? null;
  return out;
}
function chainFromWire(row, mainnetSlugOf) {
  const wire = ChainWireSchema.parse(row);
  const logo = wire.logo?.trim() || null;
  return Object.freeze({
    caip2: wire.caip2,
    slug: wire.slug,
    chainIdHex: wire.chain_id_hex,
    family: wire.family,
    aliases: Object.freeze(foldAliases(wire.aliases)),
    name: wire.name,
    shortName: wire.short_name,
    nativeToken: wire.native_token,
    nativeName: wire.native_name,
    decimals: wire.decimals,
    slip44: wire.slip44,
    nativeAsset: wire.native_asset,
    wrappedNativeAddress: wire.wrapped_native_address,
    nativeTokenAddress: wire.native_token_address,
    status: wire.status,
    isTestnet: wire.is_testnet,
    mainnet: wire.mainnet,
    mainnetSlug: wire.mainnet ? mainnetSlugOf?.(wire.mainnet) ?? null : null,
    layer: wire.layer,
    parent: wire.parent,
    reorgDepth: wire.reorg_depth,
    blockTimeSec: wire.block_time_seconds,
    finalityBlocks: wire.finality_blocks,
    explorerUrl: wire.explorer_url ? wire.explorer_url.replace(/\/+$/, "") : null,
    explorerPaths: Object.freeze(explorerPathsOf(wire.explorer_paths)),
    rpcUrls: Object.freeze([...wire.rpc_urls]),
    providers: Object.freeze({ ...wire.providers }),
    bech32Hrp: wire.bech32_hrp,
    base58Versions: Object.freeze([...wire.base58_versions]),
    cashaddrPrefix: wire.cashaddr_prefix,
    brandColor: wire.brand_color,
    logo,
    // A getter, not a value: the bundled rows are parsed when the module loads,
    // before the app's bootstrap installs the CDN origin.
    get logoUrl() {
      return publicAssetUrl(logo);
    },
    infoUrl: httpsOrNull(wire.info_url),
    metadata: Object.freeze({ ...wire.metadata })
  });
}
function isActiveChain(chain) {
  return chain.status === ChainStatus.ACTIVE;
}

// src/chains/catalogue.ts
var CatalogueSchema = z.object({
  chains: z.array(ChainWireSchema).default([]),
  provider_keys: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = CatalogueSchema.parse(catalogue_generated_default);
var BUNDLED_SLUG_BY_CAIP2 = new Map(
  SNAPSHOT.chains.map((row) => [row.caip2, row.slug])
);
new Map(
  catalogue_generated_default.chains.map((row) => [String(row.caip2), Object.freeze({ ...row })])
);
var BUNDLED_CHAINS = Object.freeze(
  SNAPSHOT.chains.map((row) => chainFromWire(row, (caip2) => BUNDLED_SLUG_BY_CAIP2.get(caip2)))
);
Object.freeze({
  ...SNAPSHOT.provider_keys
});

// src/chains/registry.ts
var log = createLogger("chains:registry");
function hexKey(value) {
  let token = value.trim().toLowerCase();
  if (!token) return null;
  if (!token.startsWith("0x")) token = `0x${token}`;
  try {
    return `0x${BigInt(token).toString(16)}`;
  } catch {
    return null;
  }
}
var ChainRegistry = class {
  byCaip2 = /* @__PURE__ */ new Map();
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  // Alternate spellings; consulted by `resolve` only after the canonical
  // lookups, so an alias can never shadow a caip2 / hex / slug.
  byAlias = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  revision = 0;
  batchDepth = 0;
  pending = false;
  sorted = null;
  constructor(chains = []) {
    for (const chain of chains) this.register(chain);
    this.pending = false;
  }
  /**
   * Changes whenever the answer to any lookup may have changed — a register,
   * an unregister, a hydration. What `useSyncExternalStore` snapshots.
   */
  get version() {
    return this.revision;
  }
  /**
   * Add `chain`; `replace: true` overwrites the row with the same CAIP-2 id.
   *
   * Throws when the chain's slug or hex id already names a DIFFERENT chain —
   * two rows can never share a spelling, with or without `replace` (Python's
   * `ChainRegistry.register` refuses the same).
   */
  register(chain, { replace = false } = {}) {
    const caip2 = normalizeCaip2(chain.caip2);
    const existing = this.byCaip2.get(caip2);
    if (existing && existing !== chain && !replace) {
      throw new Error(`Chain ${caip2} is already registered; pass replace=true to overwrite.`);
    }
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    const slugOwner = this.bySlug.get(chain.slug);
    if (slugOwner && normalizeCaip2(slugOwner.caip2) !== caip2) {
      throw new Error(`Chain slug "${chain.slug}" of ${caip2} already names ${slugOwner.caip2}.`);
    }
    const hexOwner = hex ? this.byHex.get(hex) : void 0;
    if (hexOwner && normalizeCaip2(hexOwner.caip2) !== caip2) {
      throw new Error(`Chain hex ${hex} of ${caip2} already names ${hexOwner.caip2}.`);
    }
    if (existing) this.drop(existing);
    this.byCaip2.set(caip2, chain);
    this.bySlug.set(chain.slug, chain);
    if (hex) this.byHex.set(hex, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
    this.touch();
  }
  /** Remove and return the chain `value` resolves to (any spelling). */
  unregister(value) {
    const chain = this.resolve(value);
    if (!chain) return null;
    this.drop(chain);
    this.touch();
    return chain;
  }
  drop(chain) {
    this.byCaip2.delete(normalizeCaip2(chain.caip2));
    if (this.bySlug.get(chain.slug) === chain) this.bySlug.delete(chain.slug);
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    if (hex && this.byHex.get(hex) === chain) this.byHex.delete(hex);
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  /** Drop every registered chain (test-only). */
  clear() {
    this.byCaip2.clear();
    this.bySlug.clear();
    this.byHex.clear();
    this.byAlias.clear();
    this.touch();
  }
  /**
   * Run `fn` and notify subscribers ONCE at the end, however many rows it
   * changed — a hydration of a hundred rows is one re-render, not a hundred.
   */
  batch(fn) {
    this.batchDepth += 1;
    try {
      return fn();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.pending) {
        this.pending = false;
        this.emit();
      }
    }
  }
  /** Call `listener` after every change; returns the unsubscribe. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  touch() {
    this.revision += 1;
    this.sorted = null;
    if (this.batchDepth > 0) this.pending = true;
    else this.emit();
  }
  emit() {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        log.error("chain registry listener threw", { err });
      }
    }
  }
  /** The chain with this slug (case-folded), or null. */
  get(slug) {
    return this.bySlug.get((slug ?? "").trim().toLowerCase()) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes, lowercases, drops leading zeros). */
  getByHex(chainIdHex) {
    const key = hexKey(chainIdHex ?? "");
    return key ? this.byHex.get(key) ?? null : null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"); Base58 references are case-sensitive. */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2 ?? "")) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire. Every status
   * resolves.
   */
  resolve(value) {
    if (value == null) return null;
    const token = String(value).trim();
    if (!token) return null;
    if (token.includes(":")) {
      const hit = this.getByCaip2(token);
      if (hit) return hit;
    }
    const folded = token.toLowerCase();
    if (folded.startsWith("0x")) {
      const hit = this.getByHex(folded);
      if (hit) return hit;
    } else if (/^\d+$/.test(folded)) {
      const hit = this.byHex.get(`0x${BigInt(folded).toString(16)}`);
      if (hit) return hit;
    }
    return this.bySlug.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  /** Every registered chain, whatever its status, sorted by slug. A fresh array. */
  all() {
    this.sorted ??= Object.freeze(
      [...this.byCaip2.values()].sort((a, b) => a.slug.localeCompare(b.slug))
    );
    return [...this.sorted];
  }
  /** The chains offered for new work (status `active`), sorted by slug. */
  active() {
    return this.all().filter(isActiveChain);
  }
  byFamily(family) {
    return this.all().filter((c) => c.family === family);
  }
};
var registry = globalSingleton(
  "chains/registry.v2",
  () => new ChainRegistry(BUNDLED_CHAINS)
);
globalSingleton("chains/hydrated-only.v1", () => /* @__PURE__ */ new Set());
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2;
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
function hasUtxoAddressFormat(format) {
  return Boolean(format.bech32Hrp || format.base58Versions.length || format.cashaddrPrefix);
}
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function convertBits(data) {
  let bits = 0, accumulator = 0;
  const out = [];
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push(accumulator >> bits & 255);
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? out : null;
}
function validSegwitAddress(address, hrp) {
  const decoded = decodeBech32(address);
  if (!decoded || decoded.hrp !== hrp || !decoded.data.length) return false;
  const version = decoded.data[0];
  if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
  const length = convertedLength(decoded.data.slice(1));
  return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
}
var CASHADDR_GENERATORS = [0x98f2bc8e61n, 0x79b76d99e2n, 0xf33e5fb3c4n, 0xae2eabe2a8n, 0x1e4f43e470n];
var CASHADDR_HASH_BYTES = [20, 24, 28, 32, 40, 48, 56, 64];
function validCashAddr(address, prefix) {
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return false;
  let body = lower;
  const colon = lower.indexOf(":");
  if (colon >= 0) {
    if (lower.slice(0, colon) !== prefix) return false;
    body = lower.slice(colon + 1);
  }
  const data = [...body].map((c) => BECH32.indexOf(c));
  if (data.length < 9 || data.some((v) => v < 0)) return false;
  let checksum = 1n;
  for (const value of [...[...prefix].map((c) => c.charCodeAt(0) & 31), 0, ...data]) {
    const top = checksum >> 35n;
    checksum = (checksum & 0x07ffffffffn) << 5n ^ BigInt(value);
    CASHADDR_GENERATORS.forEach((generator, bit) => {
      if (top >> BigInt(bit) & 1n) checksum ^= generator;
    });
  }
  if ((checksum ^ 1n) !== 0n) return false;
  const payload = convertBits(data.slice(0, -8));
  if (!payload?.length) return false;
  const version = payload[0];
  return (version & 128) === 0 && payload.length - 1 === CASHADDR_HASH_BYTES[version & 7];
}
function validUtxoAddress(address, format) {
  if (address.length < 14 || address.length > 90) return false;
  const folded = address.toLowerCase();
  if (format.bech32Hrp && folded.startsWith(`${format.bech32Hrp}1`)) return validSegwitAddress(address, format.bech32Hrp);
  if (format.cashaddrPrefix && (folded.startsWith(`${format.cashaddrPrefix}:`) || /^[qp]/i.test(address))) {
    return validCashAddr(address, format.cashaddrPrefix);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && format.base58Versions.includes(payload[0]);
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|ltc1|tltc1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var utxoRows = globalSingleton("chains/utxoFormatRows", () => ({
  version: -1,
  rows: []
}));
function utxoFormatRows() {
  if (utxoRows.version !== registry.version) {
    utxoRows.rows = registry.all().filter((chain) => chain.family === ChainFamily.UTXO && hasUtxoAddressFormat(chain));
    utxoRows.version = registry.version;
  }
  return utxoRows.rows;
}
function validStatedUtxoAddress(address) {
  return utxoFormatRows().some((chain) => validUtxoAddress(address, chain));
}
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validStatedUtxoAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === ChainFamily.COSMOS && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function shortenAddress(address, lengths) {
  return truncateMiddle(address, { ...getTruncation("address"), ...lengths });
}
var TokenFlowSchema = z.object({ "kind": z.enum(["native", "internal", "closure", "erc20", "trc20", "nep141", "erc721", "erc1155", "spl", "hip1", "metaprotocol", "cosmos_bank"]), "token_address": z.union([z.string(), z.null()]).default(null), "from_address": z.union([z.string(), z.null()]).default(null), "to_address": z.union([z.string(), z.null()]).default(null), "raw_amount": z.string().default("0"), "token_id": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('One value movement inside a transaction (native / internal / ERC-20 / NFT).\n\nCarries the raw on-chain amount plus the token contract so a later step can\nprice it; ``token_address is None`` means the chain\'s native asset. For a\nnon-fungible (``erc721``) or multi-token (``erc1155``) move, ``token_id``\ncarries the moved id and ``raw_amount`` is the count (``"1"`` for ERC-721).');
z.object({ "address": z.string(), "token_address": z.union([z.string(), z.null()]).default(null), "raw_delta": z.string().default("0"), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('Net per-(address, token) delta across a transaction\'s flows.\n\nThe "net flow" a tx-table shows next to a complex DeFi tx: positive\n``raw_delta`` is a net inflow to ``address``, negative a net outflow.');
z.object({ "token_address": z.string(), "chain_id": z.string(), "symbol": z.union([z.string(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "block_number": z.union([z.number().int(), z.null()]).default(null), "block_timestamp": z.union([z.number().int(), z.null()]).default(null), "source": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).describe("A resolved token price, optionally as-of a specific block.\n\nProvider-agnostic: a price source (Moralis today, others later) maps its\npayload onto this shape. ``confidence`` lets a multi-source resolver pick or\nblend.");

// src/transpose/types.ts
var NATIVE_COIN_FLOW_KINDS = /* @__PURE__ */ new Set([
  "native",
  "internal",
  "closure"
]);
var NON_FUNGIBLE_FLOW_KINDS = /* @__PURE__ */ new Set([
  "erc721",
  "erc1155"
]);
new Set(
  TokenFlowSchema.shape.kind.options.filter((kind) => !NATIVE_COIN_FLOW_KINDS.has(kind))
);
var text = (value) => typeof value === "string" && value.trim() ? value.trim() : void 0;
var unit = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1 ? value : void 0;
var seconds = (value) => typeof value === "number" && Number.isFinite(value) && value > 0 ? value : void 0;
var strings = (value) => Array.isArray(value) ? value.flatMap((item) => text(item) ? [text(item)] : []) : [];
var httpUrl = (value) => {
  const raw = text(value);
  if (!raw) return void 0;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : void 0;
  } catch {
    return void 0;
  }
};
var evidenceSchema = z.object({
  source: z.object({ kind: z.unknown(), id: z.unknown(), url: z.unknown() }).partial().passthrough().nullish(),
  confidence: z.unknown(),
  reasons: z.unknown(),
  signals: z.unknown(),
  asserted_fields: z.unknown(),
  asserted_at: z.unknown(),
  last_seen_at: z.unknown()
}).partial().passthrough();
function parseEvidence(raw) {
  const parsed = evidenceSchema.safeParse(raw);
  if (!parsed.success) return void 0;
  const { source, confidence, reasons, signals, asserted_fields, asserted_at, last_seen_at } = parsed.data;
  const sourceKind = text(source?.kind);
  const sourceId = text(source?.id);
  const url = httpUrl(source?.url);
  const signalNames = signals && typeof signals === "object" && !Array.isArray(signals) ? Object.keys(signals) : strings(signals);
  return {
    ...sourceKind ? { sourceKind } : {},
    ...sourceId ? { sourceId } : {},
    ...url ? { url } : {},
    ...unit(confidence) !== void 0 ? { confidence: unit(confidence) } : {},
    reasons: strings(reasons),
    signals: signalNames,
    assertedFields: strings(asserted_fields),
    ...seconds(asserted_at) ? { assertedAt: seconds(asserted_at) } : {},
    ...seconds(last_seen_at) ? { lastSeenAt: seconds(last_seen_at) } : {}
  };
}
function parseTokenFacts(raw) {
  const row = raw && typeof raw === "object" ? raw : {};
  const category = text(row.category)?.toLowerCase();
  const sources = (text(row.source) ?? "").split(",").map((part) => part.trim()).filter(Boolean);
  const score = row.security_score;
  const scored = row.confidence_scored;
  return {
    ...category && category !== "unknown" ? { category } : {},
    providerCategories: strings(row.categories),
    ...text(row.logo_uri) ? { logoUri: text(row.logo_uri) } : {},
    isVerified: row.is_verified === true,
    ...typeof score === "number" && Number.isInteger(score) && score >= 0 && score <= 100 ? { securityScore: score } : {},
    sources,
    ...unit(row.confidence) !== void 0 ? { confidence: unit(row.confidence) } : {},
    ...typeof scored === "boolean" || scored === null ? { confidenceScored: scored } : {},
    ...text(row.lifecycle) ? { lifecycle: text(row.lifecycle).toLowerCase() } : {},
    isHumanApproved: row.is_human_approved === true,
    evidence: Array.isArray(row.evidence) ? row.evidence.flatMap((item) => {
      const parsed = parseEvidence(item);
      return parsed ? [parsed] : [];
    }) : [],
    ...seconds(row.last_verified_at) ? { lastVerifiedAt: seconds(row.last_verified_at) } : {},
    ...seconds(row.last_seen_at) ? { lastSeenAt: seconds(row.last_seen_at) } : {}
  };
}
var TOKEN_CATEGORY_LABEL = {
  native: "Native coin",
  stablecoin: "Stablecoin",
  wrapped_native: "Wrapped native coin",
  wrapped: "Wrapped asset",
  bridged: "Bridged variant",
  governance: "Governance token",
  lst: "Liquid staking token",
  meme: "Meme token",
  defi: "DeFi token",
  tokenized_security: "Tokenized security"
};
function tokenCategoryLabel(category) {
  const key = (category ?? "").trim().toLowerCase();
  if (!key || key === "unknown") return void 0;
  return TOKEN_CATEGORY_LABEL[key] ?? key.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
var TOKEN_CATEGORY_UNKNOWN_REASON = "Not classified. No data provider supplies a token's role; it comes from the bundled token registry or from a person, and neither has set one for this token.";
function tokenConfidence(facts) {
  if (facts.confidenceScored === true && facts.confidence !== void 0) {
    return { scored: true, percent: Math.round(facts.confidence * 100) };
  }
  if (facts.confidenceScored === null) {
    return {
      scored: false,
      reason: "Not scored \u2014 this row was written before token confidence was computed."
    };
  }
  return { scored: false, reason: "Not scored \u2014 nothing has computed a confidence for this row." };
}
function tokenLogoRef(logoUri) {
  const raw = (logoUri ?? "").trim();
  if (!raw) return void 0;
  const s3 = /^s3:\/\/[^/]+\/(.+)$/i.exec(raw);
  if (s3?.[1]) return { kind: "key", key: s3[1] };
  if (/^https?:\/\//i.test(raw)) return { kind: "url", url: raw };
  if (raw.replace(/^\/+/, "").startsWith(`${CDN_ASSET_ROOT}/`)) {
    return { kind: "key", key: raw.replace(/^\/+/, "") };
  }
  return { kind: "unrenderable", stored: raw };
}
var TOKEN_KIND_LABEL = {
  native: "Native coin",
  erc20: "ERC-20",
  trc20: "TRC-20",
  erc721: "ERC-721",
  erc1155: "ERC-1155",
  spl: "SPL token",
  "spl-2022": "SPL Token-2022",
  nep141: "NEP-141",
  hip1: "HIP-1",
  metaprotocol: "Metaprotocol token",
  cosmos_bank: "Cosmos bank denom",
  // A non-fungible collection on no token standard (CryptoPunks predates ERC-721).
  nft: "NFT (no token standard)"
};
var EVM_DEFAULT_NOTE = "The stored definition says ERC-20, which is what the token service records for an EVM contract it has not classified \u2014 it is not a checked standard.";
function tokenStandard(input) {
  const stored = (input.storedKind ?? "").trim().toLowerCase();
  const declared = input.declared?.resolved ? input.declared : null;
  const disagrees = (kind) => stored && stored !== kind && stored !== "unknown" ? stored === "erc20" ? EVM_DEFAULT_NOTE : `The stored definition says ${TOKEN_KIND_LABEL[stored] ?? stored}.` : void 0;
  const withNote = (reading, kind) => {
    const note = disagrees(kind);
    return note ? { ...reading, storedDisagrees: note } : reading;
  };
  if (stored === "native") {
    return {
      kind: "native",
      label: TOKEN_KIND_LABEL.native,
      basis: "native",
      basisText: "The chain's own coin \u2014 it has no token contract."
    };
  }
  if (declared?.erc721 || declared?.erc1155) {
    const kind = declared.erc721 ? "erc721" : "erc1155";
    return withNote(
      {
        kind,
        label: `${TOKEN_KIND_LABEL[kind]} collection`,
        basis: "declared",
        basisText: "Declared by the contract itself (ERC-165)."
      },
      kind
    );
  }
  if (stored === "erc721" || stored === "erc1155") {
    return {
      kind: stored,
      label: `${TOKEN_KIND_LABEL[stored]} collection`,
      basis: "recorded",
      basisText: "As recorded on the stored definition."
    };
  }
  if (stored === "nft") {
    return {
      kind: "nft",
      label: "NFT collection",
      basis: "recorded",
      basisText: "Recorded as a non-fungible collection on no token standard \u2014 it predates or ignores ERC-721, so the contract declares none."
    };
  }
  const catalogNft = (input.catalogType ?? "").trim().toLowerCase() === "nft";
  if (catalogNft && input.decimals == null) {
    return withNote(
      {
        kind: "nft",
        label: "NFT collection",
        basis: "catalogued",
        basisText: "Catalogued as an NFT collection; the contract has not confirmed which standard."
      },
      "nft"
    );
  }
  if (stored && stored !== "unknown") {
    const label = TOKEN_KIND_LABEL[stored] ?? stored.toUpperCase();
    if (stored === "erc20") {
      return {
        kind: stored,
        label,
        basis: "recorded",
        basisText: input.decimals != null ? `As recorded \u2014 and it has a decimal scale (${input.decimals}), which only a fungible token defines.` : "As recorded, but with no decimal scale \u2014 the token service records ERC-20 for an EVM contract it has not classified, so the standard is unconfirmed."
      };
    }
    return { kind: stored, label, basis: "recorded", basisText: "As recorded on the stored definition." };
  }
  return {
    kind: "unknown",
    label: "Not recorded",
    basis: "unknown",
    basisText: "The stored definition does not record a token standard, and the contract was not asked."
  };
}

// src/forensics/checks.generated.json
var checks_generated_default = {
  checks: [
    {
      claims: [
        "entity",
        "label",
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Every payment it receives is forwarded, within minutes, to one exchange.",
      families: [
        "evm"
      ],
      id: "evm.exchange_deposit_address",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {
        forward_ratio: {
          default: 0.9,
          high: 1,
          integer: false,
          low: 0.5
        },
        max_dwell_seconds: {
          default: 604800,
          high: 7776e3,
          integer: true,
          low: 60
        },
        min_sweeps: {
          default: 2,
          high: 1e3,
          integer: true,
          low: 2
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_deposit_address",
      subject: "address",
      tags: [
        {
          name: "exchange-deposit-address",
          severity: null,
          title: "Exchange deposit address"
        }
      ],
      title: "Exchange deposit address",
      trust: 0.85
    },
    {
      claims: [
        "label",
        "tag"
      ],
      cost: "heavy",
      default_enabled: true,
      description: "A lookalike of a wallet its victims really pay, planting bait in their history.",
      families: [
        "evm"
      ],
      id: "evm.address_poisoning",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {
        min_shared_prefix: {
          default: 3,
          high: 20,
          integer: true,
          low: 3
        },
        min_shared_suffix: {
          default: 4,
          high: 20,
          integer: true,
          low: 3
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_address_poisoning",
      subject: "address",
      tags: [
        {
          name: "address_poisoning",
          severity: "high",
          title: "Address poisoning"
        }
      ],
      title: "Address poisoning",
      trust: 0.85
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Who first funded the address \u2014 an exchange withdrawal is a KYC lead; a mixer is a warning.",
      families: [
        "evm"
      ],
      id: "evm.funding_source",
      lanes: [
        "research"
      ],
      params: {},
      profiles: [
        "forensic"
      ],
      source_id: "forensic_funding_source",
      subject: "address",
      tags: [
        {
          name: "funded-by-exchange",
          severity: null,
          title: "Funded by an exchange"
        },
        {
          name: "funded-by-bridge",
          severity: null,
          title: "Funded through a bridge"
        },
        {
          name: "funded-by-mixer",
          severity: "medium",
          title: "Funded from a mixer"
        },
        {
          name: "funded-by-sanctioned",
          severity: "high",
          title: "Funded by a sanctioned address"
        }
      ],
      title: "Funding source",
      trust: 0.8
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Forwards nearly everything it receives within minutes to wallets that are not exchanges.",
      families: [
        "evm"
      ],
      id: "evm.pass_through",
      lanes: [
        "research"
      ],
      params: {
        forward_ratio: {
          default: 0.9,
          high: 1,
          integer: false,
          low: 0.5
        },
        max_dwell_seconds: {
          default: 86400,
          high: 7776e3,
          integer: true,
          low: 0
        },
        min_forwards: {
          default: 3,
          high: 1e3,
          integer: true,
          low: 2
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_pass_through",
      subject: "address",
      tags: [
        {
          name: "pass-through",
          severity: "low",
          title: "Pass-through wallet"
        }
      ],
      title: "Pass-through wallet",
      trust: 0.6
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Which protocols it used, read from its own transfers against our protocol address books.",
      families: [
        "evm"
      ],
      id: "evm.protocol_usage",
      lanes: [
        "research"
      ],
      params: {},
      profiles: [
        "forensic"
      ],
      source_id: "forensic_protocol_usage",
      subject: "address",
      tags: [
        {
          name: "mixer-user",
          severity: "medium",
          title: "Used a mixer"
        },
        {
          name: "bridge-user",
          severity: null,
          title: "Used a bridge"
        }
      ],
      title: "Protocol, mixer and bridge usage",
      trust: 0.8
    },
    {
      claims: [
        "label",
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "What its code says it is: a plain wallet, a Safe, a minimal proxy, an EIP-7702 delegated account.",
      families: [
        "evm"
      ],
      id: "evm.account_type",
      lanes: [
        "research"
      ],
      params: {},
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_account_type",
      subject: "address",
      tags: [
        {
          name: "smart-account",
          severity: null,
          title: "Smart account"
        },
        {
          name: "eip7702-delegated",
          severity: null,
          title: "EIP-7702 delegated account"
        }
      ],
      title: "Account type",
      trust: 0.9
    },
    {
      claims: [
        "entity",
        "label"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "A contract a known protocol factory created \u2014 a Uniswap pair, a Safe \u2014 named after it.",
      families: [
        "evm"
      ],
      id: "evm.factory_instance",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {},
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_factory_instance",
      subject: "address",
      tags: [],
      title: "Factory-created contract",
      trust: 0.9
    },
    {
      claims: [
        "label",
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "A token calling itself by a registered asset's symbol or name (USDT, USDC, ETH) that it is not.",
      families: [
        "evm"
      ],
      id: "evm.counterfeit_token",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {},
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_counterfeit_token",
      subject: "token",
      tags: [
        {
          name: "counterfeit-token",
          severity: "high",
          title: "Counterfeit token"
        }
      ],
      title: "Counterfeit token",
      trust: 0.9
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "A token pushed onto wallets that never asked for it: airdropped dust, a counterfeit, a provider's spam flag.",
      families: [
        "evm"
      ],
      id: "evm.spam_token",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {
        min_batch_recipients: {
          default: 10,
          high: 1e4,
          integer: true,
          low: 2
        },
        min_signals: {
          default: 2,
          high: 3,
          integer: true,
          low: 2
        },
        min_unsolicited_recipients: {
          default: 20,
          high: null,
          integer: true,
          low: 1
        }
      },
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_spam_token",
      subject: "token",
      tags: [
        {
          name: "spam-token",
          severity: "low",
          title: "Spam token"
        }
      ],
      title: "Spam token",
      trust: 0.85
    },
    {
      claims: [],
      cost: "cheap",
      default_enabled: true,
      description: "How much of what it received came straight from exchanges, mixers, bridges or sanctioned addresses.",
      families: [
        "evm"
      ],
      id: "evm.exposure",
      lanes: [
        "research"
      ],
      params: {},
      profiles: [
        "forensic"
      ],
      source_id: "forensic_exposure",
      subject: "address",
      tags: [],
      title: "Direct exposure",
      trust: 0.8
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Automated trading: a sustained burst of swaps through DEX routers, builder tips, no idle hours.",
      families: [
        "evm"
      ],
      id: "evm.trading_bot",
      lanes: [
        "research"
      ],
      params: {
        min_active_hours: {
          default: 18,
          high: 24,
          integer: true,
          low: 1
        },
        min_swaps: {
          default: 50,
          high: 1e5,
          integer: true,
          low: 10
        },
        min_swaps_per_hour: {
          default: 30,
          high: 1e5,
          integer: true,
          low: 10
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_trading_bot",
      subject: "address",
      tags: [
        {
          name: "trading-bot",
          severity: null,
          title: "Trading bot"
        }
      ],
      title: "Trading bot",
      trust: 0.6
    },
    {
      claims: [
        "label",
        "tag"
      ],
      cost: "heavy",
      default_enabled: true,
      description: "Pulls tokens out of many unrelated wallets with approvals they granted \u2014 a phishing drainer's shape.",
      families: [
        "evm"
      ],
      id: "evm.drainer",
      lanes: [
        "research"
      ],
      params: {
        min_victims: {
          default: 3,
          high: 1e3,
          integer: true,
          low: 2
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_drainer",
      subject: "address",
      tags: [
        {
          name: "drainer",
          severity: "critical",
          title: "Drainer"
        }
      ],
      title: "Drainer",
      trust: 0.6
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Claims airdrops from many distributors and passes them on to one collector.",
      families: [
        "evm"
      ],
      id: "evm.airdrop_farmer",
      lanes: [
        "research"
      ],
      params: {
        min_claims: {
          default: 5,
          high: 1e3,
          integer: true,
          low: 3
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_airdrop_farmer",
      subject: "address",
      tags: [
        {
          name: "airdrop-farmer",
          severity: "low",
          title: "Airdrop farmer"
        }
      ],
      title: "Airdrop farmer",
      trust: 0.6
    },
    {
      claims: [
        "entity",
        "label",
        "tag"
      ],
      cost: "heavy",
      default_enabled: true,
      description: "Receives sweeps from many deposit-shaped addresses \u2014 an exchange or custodian hot wallet.",
      families: [
        "evm"
      ],
      id: "evm.consolidation_wallet",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {
        max_shared_funder_share: {
          default: 0.5,
          high: 0.5,
          integer: false,
          low: 0.1
        },
        min_deposit_senders: {
          default: 3,
          high: 1e3,
          integer: true,
          low: 2
        }
      },
      profiles: [
        "forensic"
      ],
      source_id: "forensic_consolidation_wallet",
      subject: "address",
      tags: [
        {
          name: "consolidation-wallet",
          severity: null,
          title: "Consolidation (hot) wallet"
        }
      ],
      title: "Consolidation wallet",
      trust: 0.85
    },
    {
      claims: [
        "label"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "The ENS name the address declares as its own \u2014 its primary name, verified both ways.",
      families: [
        "evm"
      ],
      id: "evm.ens_name",
      lanes: [
        "research"
      ],
      params: {},
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_ens_name",
      subject: "address",
      tags: [],
      title: "ENS name",
      trust: 0.85
    },
    {
      claims: [
        "tag"
      ],
      cost: "cheap",
      default_enabled: true,
      description: "Deployed contracts the catalog calls counterfeit tokens, spam tokens or drainers.",
      families: [
        "evm"
      ],
      id: "evm.scam_deployer",
      lanes: [
        "pipeline",
        "research"
      ],
      params: {
        min_scam_contracts: {
          default: 1,
          high: 100,
          integer: true,
          low: 1
        },
        min_without_counterfeit: {
          default: 2,
          high: 100,
          integer: true,
          low: 2
        }
      },
      profiles: [
        "forensic",
        "standard"
      ],
      source_id: "forensic_scam_deployer",
      subject: "address",
      tags: [
        {
          name: "scam-deployer",
          severity: "high",
          title: "Scam deployer"
        }
      ],
      title: "Scam deployer",
      trust: 0.85
    }
  ]
};
var FORENSIC_CHECKS = Object.freeze(
  checks_generated_default.checks.map(
    (c) => Object.freeze({ ...c, params: Object.freeze({ ...c.params }) })
  )
);
var BY_ID = new Map(FORENSIC_CHECKS.map((c) => [c.id, c]));
function forensicCheck(id) {
  return BY_ID.get(id);
}
function requireForensicParam(checkId, name) {
  const param = forensicCheck(checkId)?.params[name];
  if (!param) {
    throw new Error(
      `${checkId} declares no param ${JSON.stringify(name)} in the forensic check snapshot \u2014 regenerate checks.generated.json (pnpm gen:types) or fix the reader`
    );
  }
  return param;
}

// src/spam/index.ts
var TRANSACTION_FLAG_KINDS = [
  "paid_lookalike",
  "poisoning",
  "counterfeit_token",
  "unsolicited_distribution",
  "dust"
];
var TRANSACTION_FLAG_META = {
  paid_lookalike: {
    label: "Paid a lookalike",
    tone: "critical",
    graphEdge: "alert",
    legend: "Payment to a poisoning lookalike"
  },
  poisoning: { label: "Address poisoning", tone: "alert", graphEdge: "demote", legend: "Address-poisoning transfer" },
  counterfeit_token: {
    label: "Counterfeit token",
    tone: "alert",
    graphEdge: "demote",
    legend: "Counterfeit-token transfer"
  },
  unsolicited_distribution: {
    label: "Unsolicited airdrop",
    tone: "alert",
    graphEdge: "demote",
    legend: "Unsolicited airdrop"
  },
  dust: { label: "Dust", tone: "notice", graphEdge: "demote", legend: "Dust transfer" }
};
new Map(
  TRANSACTION_FLAG_KINDS.map((kind, index) => [kind, index])
);
function transactionFlag(kind, reason, label) {
  return { kind, label: label ?? TRANSACTION_FLAG_META[kind].label, reason };
}
requireForensicParam(
  "evm.spam_token",
  "min_batch_recipients"
).default;

// src/tokens/metadata.ts
var tokenImpersonationSchema = z.object({
  claimed: z.string(),
  field: z.enum(["symbol", "name"]),
  symbol: z.string().min(1),
  name: z.string().nullish(),
  address: z.string().nullish(),
  native: z.boolean().default(false)
});
function impersonationSentence(claim) {
  const real = claim.name ? `${claim.name} (${claim.symbol})` : claim.symbol;
  const where = claim.native ? "the chain's own coin, which has no token contract" : claim.address ? `the real one is ${shortenAddress(claim.address)}` : "at another address";
  return `Calls itself ${claim.claimed}, but it is not ${real} \u2014 ${where}.`;
}
function counterfeitTokenFlag(claims) {
  const [first] = claims;
  if (!first) return null;
  if (claims.length === 1) {
    const sentence = impersonationSentence(first);
    return transactionFlag(
      "counterfeit_token",
      `This transaction moves a token that ${sentence.charAt(0).toLowerCase()}${sentence.slice(1)}`
    );
  }
  const posedAs = [...new Set(claims.map((c) => c.symbol))];
  const list = posedAs.length === 1 ? posedAs[0] : `${posedAs.slice(0, -1).join(", ")} and ${posedAs[posedAs.length - 1]}`;
  return transactionFlag(
    "counterfeit_token",
    `This transaction moves ${claims.length} tokens posing as ${list} \u2014 none of them is the real asset.`
  );
}
function titledSymbol(symbol, impersonates) {
  if (!symbol) return void 0;
  return impersonates ? `counterfeit ${symbol}` : symbol;
}
function parseTokenImpersonation(raw) {
  const parsed = tokenImpersonationSchema.safeParse(raw);
  if (!parsed.success) return void 0;
  const { claimed, field, symbol, name, address, native } = parsed.data;
  return {
    claimed,
    field,
    symbol,
    ...name ? { name } : {},
    ...address ? { address } : {},
    native
  };
}
var TOKEN_BATCH_KEYS = 100;
var tokenRowSchema = z.object({
  chain_id: z.string().min(1),
  identifier: z.string().min(1),
  symbol: z.string().nullish(),
  name: z.string().nullish(),
  decimals: z.number().int().min(0).max(36).nullish(),
  kind: z.string().nullish(),
  is_spam: z.boolean().nullish(),
  impersonates: z.unknown().optional()
}).passthrough();
var tokenBatchSchema = z.object({
  items: z.array(z.unknown()).default([]),
  missing: z.array(z.unknown()).default([])
});
function tokenMetadataKey(chainId, address) {
  const chain = toCanonicalChainId(chainId) ?? (chainId ?? "").trim().toLowerCase();
  return `${chain}|${foldTokenIdentifier(address, chain || void 0)}`;
}
var NATIVE_TOKEN_PREFIX = "native:";
function nativeTokenChain(identifier) {
  const raw = (identifier ?? "").trim();
  if (raw.slice(0, NATIVE_TOKEN_PREFIX.length).toLowerCase() !== NATIVE_TOKEN_PREFIX) return null;
  const chain = raw.slice(NATIVE_TOKEN_PREFIX.length).trim();
  return chain ? toCanonicalChainId(chain) : null;
}
function nativeTokenIdentifier(chainId) {
  const chain = toCanonicalChainId(chainId);
  return chain ? `${NATIVE_TOKEN_PREFIX}${chain}` : null;
}
function foldTokenIdentifier(identifier, chainId) {
  const native = nativeTokenChain(identifier);
  if (native) return `${NATIVE_TOKEN_PREFIX}${native}`;
  return normalizeAddress(identifier, chainId ?? void 0);
}
var EMPTY_INDEX = {
  get: () => void 0,
  byKey: /* @__PURE__ */ new Map(),
  size: 0
};
function buildTokenMetadataIndex(rows) {
  if (rows.length === 0) return EMPTY_INDEX;
  const byKey = /* @__PURE__ */ new Map();
  for (const row of rows) byKey.set(tokenMetadataKey(row.chainId, row.address), row);
  return {
    byKey,
    size: byKey.size,
    get(address, chainId) {
      if (!address) return void 0;
      return byKey.get(tokenMetadataKey(chainId, address));
    }
  };
}
function emptyTokenMetadataIndex() {
  return EMPTY_INDEX;
}
function tokenPricesFromIndex(index) {
  const byAddress = /* @__PURE__ */ new Map();
  for (const row of index.byKey.values()) {
    const key = foldTokenIdentifier(row.address, row.chainId);
    const bucket = byAddress.get(key);
    if (bucket) bucket.push(row);
    else byAddress.set(key, [row]);
  }
  const prices = {};
  const ambiguous = [];
  for (const [key, rows] of byAddress) {
    const [first, ...rest] = rows;
    if (!first) continue;
    const agrees = rest.every(
      (row) => row.decimals === first.decimals && row.symbol === first.symbol
    );
    if (!agrees) {
      ambiguous.push(key);
      continue;
    }
    prices[key] = {
      token_address: first.address,
      chain_id: first.chainId,
      symbol: first.symbol ?? null,
      decimals: first.decimals ?? null,
      // Not a price read. See the module docstring: absent, never guessed.
      price_usd: null,
      block_number: null,
      block_timestamp: null,
      source: "token-definition",
      confidence: null
    };
  }
  return { prices, ambiguous };
}
function graphTokenRefs(graph) {
  const edges = graph?.edges;
  if (!Array.isArray(edges)) return [];
  const weights = /* @__PURE__ */ new Map();
  for (const raw of edges) {
    if (!raw || typeof raw !== "object") continue;
    const edge = raw;
    const kind = typeof edge.kind === "string" ? edge.kind : "";
    if (NON_FUNGIBLE_FLOW_KINDS.has(kind)) continue;
    const address = typeof edge.token_address === "string" ? edge.token_address.trim() : "";
    if (!address) continue;
    const chainId = [edge.token_chain_id, edge.from_chain, edge.to_chain].find(
      (value) => typeof value === "string" && value.trim() !== ""
    );
    if (!chainId) continue;
    const key = tokenMetadataKey(chainId, address);
    const existing = weights.get(key);
    if (existing) existing.edges += 1;
    else weights.set(key, { ref: { chainId, address }, edges: 1 });
  }
  return [...weights.values()].sort((a, b) => b.edges - a.edges).map((entry) => entry.ref);
}
function boundTokenRefs(refs, limit) {
  const seen = /* @__PURE__ */ new Map();
  for (const ref of refs) {
    if (!ref.address || !ref.chainId) continue;
    const key = tokenMetadataKey(ref.chainId, ref.address);
    if (!seen.has(key)) seen.set(key, ref);
  }
  const unique = [...seen.values()];
  const cap = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0;
  return {
    refs: unique.slice(0, cap),
    requestedCount: unique.length,
    truncated: unique.length > cap
  };
}
function chunkTokenRefs(refs, size = TOKEN_BATCH_KEYS) {
  const step = Math.max(1, Math.min(Math.floor(size), TOKEN_BATCH_KEYS));
  const chunks = [];
  for (let i = 0; i < refs.length; i += step) chunks.push(refs.slice(i, i + step));
  return chunks;
}
function parseTokenBatch(payload) {
  const parsed = tokenBatchSchema.safeParse(payload);
  if (!parsed.success) return [];
  const rows = [];
  for (const item of parsed.data.items) {
    const row = tokenRowSchema.safeParse(item);
    if (!row.success) continue;
    const { chain_id, identifier, name, symbol, decimals, kind, is_spam, impersonates } = row.data;
    const claim = parseTokenImpersonation(impersonates);
    rows.push({
      chainId: chain_id,
      address: identifier,
      ...name && name.trim() ? { name: name.trim() } : {},
      // Chain-written text is attacker-controlled: an empty or whitespace-only
      // symbol is not a ticker, and rendering it leaves a gap where a reader
      // expects a name.
      ...symbol && symbol.trim() ? { symbol: symbol.trim() } : {},
      ...typeof decimals === "number" ? { decimals } : {},
      ...kind ? { kind } : {},
      isSpam: is_spam === true,
      ...claim ? { impersonates: claim } : {},
      facts: parseTokenFacts(item)
    });
  }
  return rows;
}
function identifierOf(ref) {
  const chain = toCanonicalChainId(ref.chainId) ?? ref.chainId;
  return foldTokenIdentifier(ref.address, chain);
}
async function fetchChunk(client, refs, options) {
  const keys = refs.map((ref) => ({
    chain_id: toCanonicalChainId(ref.chainId) ?? ref.chainId,
    identifier: identifierOf(ref)
  }));
  const response = await client.post(
    "/tokens/batch",
    { keys },
    {
      signal: options.signal,
      cacheTtlMs: options.cacheTtlMs ?? 0
    }
  );
  return parseTokenBatch(unwrapData(response));
}
async function fetchTokenMetadata(client, refs, options = {}) {
  const chunks = chunkTokenRefs(refs);
  if (chunks.length === 0) return [];
  const settled = await Promise.allSettled(
    chunks.map((chunk) => fetchChunk(client, chunk, options))
  );
  const rows = [];
  let answered = 0;
  for (const result of settled) {
    if (result.status !== "fulfilled") continue;
    answered += 1;
    rows.push(...result.value);
  }
  if (answered === 0) {
    const [first] = settled;
    throw first && first.status === "rejected" ? first.reason : new Error("token metadata batch failed");
  }
  return rows;
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  // NOT a tenancy refusal, despite what this line used to say ("You do not have
  // access to this resource within your tenant"). PERM002 is the authorizer's
  // general deny: a wrong audience, an app/env or application-type mismatch, an
  // expired or incomplete impersonation session, a revoked API key, an AWS
  // account that is not allowed, a blue-route mismatch, an unregistered public
  // API, or a gateway not whitelisted for the calling application. None of those
  // is tenancy — though a handler-level cross-tenant refusal carries PERM002
  // too, so tenancy is one cause among many and the line can no longer name
  // just that one. Mirrors `commons.errors.codes`.
  PERM002: spec("PERM002", "Resource access denied", "Refused before the service ran. Signing in again clears an expired or mismatched session; otherwise the resource belongs to another tenant, or this application isn't registered to reach that API.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // The session's access has ENDED: its organization membership was removed or
  // deactivated, or its customer was suspended or archived (a destroyed demo
  // too). Not PERM001 — no admin of an org the reader has left can grant
  // anything — and not PERM002, whose "sign in again" cannot restore a
  // membership. Every request is refused from then on, so a host stops the app
  // and says so once instead of rendering an error per panel. Mirrors
  // `commons.errors.codes`.
  PERM005: spec("PERM005", "Access ended", "Your access to this organization has ended: your membership was removed or the organization was closed. Sign out; to regain access, ask an admin of the organization to invite you again.", ErrorCategory.AUTHORIZATION, 403, { severity: "WARNING" }),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
(class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
});

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  static {
    brandClass(this, "api/ApiError");
  }
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/pools/identity.ts
var POOL_ADDRESS_TYPE = "dex_pool";
var POOL_MEMBER_ROLES = [
  "dex_pool",
  "pool",
  "liquidity_pool",
  "lp",
  "pair",
  "pool_pair"
];
var POOL_ROLE_SUFFIXES = ["pool", "pair", "lp"];
var AMM_POOL_QUALIFIERS = [
  "v1",
  "v2",
  "v3",
  "v4",
  "plain",
  "meta",
  "metapool",
  "crypto",
  "stable",
  "stableswap",
  "weighted",
  "concentrated",
  "constant",
  "amm"
];
var ROLE_SEPARATORS = /[-_.:/\s]+/;
var EXACT_ROLES = new Set(POOL_MEMBER_ROLES);
var SUFFIXES = new Set(POOL_ROLE_SUFFIXES);
var QUALIFIERS = new Set(AMM_POOL_QUALIFIERS);
function isPoolAddressType(addressType) {
  return String(addressType ?? "").trim().toLowerCase() === POOL_ADDRESS_TYPE;
}
function isPoolRole(role) {
  const value = String(role ?? "").trim().toLowerCase();
  if (!value) return false;
  if (EXACT_ROLES.has(value)) return true;
  const parts = value.split(ROLE_SEPARATORS).filter(Boolean);
  if (parts.length < 2) return false;
  const last = parts[parts.length - 1];
  const before = parts[parts.length - 2];
  if (last === void 0 || before === void 0) return false;
  return SUFFIXES.has(last) && QUALIFIERS.has(before);
}
function looksLikePool(hints) {
  if (isPoolAddressType(hints.addressType)) return true;
  return (hints.roles ?? []).some(isPoolRole);
}

// src/tokens/index.ts
var holderSchema = z.object({
  address: z.string().min(1),
  balance: z.string().nullish(),
  balance_formatted: z.string().nullish(),
  usd_value: z.number().finite().nonnegative().nullish(),
  percentage_of_supply: z.number().finite().min(0).max(100).nullish(),
  is_contract: z.boolean().nullish(),
  label: z.string().nullish()
});
var tokenOwnersPageSchema = z.object({
  chain_id: z.string(),
  identifier: z.string(),
  holders: z.array(holderSchema),
  cursor: z.string().nullish(),
  live: z.boolean()
});
async function fetchTokenOwners(client, chain, identifier, options) {
  return tokenOwnersPageSchema.parse(unwrapData(await client.get(tokenReadPath(chain, identifier, "owners"), {
    query: { limit: options.limit, ...options.cursor ? { cursor: options.cursor } : {} },
    signal: options.signal,
    cacheTtlMs: 0
  })));
}
var holderStatsSchema = z.object({
  token_address: z.string(),
  chain: z.string().nullish(),
  total_holders: z.number().int().nonnegative().nullish(),
  /** Top-N holders' share of supply, ascending by rank. `supply` stays a string: it is past float precision. */
  supply_bands: z.array(z.object({
    rank: z.number().int().positive(),
    supply: z.string().nullish(),
    // No max: a provider rounding the top-100 share to 100.01 must not fail the whole document.
    supply_percent: z.number().finite().nonnegative().nullish()
  })),
  /** Net holder change over trailing windows, shortest first; a window the service could not read has no seconds. */
  holder_changes: z.array(z.object({
    window: z.string(),
    window_seconds: z.number().int().positive().nullish(),
    change: z.number().int().nullish(),
    change_percent: z.number().finite().nullish()
  })),
  holders_by_acquisition: z.record(z.string(), z.number().int().nonnegative()),
  holder_distribution: z.record(z.string(), z.number().int().nonnegative())
});
var tokenHolderStatsSchema = z.object({
  chain_id: z.string(),
  identifier: z.string(),
  stats: holderStatsSchema.nullable(),
  source: z.string().nullish(),
  live: z.boolean()
});
async function fetchTokenHolderStats(client, chain, identifier, options = {}) {
  return tokenHolderStatsSchema.parse(unwrapData(await client.get(tokenReadPath(chain, identifier, "holder-stats"), {
    signal: options.signal,
    cacheTtlMs: 0
  })));
}
function tokenReadPath(chain, identifier, segment) {
  const canonical = toCanonicalChainId(chain) ?? chain;
  return `/tokens/${encodeURIComponent(canonical)}/${encodeURIComponent(normalizeAddress(identifier, canonical))}/${segment}`;
}
function tokenHolderSubject(hints) {
  const roles = (hints.roles ?? []).map((role) => String(role ?? "").toLowerCase().replace(/_/g, "-"));
  if (looksLikePool(hints)) {
    return roles.some((role) => ["uniswap-v2-pool", "uniswap-v2-pair"].includes(role)) ? "lp-token" : "pool-positions";
  }
  if (hints.addressType === "token" || roles.some((role) => ["erc20", "erc-20", "spl-token", "fungible-token"].includes(role))) return "token";
  return null;
}
function uniqueTokenHolders(pages, chain) {
  const holders = /* @__PURE__ */ new Map();
  for (const page of pages) if (page.live) for (const holder of page.holders) {
    const key = normalizeAddress(holder.address, chain);
    if (!holders.has(key)) holders.set(key, holder);
  }
  return [...holders.values()];
}
function nextTokenOwnersCursor(page, used, loadedRows, ceiling) {
  return page.live && page.cursor && !used.includes(page.cursor) && loadedRows < ceiling ? page.cursor : void 0;
}

export { NATIVE_TOKEN_PREFIX, TOKEN_BATCH_KEYS, TOKEN_CATEGORY_LABEL, TOKEN_CATEGORY_UNKNOWN_REASON, TOKEN_KIND_LABEL, boundTokenRefs, buildTokenMetadataIndex, chunkTokenRefs, counterfeitTokenFlag, emptyTokenMetadataIndex, fetchTokenHolderStats, fetchTokenMetadata, fetchTokenOwners, foldTokenIdentifier, graphTokenRefs, impersonationSentence, nativeTokenChain, nativeTokenIdentifier, nextTokenOwnersCursor, parseTokenBatch, parseTokenFacts, parseTokenImpersonation, titledSymbol, tokenCategoryLabel, tokenConfidence, tokenHolderStatsSchema, tokenHolderSubject, tokenImpersonationSchema, tokenLogoRef, tokenMetadataKey, tokenOwnersPageSchema, tokenPricesFromIndex, tokenStandard, uniqueTokenHolders };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map